/**
 * 玩法快捷指令映射表 & 预设指令
 *
 * 每个玩法 prompt 覆盖完整维度：玩法类型、主题、视觉风格、配色方案、核心元素、
 * 核心机制、胜利/失败条件、难度等级、关卡设计、关卡数量、试玩时长、CTA按钮、
 * CTA触发时机、引导手势、粒子特效、动画效果、音效配置、UI风格、镜头效果、开始界面
 *
 * 结算策略提案体系（6 种组合随机分布在可加多关逻辑的玩法快捷指令中，
 * 保证每种组合至少覆盖 4 个不同玩法分类；生成时 LLM 依据提案产出对应配置）：
 * A popup_every_level：每关过关弹结算窗（CTA+下一关），失败弹窗（CTA+重试），末关按钮=再来一次
 * B auto_banner_on_levelup：过关自动推进不弹窗，过关瞬间顶部浮出 CTA 横幅 5 秒，末关弹窗（CTA+再来一次）
 * C auto_banner_persistent：过关自动推进不弹窗，全程底部常驻 CTA 条幅，末关弹窗（CTA+再来一次）
 * D auto_then_popup：前 2 关自动推进，第 3 关起每关弹窗（CTA+下一关），末关按钮=再来一次
 * E auto_mid_banner：过关自动推进不弹窗，游戏中期弹一次 CTA 横幅，末关弹窗（CTA+再来一次）
 * F popup_persistent_banner：每关弹窗（CTA+下一关）+ 全程底部常驻 CTA 条幅，末关按钮=再来一次
 */

// ─── 玩法 Prompt 映射 ──────────────────────────────────────

export const GAMEPLAY_PROMPTS: Record<string, string> = {
  // ═══════════════════════════════════════════════════════════
  // 跑酷
  // ═══════════════════════════════════════════════════════════
  runner: `帮我生成一个跑酷试玩
玩法类型：跑酷
主题：森林冒险
视觉风格：明亮卡通
配色方案：翠绿主色 + 暖黄阳光 + 深棕树干
背景色：#228B22
核心元素：一只奔跑的小狐狸角色、金币、蘑菇、松果收集物
核心机制：3条跑道自动奔跑，左右滑动切换车道，躲避障碍物并收集金币
胜利条件：到达终点或达到目标分数
失败条件：碰撞障碍物3次生命耗尽
难度等级：中等，初始速度5单位/秒，每10秒加速0.2
关卡设计：共3关递进——第1关「新手森林」稀疏树桩石头、第2关「密林深处」增灌木丛和滚石、第3关「瀑布险途」加速+滚木
关卡数量：3关
试玩时长：35秒
CTA按钮文案：开始冒险
CTA触发时机：达到目标分数或通关3关后弹出全屏CTA
结算策略提案：每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹出结算弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场1.5秒后显示左右滑动箭头提示，3秒后消失
粒子特效：金币收集金色粒子爆发、碰撞红色碎片、加速时绿色尾迹
动画效果：角色奔跑弹性步伐、障碍物入场渐显、金币旋转闪烁、过关庆祝五彩纸屑飘落
音效配置：轻快森林背景音乐 + 金币收集叮咚音效 + 碰撞沉闷提示音 + 过关胜利号角
UI风格：顶部HUD显示分数/金币/生命，半透明圆角卡片，底部进度条
镜头效果：加速时镜头微幅拉近，过关时慢动作特写
开始界面：副标题显示「尽情奔跑吧」，狐狸角色预览动画`,

  'runner-dodge': `帮我生成一个跑酷躲避试玩
玩法类型：跑酷躲避
玩法模式：dodgeMode: true
主题：赛博朋克城市
视觉风格：赛博朋克霓虹
配色方案：暗夜底色 #1a1a2e + 霓虹青#00ffcc + 品红#ff00ff + 电光蓝#00aaff
背景色：#1a1a2e
核心元素：霓虹跑者角色、能量道具、加速光柱收集物
核心机制：单跑道上下滑动躲避障碍物，收集能量道具触发加速冲刺
胜利条件：坚持到时间结束按分数结算
失败条件：碰撞障碍物立即失败
障碍物：移动的悬浮汽车、激光栅栏、全息路障、无人机巡逻
难度等级：由慢到快，前10秒教学速度，后期障碍密集
关卡设计：无限模式，每15秒进入更高难度波次，障碍速度+种类递增
关卡数量：无限模式
试玩时长：40秒
CTA按钮文案：挑战极限
CTA触发时机：游戏结束或达到高分里程碑时弹出
结算策略提案：游戏全程底部常驻半透明CTA下载条幅，通关（达成目标分数）或失败时弹出结算弹窗（CTA下载按钮+再来一次/重试按钮）
引导手势：进场1秒后显示上滑躲避提示，2秒后消失
粒子特效：霓虹光效尾迹拖尾、能量道具蓝紫光晕脉冲、加速时残影分身、碰撞电火花碎裂
动画效果：霓虹灯管闪烁、建筑投影流动、车辆悬浮微动、角色冲刺时身体拉长变形
音效配置：电子合成BGM + 加速呼啸音效 + 碰撞电磁警告音 + 收集能量脉冲音效
UI风格：赛博朋克HUD数据流边框、分数数字跳动、顶部半透明状态栏
镜头效果：加速时镜头边缘色散特效，碰撞时屏幕红色闪烁
开始界面：副标题显示「穿越霓虹之城」，霓虹灯管闪烁动画`,

  'runner-tunnel': `帮我生成一个隧道穿越跑酷试玩
玩法类型：隧道跑酷
主题：矿洞隧道探险
视觉风格：暗色隧道+灯光照明
配色方案：深灰岩壁 #3a3a3a + 暖黄灯光#ffcc44 + 轨道银#888888 + 收集物金#ffdd00
背景色：#2a2a2a
核心元素：奔跑角色、隧道中的矿车/岩石障碍、金币、照明火把
核心机制：隧道内左右三车道自动奔跑，躲避岩壁和矿车障碍，视野较窄考验反应
胜利条件：到达隧道终点或达到目标分数
失败条件：碰撞障碍物3次生命耗尽
难度等级：隧道逐渐变窄、弯道增多、障碍密集
关卡设计：3关——第1关「浅层矿道」宽隧道稀疏障碍、第2关「深层洞穴」窄弯+滚石、第3关「地下暗河」极窄+水流障碍
关卡数量：3关
试玩时长：35秒
CTA按钮文案：探索深处
CTA触发时机：通关3关或生命耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，过关瞬间顶部浮出CTA下载横幅停留5秒自动消失，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
引导手势：进场1.5秒后显示左右滑动提示
粒子特效：火把光晕粒子、金币收集金色爆发、碰撞碎石粒子
动画效果：角色奔跑动画、隧道灯光闪烁、障碍滚石旋转
音效配置：脚步回声BGM + 碰撞碎石音效 + 金币收集音效 + 隧道风声环境音
UI风格：矿洞风格HUD，顶部分数/金币/生命
镜头效果：隧道纵深透视，加速时镜头前推
开始界面：副标题显示「深入未知」，隧道灯光动画`,

  'runner-waterslide': `帮我生成一个水上滑梯跑酷试玩
玩法类型：水上滑梯跑酷
主题：水上乐园
视觉风格：明亮夏日卡通
配色方案：水池蓝 #00bcd4 + 滑梯橙#ff9800 + 阳光黄#ffeb3b + 棕榈绿#4caf50
背景色：#e0f7fa
核心元素：滑梯上的泳圈角色、水花障碍、沙滩球收集物、金币
核心机制：水上滑梯自动下滑，左右移动躲避水上乐园中的浮标和水花障碍
胜利条件：到达终点泳池或达到目标分数
失败条件：碰撞障碍3次或滑出滑梯
难度等级：滑梯弯道增多、障碍密度递增
关卡设计：3关——第1关「儿童池」缓坡宽道、第2关「急速滑道」急弯+喷泉、第3关「海啸浪池」巨浪+密集浮标
关卡数量：3关
试玩时长：35秒
CTA按钮文案：再来一次
CTA触发时机：通关或失败时弹出
结算策略提案：前2关过关自动进入下一关不弹窗，第3关起每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场1.5秒后显示左右滑动提示
粒子特效：水花飞溅粒子、金币收集水珠爆发、碰撞水雾粒子
动画效果：角色泳圈弹跳、水花溅射、滑梯水流动画
音效配置：夏日BGM + 水花溅音效 + 金币叮咚 + 滑梯呼啸声
UI风格：水上乐园风格HUD，圆形气泡边框
镜头效果：跟随下滑的纵向视角，加速时水花模糊边缘
开始界面：副标题显示「清凉一夏」，水花溅射动画`,

  'runner-space': `帮我生成一个太空失重跑酷试玩
玩法类型：太空失重跑酷
主题：太空站外太空
视觉风格：科幻深空
配色方案：深空黑 #0a0a2e + 星云紫#7c4dff + 陨石灰#9e9e9e + 能量蓝#00e5ff + 收集物金#ffd740
背景色：#0a0a2e
核心元素：漂浮宇航员角色、陨石和太空垃圾障碍、能量晶体收集物、星星背景
核心机制：零重力环境自动漂浮前进，左右移动闪避陨石和太空碎片，惯性滑动操控
胜利条件：到达空间站或达到目标分数
失败条件：碰撞陨石3次生命耗尽
难度等级：陨石密度递增、漂浮速度加快
关卡设计：3关——第1关「近地轨道」稀疏陨石、第2关「小行星带」密集+旋转陨石、第3关「太阳风暴」太阳风+电磁暴干扰
关卡数量：3关
试玩时长：40秒
CTA按钮文案：返回地球
CTA触发时机：通关或生命耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，游戏进行到中期弹出一次CTA下载横幅，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
引导手势：进场1.5秒后显示左右滑动提示
粒子特效：引擎蓝色尾焰、碰撞爆炸碎片、能量晶体收集光晕
动画效果：角色失重漂浮、陨石自转、星星视差滚动
音效配置：太空氛围BGM + 陨石碰撞音效 + 能量收集音效 + 无线电杂音
UI风格：科幻HUD全息边框，顶部分数/生命/能量
镜头效果：星空视差滚动，碰撞时屏幕震动+失重慢动作
开始界面：副标题显示「星际漂流」，宇航员漂浮动画`,

  // ═══════════════════════════════════════════════════════════
  // 三消 / 消除
  // ═══════════════════════════════════════════════════════════
  match3: `帮我生成一个三消试玩
玩法类型：三消
主题：璀璨宝石
视觉风格：华丽精致
配色方案：深紫底色 #1a0033 + 宝石红#ff2255 + 蓝宝石#2255ff + 绿宝石#22cc66 + 紫水晶#cc44ff + 黄玉#ffcc00 + 钻石#ffffff + 翡翠#00cc88
背景色：#1a0033
核心元素：7种不同颜色宝石（红宝石、蓝宝石、绿宝石、紫水晶、黄玉、钻石、翡翠），每颗宝石有光泽高光和切割面
核心机制：8x8棋盘，滑动交换相邻宝石，匹配3个以上同色消除得分，消除后上方掉落新宝石填充
胜利条件：达到目标分数
失败条件：步数用完未达标
难度等级：中等，目标分数5000→8000→12000递增
关卡设计：3关递进——第1关6x6棋盘4色入门，第2关7x7棋盘5色+冰冻障碍，第3关8x8棋盘6色+限时炸弹
关卡数量：3关
试玩时长：40秒
CTA按钮文案：立即下载
CTA触发时机：通关3关或步数耗尽时弹出
结算策略提案：每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹出结算弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场2秒后显示手指滑动交换宝石提示
粒子特效：3连消除彩色粒子爆炸、4连横向/纵向闪电光效、5连彩虹光柱+全屏星星、连消combo数字飞字
动画效果：宝石交换丝滑滑动200ms、消除缩放消失250ms、上方掉落弹性动画300ms、combo文字放大弹出
音效配置：消除清脆音效 + 连消升级音效 + 4连闪电音效 + 5连彩虹音效 + 优雅背景音乐
UI风格：华丽金色边框，宝石光泽质感，分数大字显示，步数倒计时顶部
镜头效果：5连消除全屏微震+彩虹光晕，过关时慢动作+金色光芒
开始界面：副标题显示「闪耀消除」，宝石旋转展示动画`,

  eliminate: `帮我生成一个消除试玩
玩法类型：点击消除
主题：糖果甜点
视觉风格：可爱卡通
配色方案：暖粉底色 #FFF5EE + 糖果红#ff6b6b + 奶油黄#ffd93d + 薄荷绿#6bcb77 + 巧克力棕#8b5e3c + 草莓粉#ff8fab
背景色：#FFF5EE
核心元素：6种彩色糖果和甜点（棒棒糖🍭、软糖、巧克力、马卡龙、杯子蛋糕、甜甜圈）
核心机制：点击2个以上相同相邻的糖果消除，单次消除越多分数越高，消除后上方掉落新糖果
胜利条件：达到目标分数
失败条件：步数用完未达标或无可消除相邻糖果
难度等级：轻松→中等，关卡目标分数递增
关卡设计：5关递进——第1关5x5 4种糖果入门、第2关6x5 5种+障碍糖纸、第3-5关6x6 6种+障碍递增
关卡数量：5关
试玩时长：35秒
CTA按钮文案：免费畅玩
CTA触发时机：完成5关或步数耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，游戏全程底部常驻半透明CTA下载条幅，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
引导手势：进场2秒后显示手指点击糖果提示
粒子特效：消除时糖果爆裂彩色粒子、连消星星飘落特效、过关七彩糖果雨
动画效果：消除糖果缩放弹跳消失、新糖果掉落弹性动画、连消combo文字放大弹出
音效配置：清脆消除音效 + 连消升级音效 + 欢乐背景音乐 + 过关庆祝音效
UI风格：圆角糖果色卡片，粉嫩柔和，顶部进度条+分数
镜头效果：大消除时屏幕微震，过关时彩色光晕，combo 3+时镜头微拉近
开始界面：副标题显示「甜蜜消除」，糖果飘落动画`,

  'eliminate-match3': `帮我生成一个消除三消试玩
玩法类型：消除三消
主题：魔法水晶
视觉风格：奇幻魔法
配色方案：深蓝紫底色 #1a0050 + 水晶透明蓝#88ccff + 火焰橙#ff8844 + 雷电黄#ffdd00 + 自然绿#44cc44 + 暗影紫#8844cc
背景色：#1a0050
核心元素：5种魔法水晶元素（水之水晶💧、火之水晶🔥、雷之水晶⚡、自然水晶🌿、暗影水晶🌑）
核心机制：7x7棋盘，点击交换相邻水晶，匹配3个以上消除，消除后上方掉落
胜利条件：达到目标分数
失败条件：步数用完
难度等级：中等，目标分数3000→5000→8000
关卡设计：3关——第1关5色7x7 15步、第2关加入冰冻水晶 20步、第3关加入炸弹水晶 25步
关卡数量：3关
试玩时长：40秒
CTA按钮文案：开始魔法之旅
CTA触发时机：通关后或步数耗尽时弹出
引导手势：进场2秒后显示滑动交换提示
粒子特效：消除时对应元素特效（水花/火焰/电光/绿叶/暗影）、连消魔法阵特效
动画效果：水晶交换滑动、消除碎裂、新水晶从天而降
音效配置：魔法音效 + 消除音效 + 奇幻背景音乐
UI风格：魔法书卷轴边框，水晶光泽质感，顶部魔法阵进度条
镜头效果：大消除时全屏魔法光晕，过关时镜头拉远展示魔法阵
开始界面：副标题显示「魔法水晶消除」`,

  'match3-jelly-glass': `帮我生成一个果冻玻璃质感三消试玩
玩法类型：三消
主题：果冻糖果乐园
视觉风格：jelly-glass（3D果冻玻璃质感）
配色方案：清新黄绿色系——柠檬黄#E8D44D + 薄荷绿#7BC67E + 翡翠绿#2ECC71 + 嫩芽绿#A8E6A1 + 阳光橙#FFB347 + 奶油白#FFF8E7
背景色：#F0FFF0（浅绿棋盘格交替背景）
核心元素：6种半透明果冻玻璃元素——青蛙🐸、星星⭐、耳环💎、柠檬、手提包👜、口红，每个元素都有3D果冻质感，表面有强烈高光反射和底部透光折射效果
核心机制：6x6棋盘，拖拽交换相邻格子，匹配3个以上同色消除，消除后上方掉落新元素填充
胜利条件：达到目标消除数
失败条件：步数用完未达标
难度等级：简单休闲，目标消除数25→35→50递增
关卡设计：3关递进——第1关6x6 5色入门、第2关6x6 6色+冰冻果冻、第3关7x7 6色+炸弹果冻
关卡数量：3关
试玩时长：35秒
CTA按钮文案：进入小游戏
CTA触发时机：通关3关或步数耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，过关瞬间顶部浮出CTA下载横幅停留5秒自动消失，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
引导手势：进场2秒后显示手指拖拽交换提示
粒子特效：消除时果冻爆裂彩色粒子（带折射光效）、连消combo文字飞字、过关果冻雨特效
动画效果：果冻交换丝滑滑动200ms、消除缩放消失250ms（带碎裂折射光效）、上方掉落弹性动画300ms
音效配置：Q弹果冻消除音效 + 连消升级音效 + 清新背景音乐
UI风格：浅绿/米白棋盘格背景，圆角卡片UI，顶部进度条+分数，清新治愈风格
镜头效果：5连消除全屏微震+彩虹光晕，过关时慢动作+金色光芒
开始界面：副标题显示「无聊消一消，开心又解压！」，果冻元素弹跳展示动画`,

  'eliminate-link': `帮我生成一个连线消除试玩
玩法类型：连线消除
主题：星空星座
视觉风格：梦幻星空
配色方案：深空底色 #050520 + 星点白#ffffff + 星座金#ffcc00 + 流星蓝#4488ff
背景色：#050520
核心元素：6种不同颜色的星星（蓝星、金星、红星、绿星、紫星、白星）
核心机制：手指滑动连线连接相同颜色的星星，连线至少2颗消除，连越多分数越高
玩法模式：matchInput: link
胜利条件：达到目标分数
失败条件：无可连线或步数用完
难度等级：中等，目标分数递增
关卡设计：3关——星图逐渐变大，星星数量和种类递增
关卡数量：3关
试玩时长：40秒
CTA按钮文案：点亮星空
CTA触发时机：通关后弹出
引导手势：进场2秒后显示手指滑动连线提示
粒子特效：连线流光粒子、消除星光爆发、星座连线金色光效
动画效果：连线路径流光动画、星星消除闪烁、新星星飘落
音效配置：星星闪烁音效 + 消除音效 + 空灵背景音乐
UI风格：星空主题半透明UI，连线进度条
镜头效果：长连线时镜头微拉远，过关时星座连线全屏展示
开始界面：副标题显示「连接星座」`,

  'eliminate-tap': `帮我生成一个点击消除试玩
玩法类型：点击消除
主题：水果乐园
视觉风格：鲜艳卡通
配色方案：暖黄底色 #FFFAF0 + 苹果红#ff4444 + 橙子橘#ff8844 + 柠檬黄#ffdd44 + 葡萄紫#9944ff + 西瓜绿#44cc44
背景色：#FFFAF0
核心元素：6种水果（苹果🍎、橙子🍊、柠檬🍋、葡萄🍇、西瓜🍉、草莓🍓）
核心机制：点击2个以上相邻相同水果消除，消除后上方掉落新水果
玩法模式：matchInput: tap
胜利条件：达到目标分数
失败条件：无可消除水果
难度等级：简单→中等
关卡设计：无限制消除，目标分数3000→5000→8000
关卡数量：3关
试玩时长：35秒
CTA按钮文案：开始采摘
CTA触发时机：通关后弹出
引导手势：进场2秒后显示点击水果提示
粒子特效：消除时水果汁喷溅粒子、连消水果雨特效
动画效果：水果消除弹跳消失、新水果掉落弹性动画
音效配置：清脆消除音效 + 果汁音效 + 轻快背景音乐
UI风格：水果主题圆角卡片，顶部分数+进度
镜头效果：大消除时屏幕微震，过关时水果雨动画
开始界面：副标题显示「水果大丰收」`,

  'eliminate-bubble': `帮我生成一个泡泡射击消除试玩
玩法类型：泡泡射击消除
主题：海底泡泡
视觉风格：清新海洋
配色方案：深海蓝底色 #002244 + 泡泡七彩（红橙黄绿蓝紫）
背景色：#002244
核心元素：6种彩色泡泡，发射器在底部中央
核心机制：瞄准发射彩色泡泡，匹配3个以上同色消除，消除所有泡泡过关
胜利条件：消除所有泡泡
失败条件：泡泡堆积到发射线
难度等级：中等，每关泡泡布局递增
关卡设计：3关——泡泡布局从简单到复杂，泡泡颜色种类递增
关卡数量：3关
试玩时长：45秒
CTA按钮文案：发射吧
CTA触发时机：通关或失败时弹出
引导手势：进场2秒后显示瞄准滑动提示
粒子特效：泡泡消除水花粒子、发射轨迹气泡
动画效果：泡泡碰撞弹跳、消除爆裂、连消水波扩散
音效配置：泡泡破裂音效 + 连消升级音效 + 海底背景音乐
UI风格：海洋主题边框，顶部泡泡数量进度
镜头效果：连消时镜头微震，过关时海底光效
开始界面：副标题显示「海底泡泡大作战」`,

  'eliminate-slider': `帮我生成一个滑动消除试玩
玩法类型：滑动消除
主题：珠宝首饰
视觉风格：奢华精致
配色方案：深色绒布底色 #1a0a0a + 戒指金#ffcc00 + 项链银#cccccc + 耳环珍珠白#fff5ee + 手镯翡翠绿#22cc66 + 皇冠钻石蓝#4488ff + 展示柜黑#0d0d0d
背景色：#1a0a0a
核心元素：5种珠宝首饰——戒指💍（金色）、项链📿（银色）、耳环✨（珍珠白）、手镯💫（翡翠绿）、皇冠👑（钻石蓝），每种珠宝有光泽高光和宝石镶嵌细节
核心机制：6x6棋盘，手指滑动整行或整列移动珠宝，滑动方向决定行/列移动方向，匹配3个以上同色珠宝消除得分，消除后新珠宝从对应方向滑入填充
胜利条件：达到目标分数
失败条件：步数用完
难度等级：中等，棋盘大小和珠宝种类递增
关卡设计：3关递进——第1关5x5 4种珠宝15步入门、第2关6x6 5种珠宝20步+冰冻障碍、第3关6x6 5种珠宝25步+限时炸弹
关卡数量：3关
试玩时长：40秒
CTA按钮文案：收集珠宝
CTA触发时机：通关3关或步数耗尽时弹出
引导手势：进场2秒后显示手指滑动方向提示，2.5秒后消失
粒子特效：消除时珠宝碎片粒子（金/银/珍珠/翡翠/钻石色）、4连消除横向/纵向闪电光效、5连消除全屏珠宝光柱、连消combo数字飞字
动画效果：整行/整列滑动丝滑250ms、珠宝消除闪烁缩放消失、新珠宝从边缘滑入弹性动画、combo文字放大弹出、冰冻障碍碎裂动画、炸弹倒计时闪烁
音效配置：珠宝滑动清脆音效 + 消除叮咚音效 + 连消升级音效 + 4连闪电音效 + 5连光柱音效 + 优雅奢华背景音乐
UI风格：珠宝展柜深色边框，顶部当前分数+目标分数+步数，棋盘区域居中，珠宝光泽质感
镜头效果：5连消除时全屏微震+光柱，连消3次+时镜头微拉近，过关时聚光灯效果+金色光芒
开始界面：副标题显示「珠宝收藏家」，珠宝旋转展示动画`,

  'eliminate-merge-earn': `帮我生成一个矿石拖拽合成试玩
玩法类型：矿石拖拽合成
主题：宝石矿工
视觉风格：冒险卡通
配色方案：矿洞暗色 #1a1008 + 宝石彩色
背景色：#1a1008
核心元素：5种矿石（铜矿→铁矿→银矿→金矿→钻石矿）
核心机制：4x4 棋盘铺满矿石，拖拽相邻同值矿石合成升级（支持横/纵/斜向），限时 45s 增加紧迫感，合成触发链式 combo + 三合一奖励
合成模式：拖拽模式（drag），初始铺满棋盘，限时 45s + 链式 combo + 三合一 + 红包
链式合成：连续触发连锁合成，combo倍率1.5x
三合一合成：三个相同矿石同时合成获1.5x额外奖励
红包系统：开启红包进度条，每次合成奖励金币飞入，进度满弹出红包
金币飞入：每次合成时金币从合成位置飞向顶部UI
胜利条件：合成出钻石矿
失败条件：矿石堆满或倒计时归零
难度等级：中等
关卡设计：3关——矿石种类递增，合成链延长，每关倒计时递减，难度递增
关卡数量：3关
试玩时长：40秒
CTA按钮文案：开始挖矿
CTA触发时机：合成钻石或失败时弹出
引导手势：进场2秒后显示滑动方向+合成提示
粒子特效：消除矿石碎片、合成升级金色光柱、钻石闪耀
动画效果：矿石滑动压缩动画、合成合并缩放、钻石旋转展示、关卡过渡光效
音效配置：挖矿音效 + 合成升级音效 + 倒计时滴答音效 + 冒险背景音乐
UI风格：矿洞主题边框，顶部分数+最高分+倒计时+关卡指示器，底部合成槽展示
镜头效果：合成钻石时全屏闪耀，过关时矿洞光效
开始界面：副标题显示「挖到钻石矿」`,

  // ═══════════════════════════════════════════════════════════
  // 合成
  // ═══════════════════════════════════════════════════════════
  merge: `帮我生成一个水果拖拽合成试玩
玩法类型：水果拖拽合成
主题：水果合成
视觉风格：可爱卡通
配色方案：暖黄底色 #FFF8DC + 葡萄紫#9944ff + 樱桃红#ff3344 + 橙子橘#ff8844 + 西瓜绿#22cc44 + 菠萝黄#ffcc00
背景色：#FFF8DC
核心元素：6种水果——葡萄🍇→樱桃🍒→橙子🍊→西瓜🍉→菠萝🍍→大西瓜冠军🏆
核心机制：4x4棋盘铺满水果，拖拽两个相同水果合成升级（支持横向、纵向、斜向对角相邻合成），合成后腾出一个空位，其余水果保持原位不移动
合成模式：拖拽模式（drag），初始铺满棋盘（mergeInitialFill 1.0），元素固定在网格位置，元素放大显示
链式合成：连续触发连锁合成，combo倍率1.5x
三合一合成：三个相同水果同时合成获1.5x额外奖励
胜利条件：合成出目标水果（目标值128）
失败条件：棋盘填满且无相邻同值可合成
难度等级：简单上手，后期高级水果稀少空间紧缺
关卡设计：4x4棋盘，铺满起手，拖拽相邻同值合成，目标合成128
关卡数量：无限模式
试玩时长：45秒
CTA按钮文案：来合成吧
CTA触发时机：合成出大西瓜或堆满溢出时弹出
结算策略提案：游戏全程底部常驻半透明CTA下载条幅，合成出大西瓜（或堆满溢出）时弹出结算弹窗（CTA下载按钮+再来一次按钮）
引导手势：进场后前三步依次引导横向、纵向、斜向拖拽合成
粒子特效：合成时爆炸彩色粒子、升级时金色光柱、满分时烟花绽放、水果碎片飞溅
动画效果：水果重力下落、碰撞弹跳堆叠、合并时缩放光效、大西瓜出现时全屏震动
音效配置：Q弹合成音效 + 升级叮咚音效 + 大西瓜出现欢呼音效 + 轻快背景音乐
UI风格：水果主题圆角卡片，顶部分数+最高分，清新田园边框
镜头效果：合成大西瓜时镜头拉近特写+慢动作，连续合成时屏幕微震
开始界面：副标题显示「合成大西瓜」，水果飘落动画`,

  'merge-collect': `帮我生成一个猫咪拖拽合成试玩
玩法类型：猫咪拖拽合成
主题：猫咪收集
视觉风格：温暖治愈
配色方案：暖咖底色 #FFF0E6 + 猫咪橘色#ff8844 + 奶油白#fff5ee + 虎斑灰#888888 + 黑猫#333333
背景色：#FFF0E6
核心元素：6种猫咪——小奶猫🐱→橘猫→虎斑猫→暹罗猫→布偶猫→猫王👑
核心机制：拖拽相同猫咪合并升级，同时收集图鉴，每合成新品种解锁图鉴条目，合成后其他猫咪保持原位不移动
合成模式：拖拽模式（drag），初始预填充75%棋盘，元素固定在网格位置
链式合成：连续触发连锁合成，combo倍率1.5x
三合一合成：三个相同猫咪同时合成获1.5x额外奖励
红包系统：关闭，专注图鉴收集体验
金币飞入：关闭，不使用金币干扰收集乐趣
胜利条件：收集全部6种猫咪图鉴
失败条件：猫咪堆满棋盘
难度等级：轻松休闲
关卡设计：5x5棋盘，每3秒自动生成一只小奶猫，目标收集全图鉴
关卡数量：无限模式
试玩时长：50秒
CTA按钮文案：收集更多猫咪
CTA触发时机：解锁猫王或时间耗尽时弹出
结算策略提案：游戏进行到中期弹出一次CTA下载横幅，解锁猫王（或时间耗尽）时弹出结算弹窗（CTA下载按钮+再来一次按钮）
引导手势：进场2秒后显示拖拽合并提示
粒子特效：合成时爱心粒子、解锁图鉴星光、猫咪升级毛茸茸光效
动画效果：猫咪合并可爱变身、新猫咪蹦跳入场、图鉴解锁翻页动画
音效配置：猫咪喵喵音效 + 合成升级音效 + 温暖治愈背景音乐
UI风格：猫咪主题毛绒边框，顶部图鉴进度条，柔和配色
镜头效果：解锁猫王时全屏爱心特效+慢动作
开始界面：副标题显示「收集所有猫咪」`,

  'merge-hongbao': `帮我生成一个水果拖拽合成试玩
玩法类型：水果拖拽合成
主题：热带水果乐园
视觉风格：清新卡通
配色方案：浅绿底色 #E8F5E9 + 水果彩色
背景色：#E8F5E9
核心元素：6 种水果——樱桃→草莓→橙子→西瓜→菠萝→榴莲
核心机制：拖拽两个相同水果手动合成升级（支持横/纵/斜向相邻合成），每次合成弹出红包奖励特效，合成后腾出空位
合成模式：拖拽模式（drag），初始铺满棋盘，玩家手动拖拽合成，无需自动挂机
链式合成：连续触发连锁合成，combo 倍率 1.5x
三合一合成：关闭，专注手动合成节奏
─────────────────────────────
【网赚红包特效层】（锦上添花，不改变以上玩法本体）：
红包系统：开启网赚红包特效，顶部提现进度条以红包为游标，每次合成飘 +¥ 金币飞入进度条
金币飞入：每次合成时金币从合成位置飞向顶部进度条
通关弹出红包领取弹窗（开口红包 + 金额 + "再集 ¥X 可提现"）
达成门槛弹"下载领 ¥30"强 CTA
redpacketEnabled=true, coinFlyEnabled=true
─────────────────────────────
胜利条件：合成出榴莲
失败条件：水果堆满溢出
难度等级：简单上手，手动拖拽合成
关卡设计：5x5 棋盘，每合成榴莲后进入下一关，难度递增
关卡数量：3 关
试玩时长：45 秒
CTA 按钮文案：下载领 ¥30
CTA 触发时机：合成榴莲或时间耗尽时弹出
引导手势：进场后前三步依次引导横向、纵向、斜向拖拽合成
粒子特效：合成时爆炸金色粒子、升级时彩色光柱、满分时烟花绽放
动画效果：水果自动合并缩放光效、连续合成动画、榴莲出现时全屏震动、关卡过渡翻页
音效配置：Q 弹合成音效 + 升级叮咚音效 + 榴莲出现欢呼音效 + 轻快背景音乐
UI 风格：清新圆角卡片，顶部分数 + 最高分 + 关卡指示器
镜头效果：合成榴莲时全屏震动 + 缩放光效，关卡过渡时全屏光效
开始界面：副标题显示「合成水果大王」`,

  'merge-jelly-glass': `帮我生成一个果冻玻璃质感合成试玩
玩法类型：拖拽合成
主题：果冻糖果合成
视觉风格：jelly-glass（3D果冻玻璃质感）
配色方案：清新黄绿色系——柠檬黄#E8D44D + 薄荷绿#7BC67E + 翡翠绿#2ECC71 + 嫩芽绿#A8E6A1 + 阳光橙#FFB347 + 奶油白#FFF8E7
背景色：#F0FFF0（浅绿渐变背景）
核心元素：8种半透明果冻玻璃元素递进链——小水滴💧→小果冻→中果冻→大果冻→果冻熊🐻→果冻星⭐→果冻皇冠👑→果冻钻石💎，每个元素都有3D果冻质感，表面有强烈高光反射和底部透光折射效果，等级越高体积越大、光泽越强
核心机制：5x5棋盘，拖拽两个相同等级果冻合并升级，同时收集图鉴，合成后其他果冻保持原位不移动
合成模式：拖拽模式（drag），初始预填充75%棋盘，元素固定在网格位置
链式合成：连续触发连锁合成，combo倍率1.5x
三合一合成：三个相同果冻同时合成获1.5x额外奖励
胜利条件：合成出果冻钻石（最高级）
失败条件：果冻堆满棋盘
难度等级：轻松休闲，合成链清晰有递进感
关卡设计：5x5棋盘，每3秒自动生成一只小水滴，目标合成到最高级
关卡数量：无限模式
试玩时长：45秒
CTA按钮文案：合成更多果冻
CTA触发时机：合成出果冻钻石或棋盘堆满时弹出
结算策略提案：游戏进行到约一半时长时弹出一次CTA下载横幅，合成出果冻钻石（或棋盘堆满）时弹出结算弹窗（CTA下载按钮+再来一次按钮）
引导手势：进场2秒后显示拖拽合并提示
粒子特效：合成时果冻爆裂彩色粒子（带折射光效）、升级时金色光柱、果冻钻石出现时全屏彩虹粒子
动画效果：果冻合并可爱变身（带Q弹折射光效）、新果冻蹦跳入场、图鉴解锁翻页动画
音效配置：Q弹果冻合成音效 + 升级叮咚音效 + 温暖治愈背景音乐
UI风格：浅绿/米白棋盘格背景，圆角卡片UI，顶部图鉴进度条，清新治愈风格
镜头效果：合成果冻钻石时全屏爱心特效+慢动作+彩虹光晕
开始界面：副标题显示「合成你的果冻王国」，果冻元素弹跳展示动画`,

  // ═══════════════════════════════════════════════════════════
  // 放置经营
  // ═══════════════════════════════════════════════════════════
  idle: `帮我生成一个放置经营试玩
玩法类型：放置经营
主题：农场经营
视觉风格：田园卡通
配色方案：翠绿底色 #90EE90 + 麦田金#ffcc44 + 天空蓝#66bbff + 泥土棕#8b5e3c
背景色：#90EE90
核心元素：4种作物——小麦🌾→胡萝卜🥕→番茄🍅→玉米🌽，小屋🏠、农场🌾、工厂🏭建筑
核心机制：点击种植作物，自动生长成熟后收获获得金币，金币可解锁新作物和升级建筑提高产量
胜利条件：解锁全部作物并达到目标金币
失败条件：无
难度等级：轻松休闲，逐步解锁
关卡设计：无限模式，初始金币100，目标累积1000金币，3种建筑可升级
关卡数量：无限
试玩时长：50秒
CTA按钮文案：开始种植
CTA触发时机：解锁全部作物或达到目标金币时弹出
引导手势：进场2秒后显示手指点击种植提示
粒子特效：作物生长绿芽粒子、收获金币飘落粒子、建筑升级闪光、满金币烟花
动画效果：作物生长动画（种子→发芽→成熟）、金币飞入计数动画、建筑升级变大动画
音效配置：自然环境鸟鸣+风声 + 种植铲土音效 + 收获金币音效 + 轻松田园背景音乐
UI风格：田园木质边框，顶部金币数+金币/秒速率，底部建筑购买面板，圆角卡片
镜头效果：建筑升级时镜头微拉近，解锁全部作物时全屏庆祝
开始界面：副标题显示「我的小农场」，作物展示动画`,

  'manage-tycoon': `帮我生成一个经营大亨试玩
玩法类型：经营大亨
主题：城市商业帝国
视觉风格：现代简约
配色方案：都市蓝灰底色 #1a2a3a + 金色收益#ffcc00 + 玻璃蓝#4488cc
背景色：#1a2a3a
核心元素：4种商业建筑——便利店🏪→餐厅🍽️→商场🏬→摩天大楼🏢
核心机制：点击加速经营，自动收益，升级建筑提高产量，雇佣经理自动收银
胜利条件：建成摩天大楼
失败条件：无
难度等级：简单→中等，建筑费用递增
关卡设计：4级建筑升级链，初始金币200，目标2000金币
关卡数量：无限
试玩时长：45秒
CTA按钮文案：建立帝国
CTA触发时机：建成摩天大楼或时间耗尽时弹出
引导手势：进场2秒后显示点击经营提示
粒子特效：金币飘落、建筑升级金色光柱、收益数字飞字、摩天大楼建成烟花
动画效果：建筑升级缩放动画、金币飞入、人流量递增动画、城市昼夜切换
音效配置：城市背景音效 + 收银音效 + 升级庆祝音效 + 商业背景音乐
UI风格：现代商务边框，顶部收益面板，底部建筑树，数据可视化
镜头效果：升级时镜头拉近特写，建成摩天大楼时城市全景
开始界面：副标题显示「打造商业帝国」`,

  'manage-kitchen': `帮我生成一个厨房经营试玩
玩法类型：厨房经营
主题：美食餐厅
视觉风格：温暖美食风
配色方案：暖橙底色 #FFF5E6 + 食物红#ff6644 + 奶油黄#ffdd88 + 木质棕#8b5e3c
背景色：#FFF5E6
核心元素：4种厨具——烤炉→煎锅→蒸笼→高级厨房，4种菜品——面包🍞→牛排🥩→寿司🍣→龙虾🦞
核心机制：点击烹饪加速，自动出餐，升级厨具提高出餐速度，完成订单获得金币
胜利条件：升级到高级厨房并完成所有菜品
失败条件：无
难度等级：轻松休闲
关卡设计：4级厨具升级，初始金币150，目标1000金币
关卡数量：无限
试玩时长：45秒
CTA按钮文案：开餐厅吧
CTA触发时机：升级到高级厨房时弹出
引导手势：进场2秒后显示点击烹饪提示
粒子特效：烹饪蒸汽粒子、金币飘落、菜品完成星星特效
动画效果：烹饪火焰动画、食物完成弹跳、顾客满意表情动画
音效配置：厨房烹饪滋滋声 + 上菜铃声音效 + 温馨背景音乐
UI风格：木质厨具边框，顶部收益+订单面板，底部厨具升级面板
镜头效果：菜品完成时镜头特写，升级高级厨房时全屏庆祝
开始界面：副标题显示「美食大厨」`,

  'manage-garden': `帮我生成一个花园经营试玩
玩法类型：花园经营
主题：花草花园
视觉风格：清新治愈风
配色方案：草地绿底色 #eaf7ea + 花朵粉#ff88aa + 阳光黄#ffd54f + 泥土棕#7a5c3e
背景色：#eaf7ea
核心元素：4种植物——种子🌱→郁金香🌷→向日葵🌻→花园温室🏡
核心机制：点击浇水加速生长，自动采收花朵，升级园艺工具提高采收速度，卖花赚取金币
胜利条件：建成花园温室并完成采收目标
失败条件：无
难度等级：轻松休闲
关卡设计：4级植物升级，初始金币180，目标600金币
关卡数量：无限
试玩时长：45秒
CTA按钮文案：打造花园
CTA触发时机：建成花园温室时弹出
引导手势：进场2秒后显示点击浇水提示
粒子特效：浇水水滴粒子、花瓣飘落、金币飞入、温室建成庆祝烟花
动画效果：植物生长摇摆动画、采收弹跳动画、蝴蝶飞舞动画
音效配置：浇水水滴声 + 采收清脆音效 + 轻快田园背景音乐
UI风格：藤蔓木质边框，顶部采收+金币面板，底部植物升级面板
镜头效果：采收完成时镜头特写，建成温室时全屏庆祝
开始界面：副标题显示「治愈花园」`,

  // ═══════════════════════════════════════════════════════════
  // 解谜
  // ═══════════════════════════════════════════════════════════
  puzzle: `帮我生成一个拼图解谜试玩
玩法类型：拼图解谜
主题：世界风景
视觉风格：写实唯美
配色方案：天空蓝底色 #87CEEB + 风景自然色
背景色：#87CEEB
核心元素：完整风景图片切割为3×3=9块拼图碎片
核心机制：点击选中拼图块，点击目标位置交换，将碎片拼回完整图片
胜利条件：拼图完整复位
失败条件：时间耗尽
难度等级：中等（3×3）
关卡设计：3关——图片复杂度递增，第1关简单风景、第2关城市建筑、第3关复杂自然
关卡数量：3关
试玩时长：45秒
CTA按钮文案：解锁更多关卡
CTA触发时机：完成3关或时间耗尽时弹出
结算策略提案：每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹出结算弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场2秒后显示点击交换提示
粒子特效：拼图正确绿色闪光、全拼完庆祝金色彩带
动画效果：拼图块交换滑动动画、正确位置高亮边框、拼完全图渐显动画
音效配置：拼图咔嗒音效 + 正确提示音 + 完成庆祝音效 + 轻松背景音乐
UI风格：木质拼图边框，顶部计时器+提示按钮，正确位置半透明引导
镜头效果：拼完时镜头拉远展示完整图片
开始界面：副标题显示「拼出美丽世界」`,

  'puzzle-draw': `帮我生成一个画线解谜试玩
玩法类型：画线解谜
主题：一笔画
视觉风格：简约手绘
配色方案：白纸底色 #FFFFFF + 墨线#333333 + 完成绿#44cc44
背景色：#FFFFFF
核心元素：节点和连线，从起点到终点一笔画完所有路径
核心机制：手指滑动连线，一笔画完所有路径不重复，连接所有节点
胜利条件：一笔画完所有路径
失败条件：重复路径或未完成
难度等级：递进，节点数从5→8→12
关卡设计：3关——节点和路径复杂度递增
关卡数量：3关
试玩时长：40秒
CTA按钮文案：挑战更多
CTA触发时机：通关后弹出
引导手势：进场2秒后显示手指滑动提示
粒子特效：成功连线绿色流光、完成时金色星星
动画效果：连线路径流光动画、节点点亮动画、完成时全图闪烁
音效配置：画线沙沙音效 + 完成提示音 + 轻松背景音乐
UI风格：手绘风格边框，顶部关卡进度，纸张纹理背景
镜头效果：完成时镜头微拉远展示全图
开始界面：副标题显示「一笔画挑战」`,

  'puzzle-liquid': `帮我生成一个液体排序解谜试玩
玩法类型：液体排序
主题：化学实验室
视觉风格：科技简约
配色方案：实验室白底 #F0F4F8 + 液体红🔴#ff3344 + 蓝🔵#3366ff + 绿🟢#33cc66 + 黄🟡#ffcc00 + 紫🟣#9944ff
背景色：#F0F4F8
核心元素：6个玻璃试管，5种颜色液体，2个空试管
核心机制：点击选中试管，再点击目标试管倒水，只能倒入空试管或顶部颜色相同的试管
胜利条件：每种颜色液体集中在同一个试管中
失败条件：步数用完未完成
难度等级：中等，试管数和颜色数递增
关卡设计：3关——第1关4色5管10步，第2关5色6管15步，第3关5色6管+限制20步
关卡数量：3关
试玩时长：45秒
CTA按钮文案：解锁更多关卡
CTA触发时机：通关后弹出
引导手势：进场2秒后显示点击倒水提示
粒子特效：液体倾倒流动粒子、完成时试管闪光
动画效果：液体倾倒倾斜动画200ms、颜色流动过渡、页面切换弹性动画
音效配置：倒水咕噜音效 + 完成叮咚音效 + 实验室背景音乐
UI风格：玻璃试管边框，顶部步数计数，实验室工作台背景
镜头效果：完成时镜头拉近试管展示
开始界面：副标题显示「分拣彩色液体」`,

  'puzzle-pin': `帮我生成一个拔钉子解谜试玩
玩法类型：拔钉子解谜
主题：机关密室逃脱
视觉风格：写实木质感
配色方案：木纹底色 #8B5E3C + 金属钉灰#999999 + 金币金#ffcc00
背景色：#8B5E3C
核心元素：5根金属钉子、挡板机关、金币、陷阱
核心机制：按正确顺序点击拔钉子，拔错顺序金币掉入陷阱，拔对顺序金币落下
胜利条件：金币安全落下
失败条件：金币掉入陷阱
难度等级：中等，钉子数量和机关复杂度递增
关卡设计：3关——钉子从5→8→12根，机关从简单→复杂
关卡数量：3关
试玩时长：40秒
CTA按钮文案：挑战更多机关
CTA触发时机：通关或失败时弹出
引导手势：进场2秒后显示点击钉子提示
粒子特效：拔钉子金属火花、金币落下金光、陷阱触发红色警示
动画效果：钉子拔出动画、挡板移动物理动画、金币掉落弹跳
音效配置：拔钉子金属音效 + 金币音效 + 陷阱触发警告音 + 紧张背景音乐
UI风格：木纹边框，顶部提示，机关密室主题
镜头效果：金币落下时慢动作，失败时红色闪烁
开始界面：副标题显示「密室逃脱」`,

  'puzzle-screw': `帮我生成一个拧螺丝解谜试玩
玩法类型：拧螺丝解谜
主题：机械工坊
视觉风格：工业金属
配色方案：金属灰底色 #555555 + 螺丝银#cccccc + 螺母金#ffcc00
背景色：#555555
核心元素：各种尺寸的螺丝和螺母，从金属板上拧下螺丝解开机关
核心机制：点击螺丝旋转拧下，先拧松外层螺丝才能拧内层，按顺序解开所有螺丝
胜利条件：所有螺丝拧下
失败条件：操作次数用完
难度等级：中等，螺丝层数和数量递增
关卡设计：3关——螺丝从简单排列→多层嵌套→复杂交错
关卡数量：3关
试玩时长：40秒
CTA按钮文案：解锁新关卡
CTA触发时机：通关后弹出
引导手势：进场2秒后显示旋转拧螺丝提示
粒子特效：螺丝拧下金属火花、完成时齿轮转动
动画效果：螺丝旋转拧下动画、金属板松动物理、嵌套螺丝解锁连锁
音效配置：螺丝旋转机械音效 + 完成齿轮音效 + 工业背景音乐
UI风格：金属面板边框，顶部操作次数，工业风格
镜头效果：完成时镜头拉远展示机械全貌
开始界面：副标题显示「机械大师」`,

  // ═══════════════════════════════════════════════════════════
  // 纸牌
  // ═══════════════════════════════════════════════════════════
  card: `帮我生成一个扑克牌试玩
玩法类型：扑克牌对战
主题：经典斗地主
视觉风格：华丽金色
配色方案：深绿牌桌底色 #1a3a1a + 金色边框#ffcc00 + 红色牌面#cc2233 + 黑色牌面#111111
背景色：#1a3a1a
核心元素：54张标准扑克牌（含大小王），玩家手牌区域，AI对手区域，台面出牌区
核心机制：斗地主简化版，2人对战，每人17张牌，点击出牌，AI自动跟牌或跳过
胜利条件：先出完手牌获胜
失败条件：对手先出完
难度等级：中等，AI策略思考1.5秒延迟
牌型支持：单牌、对子、三带一、顺子（5张+）、炸弹（四张相同）
关卡设计：5局，3局2胜制，每局10轮限制
关卡数量：5局
试玩时长：45秒
CTA按钮文案：免费下载
CTA触发时机：5局结束或对手先达到3胜时弹出
引导手势：进场1秒后显示点击选牌+出牌提示
粒子特效：出牌粒子、炸弹全屏震动+金色爆炸粒子、胜利金色粒子雨
动画效果：出牌250ms飞出动画、牌面翻转、炸弹震动特效、AI思考头像闪烁
音效配置：出牌清脆音效 + 炸弹爆炸音效 + 胜利音效 + 紧张背景音乐
UI风格：深绿牌桌+金色边框，牌面自动绘制花色数字，顶部比分+轮次
镜头效果：炸弹时全屏震动+金色光晕，胜利时慢动作展示
开始界面：副标题显示「挑战扑克之王」`,

  'card-poker': `帮我生成一个扑克对战试玩
玩法类型：扑克对战
主题：德州扑克
视觉风格：奢华赌场
配色方案：深蓝牌桌底色 #0a1a2a + 金色筹码#ffcc00 + 天鹅绒红#8b0000
背景色：#0a1a2a
核心元素：标准52张扑克牌，筹码堆，玩家+AI对手
核心机制：简化德州扑克，发2张底牌+5张公共牌，组合最佳5张牌比大小
胜利条件：牌型大于对手
失败条件：牌型小于对手
难度等级：中等
关卡设计：5局，每局30秒思考时间
关卡数量：5局
试玩时长：45秒
CTA按钮文案：开始下注
CTA触发时机：5局结束或筹码耗尽时弹出
引导手势：进场2秒后显示点击下注提示
粒子特效：下注筹码粒子、胜利金光、皇家同花顺全屏特效
动画效果：发牌动画、翻牌动画、筹码移动、胜利庆祝
音效配置：发牌音效 + 筹码碰撞音效 + 胜利欢呼 + 赌场背景音乐
UI风格：奢华赌场边框，顶部筹码+牌型显示，绿色牌桌
镜头效果：all-in时镜头拉近，胜利时聚光灯
开始界面：副标题显示「德州扑克之夜」`,

  'card-solitaire': `帮我生成一个纸牌接龙试玩
玩法类型：纸牌接龙
主题：经典接龙
视觉风格：简约优雅
配色方案：深绿底色 #1a3a1a + 白牌#ffffff + 红牌#cc2233 + 黑牌#111111
背景色：#1a3a1a
核心元素：52张标准扑克牌，7列牌堆，4个花色基础区
核心机制：点击拖动牌到目标列，按红黑交替+降序排列，将全部牌按花色收集到基础区
胜利条件：4个花色全部收集完成
失败条件：无可用移动
难度等级：中等
关卡设计：标准克朗代克接龙，7列牌堆1-7张
关卡数量：1局
试玩时长：50秒
CTA按钮文案：下载完整版
CTA触发时机：通关或无可移动时弹出
引导手势：进场2秒后显示拖动纸牌提示
粒子特效：完成收集花色闪耀、全部完成金色烟花
动画效果：牌面翻转动画、拖动移动丝滑、收集区牌叠动画
音效配置：翻牌音效 + 移动牌音效 + 完成庆祝音效 + 优雅背景音乐
UI风格：经典接龙边框，顶部4个花色收集区，底部7列牌堆
镜头效果：全部完成时镜头拉远展示全貌
开始界面：副标题显示「经典接龙挑战」`,

  // ═══════════════════════════════════════════════════════════
  // 麻将消除
  // ═══════════════════════════════════════════════════════════
  'eliminate-mahjong-scatter': `帮我生成一个麻将收集消除试玩
玩法类型：麻将收集消除
主题：经典麻将
视觉风格：传统中国风
配色方案：深红木底色 #2d1810 + 麻将米白#f5f0e8 + 牌面红#cc2233 + 牌面绿#22aa44 + 金色边框#ccaa44
背景色：#2d1810
核心元素：5种麻将牌面——红中(红)、一条(绿)、四筒(蓝红)、五万(黑+红五)、发财(绿)，完整塑料麻将牌3D质感
核心机制：点击未被遮挡的牌飞入底部收集槽，碰（3张相同）或杠（4张相同）自动消除，清空全部牌过关
胜利条件：清空全部牌
失败条件：槽位满或时间到
难度等级：中等，27张牌/8槽/120秒
关卡设计：3关——Easy:12张/9槽/180秒、Medium:27张/8槽/120秒、Hard:45张/7槽/90秒
关卡数量：3关
试玩时长：45秒
CTA按钮文案：开始消除
CTA触发时机：3关完成后或失败时弹出
引导手势：进场2秒后显示点击配对提示，空闲5秒自动引导
粒子特效：碰消除红色粒子爆发、杠消除金色粒子+光晕、连消combo数字飞字、过关鞭炮特效
动画效果：牌入场下落+弹簧+旋转归位900ms、牌飞入槽位动画、消除时槽位脉冲+塌缩、连体牌2倍宽特殊动画
音效配置：麻将碰撞清脆音效 + 碰/杠消除音效 + 过关鞭炮音效 + 古典背景音乐
UI风格：麻将桌边框，剩余牌数+倒计时+撤销/提示按钮，底部8格收集槽，红色喜庆主题
镜头效果：杠消除时镜头微拉近+金色光晕，过关时全屏庆祝
开始界面：副标题显示「麻将消消乐」`,

  'eliminate-mahjong': `帮我生成一个麻将配对消除试玩
玩法类型：麻将配对消除
主题：经典麻将
视觉风格：传统中国风
配色方案：深红木底色 #2d1810 + 麻将白#f5f0e8 + 牌面彩色
背景色：#2d1810
核心元素：多层堆叠麻将牌，标准万筒条字牌
核心机制：点击两张相同的未被遮挡的牌消除，消除所有牌过关
胜利条件：消除所有牌
失败条件：无可消除配对或时间耗尽
难度等级：中等，牌局复杂度递增
关卡设计：3关——牌堆层数从2层→3层→4层，牌数从36→54→72
关卡数量：3关
试玩时长：45秒
CTA按钮文案：开始消除
CTA触发时机：通关后弹出
引导手势：进场2秒后显示点击配对提示
粒子特效：消除金光特效、连消红色粒子
动画效果：牌选中高亮、消除缩放消失、新牌层渐显
音效配置：麻将碰撞音效 + 消除音效 + 古典背景音乐
UI风格：麻将桌主题边框，顶部剩余牌数+计时器
镜头效果：消除时微震，过关时全屏庆祝
开始界面：副标题显示「经典麻将配对」`,

  // ═══════════════════════════════════════════════════════════
  // 挪车解谜
  // ═══════════════════════════════════════════════════════════
  parking: `帮我生成一个挪车解谜试玩
玩法类型：挪车解谜
主题：城市停车场
变体类型：classic（经典华容道）——拖拽移动车辆，目标车驶出即胜利
视觉风格：卡通俯视图
配色方案：深灰沥青底色 #1a1a2e + 红色目标车#ff3344 + 蓝车#3366ff + 绿车#33cc66 + 黄车#ffcc00 + 白车#ffffff + 黑车#333333 + 出口绿箭头#44ff44
背景色：#1a1a2e
核心元素：6辆不同颜色汽车——1辆红色目标车（横向长度2格）、5辆障碍车（蓝绿黄白黑，不同尺寸方向）
车辆尺寸：小车占2格，卡车占3格，尺寸差异影响腾挪空间与策略难度
核心机制：拖动汽车沿其朝向方向前后移动，拖拽带惯性滑动效果，释放后自动对齐网格，为红色目标车清出通道到达右侧出口
方向箭头：车辆上显示方向箭头标记，直观提示可滑动方向，降低新手理解成本
胜利条件：红色目标车到达右侧出口
失败条件：步数用完或时间耗尽
难度等级：车辆数量和排列复杂度递增
关卡设计：5关递进——第1关5x5 4辆车教学、第2关5x5 6辆车、第3关6x6 8辆车、第4关6x6 10辆车+单行道、第5关6x6 12辆车+障碍物
关卡数量：5关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制、障碍物、单行道
试玩时长：45秒
总时长限制：45秒（跨关分摊，提前通关剩余时间结转下一关）
CTA按钮文案：免费下载
CTA触发时机：通关5关或步数耗尽时弹出
结算策略提案：前2关过关自动进入下一关不弹窗，第3关起每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指拖拽方向箭头提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手指
粒子特效：车辆移动轨迹粒子、目标车到达出口绿色烟花、步数用尽红色警告粒子
动画效果：车辆拖拽丝滑惯性滑动150ms、选中车辆白色边框高亮、不可移动方向红色叉号提示、出口绿色箭头闪烁、过关庆祝烟花
飞出动画：目标车到达出口后飞出动画
Combo连击：连续有效移动触发combo加分提示
音效配置：车辆移动咔嗒音效 + 碰撞金属音效 + 目标车到达出口欢呼音效 + 城市背景音乐
阻挡音效：车辆无法移动时播放低沉嘟嘟声提示
UI风格：俯视停车场地图，顶部步数记录+最佳记录+计时器，底部重置按钮，出口绿色箭头标记
镜头效果：目标车到达出口时镜头拉近+慢动作，过关时全屏庆祝
开始界面：副标题显示「挪出重围」，红色目标车预览动画
过关弹窗：过关后弹窗显示通关信息+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作50次上限触发CTA，死局自动检测与重置`,

  'puzzle-parking': `帮我生成一个停车解谜试玩
玩法类型：停车解谜
主题：地下停车场
变体类型：obstacle（障碍机关）——场上有立柱/墙壁等不可移动障碍，需绕过或利用
视觉风格：写实3D俯视
配色方案：深灰水泥底色 #333333 + 白线停车位#ffffff + 目标车红#ff3344 + 障碍车蓝#3366ff/绿#33cc66/黄#ffcc00/银#c0c0c0/黑#222222/橙#ff8844 + 出口绿箭头#44ff44 + 墙壁深灰#555555
背景色：#333333
核心元素：10辆车——1辆红色目标车（横向长度2格）、9辆障碍车（小轿车2辆、SUV 3辆、卡车2辆、面包车2辆，不同尺寸和方向），白色停车位标线，绿色出口箭头，地下停车场立柱和墙壁
车辆尺寸：小车占2格，卡车占3格，不同尺寸影响腾挪空间与策略难度
核心机制：拖动车辆沿其朝向方向前后移动，拖拽带惯性滑动效果，释放后自动对齐网格，每辆车只能沿其长度方向移动（横向车只能左右、纵向车只能上下），为红色目标车清出通道到达右侧出口
方向箭头：车辆上显示方向箭头标记，直观提示可滑动方向，降低新手理解成本
胜利条件：红色目标车到达右侧出口
失败条件：步数用完（每关有步数限制）
难度等级：中等，车辆数量和排列复杂度递增，第1关教学级，第3关烧脑级
关卡设计：3关递进——第1关6x6 6辆车（4种障碍车）15步教学关、第2关6x6 9辆车（6种障碍车+立柱）20步中级关、第3关6x6 12辆车（含卡车+面包车+单行道）25步困难关
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制、障碍物、单行道
试玩时长：45秒
总时长限制：45秒（跨关分摊，提前通关剩余时间结转下一关）
CTA按钮文案：下载更多关卡
CTA触发时机：通关3关或步数耗尽时弹出
结算策略提案：每关过关弹出结算弹窗（CTA下载按钮+下一关按钮）且游戏全程底部常驻半透明CTA下载条幅，失败弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场2秒后显示手指拖拽方向箭头提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手指
粒子特效：车辆移动轨迹微尘粒子、目标车到达出口绿色烟花+光柱粒子、步数用尽红色警告粒子、碰撞墙壁火花粒子
动画效果：车辆拖拽丝滑惯性滑动150ms、选中车辆白色边框高亮脉冲、不可移动方向红色叉号标记、出口绿色箭头呼吸闪烁、过关时车辆排队驶出动画、步数不足时数字红色闪烁
飞出动画：目标车到达出口后飞出动画
Combo连击：连续有效移动触发combo加分提示
音效配置：车辆移动引擎低沉音效 + 碰撞墙壁金属音效 + 目标车到达出口欢呼音效 + 步数不足警告音效 + 停车场环境背景音乐
阻挡音效：车辆无法移动时播放低沉嘟嘟声提示
UI风格：地下停车场俯视地图，顶部步数记录+最佳记录+计时器，底部重置按钮，出口绿色箭头标记，墙壁和立柱3D阴影
镜头效果：目标车到达出口时镜头拉近慢动作特写，解决关键一步时镜头微震，通关时全屏庆祝+停车场灯光亮起
开始界面：副标题显示「停车大师」，红色目标车预览动画
过关弹窗：过关后弹窗显示通关信息+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: text（文字重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作50次上限触发CTA，死局自动检测与重置`,

  'parking-hongbao': `帮我生成一个挪车解谜红包试玩
玩法类型：挪车解谜
主题：城市停车场
变体类型：classic（经典华容道）——拖拽移动车辆，目标车驶出即胜利，每关通关触发红包奖励
视觉风格：明亮卡通俯视
配色方案：灰蓝底色 #2a2a3e + 白线停车位#ffffff + 目标车红#ff3344 + 障碍车蓝#3366ff/绿#33cc66/黄#ffcc00/橙#ff8844/银#c0c0c0 + 出口绿箭头#44ff44
背景色：#2a2a3e
核心元素：8辆不同颜色汽车——1辆红色目标车（横向长度2格）、7辆障碍车，白色停车位标线，绿色出口箭头，红包进度条
车辆尺寸：小车占2格，卡车占3格，尺寸差异影响腾挪空间与策略难度
核心机制：拖动汽车沿其朝向方向前后移动，拖拽带惯性滑动效果，释放后自动对齐网格，为红色目标车清出通道到达右侧出口，每通关一关获得红包奖励
方向箭头：车辆上显示方向箭头标记，直观提示可滑动方向，降低新手理解成本
胜利条件：红色目标车到达右侧出口
失败条件：步数用完或时间耗尽
难度等级：车辆数量和排列复杂度递增
关卡设计：3关递进——第1关5x5 6辆车教学、第2关6x6 8辆车、第3关6x6 10辆车+障碍物
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制、障碍物、单行道
试玩时长：45秒
总时长限制：45秒（跨关分摊，提前通关剩余时间结转下一关）
─────────────────────────────
【网赚红包特效层】（锦上添花，不改变以上玩法本体）：
红包系统：开启网赚红包特效，顶部提现进度条以红包为游标，3个里程碑（33%/66%/100%），目标金额30元，每关通关基础金额5元×关卡号
金币飞入：每次通关时金币飞向顶部进度条
通关弹出红包领取弹窗（开口红包 + 金额 + "再集 ¥X 可提现"）
达成门槛弹"下载领 ¥30"强 CTA
redpacketEnabled=true, coinFlyEnabled=true
─────────────────────────────
CTA按钮文案：下载领 ¥30
CTA触发时机：通关3关或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指拖拽方向箭头提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手指
粒子特效：车辆移动轨迹粒子、目标车到达出口金色烟花+金币飞溅、步数用尽红色警告粒子
动画效果：车辆拖拽丝滑惯性滑动150ms、选中车辆白色边框高亮、不可移动方向红色叉号提示、出口绿色箭头闪烁、过关庆祝烟花+红包弹窗
飞出动画：目标车到达出口后飞出动画
Combo连击：连续有效移动触发combo加分提示
音效配置：车辆移动咔嗒音效 + 碰撞金属音效 + 目标车到达出口欢呼音效 + 红包里程碑叮咚音效 + 轻快背景音乐
阻挡音效：车辆无法移动时播放低沉嘟嘟声提示
UI风格：停车场地图，顶部步数记录+最佳记录+计时器+红包进度条，底部重置按钮，出口绿色箭头标记
镜头效果：目标车到达出口时镜头拉近+慢动作，过关时全屏庆祝+红包弹窗弹入
开始界面：副标题显示「挪出重围领红包」，红色目标车预览动画
过关弹窗：过关后弹窗显示通关信息+庆祝特效+红包里程碑弹窗
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作50次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：点击开走 ──
  'parking-tap-away': `帮我生成一个点击开走试玩
玩法类型：点击开走
主题：交通堵塞疏散
变体类型：tap_away（点击开走）——点击车辆自动沿朝向前进直到碰撞或驶出，所有车驶出即胜利
视觉风格：卡通俯视
配色方案：沥青底色 #2a2a3e + 红车#ff4455 + 蓝车#3388ff + 绿车#44cc66 + 黄车#ffcc00 + 白车#f0f0f0 + 出口绿箭头#44ff44
背景色：#2a2a3e
核心元素：8辆汽车——红/蓝/绿/黄/白/紫/橙/黑各1辆，不同尺寸方向，网格停车场
车辆尺寸：小车占2格，卡车占3格
核心机制：点击车辆，车辆自动沿朝向前进直到碰到其他车辆或边界停止，所有车辆驶出出口即胜利，操作极简只需点击
方向箭头：车辆上显示方向箭头标记，直观提示可行驶方向
胜利条件：所有车辆驶出出口
失败条件：时间耗尽
难度等级：简单上手，车辆数量递增
关卡设计：3关递进——第1关5x5 5辆车教学、第2关6x6 7辆车、第3关6x6 9辆车
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、时间限制
试玩时长：35秒
总时长限制：35秒（跨关分摊）
CTA按钮文案：解锁更多关卡
CTA触发时机：全部车辆驶出或时间耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，游戏全程底部常驻半透明CTA下载条幅，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
CTA位置：底部常驻
CTA倒计时：25秒
引导手势：进场1秒后显示手指点击车辆提示，持续2秒后消失
空闲提示：第二关起无操作2秒后自动高亮可点击车辆
粒子特效：车辆驶出彩色尾迹粒子、全部清空绿色烟花、时间不足红色警告
动画效果：车辆点击弹性缩放、自动行驶平滑滑动、驶出时飞出动画、全部清空庆祝
飞出动画：车辆驶出后飞出动画
音效配置：点击咔嗒音效 + 车辆行驶引擎音效 + 驶出欢呼音效 + 轻快背景音乐
阻挡音效：车辆碰撞时金属撞击声提示
UI风格：俯视停车场地图，顶部驶出计数+计时器，底部出口箭头标记
镜头效果：全部驶出时镜头拉远全景，过关庆祝
开始界面：副标题显示「一键开走」
过关弹窗：过关后弹窗显示驶出车辆数+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作30次上限触发CTA，死局自动检测与重排`,

  // ── 挪车变体：颜色接人 ──
  'parking-bus-out': `帮我生成一个颜色接人试玩
玩法类型：颜色接人
主题：公车站点
变体类型：bus_out（颜色接人）——点击同色乘客上车，满员后车辆开走，所有乘客被接走即胜利
视觉风格：卡通都市
配色方案：街道底色 #3a3a4e + 公交红#e74c3c + 公交蓝#3498db + 公交绿#2ecc71 + 公交黄#f1c40f + 乘客彩色 + 站台灰#888888
背景色：#3a3a4e
核心元素：4辆公交车（红/蓝/绿/黄各1辆，每辆3座位）+ 12名乘客（红3/蓝3/绿3/黄3），公交站台，方向箭头
车辆尺寸：公交车占3格，乘客占1格
核心机制：点击与公交同色的乘客，乘客自动走向对应颜色公交上车，满员后公交自动驶出，所有乘客被接走即胜利
方向箭头：公交车上显示方向箭头标记乘客上车方向
胜利条件：所有乘客被接走
失败条件：时间耗尽
难度等级：中等，乘客数量和颜色种类递增
关卡设计：3关递进——第1关4色×2人=8人教学、第2关4色×3人=12人、第3关5色×3人=15人
关卡数量：3关
逐关元数据：每关可独立配置公交数量、乘客数量、颜色种类、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：开始接人
CTA触发时机：全部乘客接走或时间耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，过关瞬间顶部浮出CTA下载横幅停留5秒自动消失，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指点击同色乘客提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动高亮可匹配的乘客
粒子特效：乘客上车星光粒子、公交满员金色光圈、驶出彩色尾迹、过关烟花
动画效果：乘客走向公交弹性步行动画、上车缩放消失、公交满员脉冲发光、驶出飞出动画
飞出动画：公交驶出后飞出动画
音效配置：乘客上车叮咚音效 + 公交满员喇叭音效 + 驶出引擎音效 + 都市背景音乐
阻挡音效：点击错误颜色乘客时播放轻柔提示音
UI风格：街道俯视地图，顶部已接人数+计时器，底部公交站点指示器
镜头效果：公交满员时镜头微拉近，全部接走时全屏庆祝
开始界面：副标题显示「彩色巴士」
过关弹窗：过关后弹窗显示接载效率+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: text（文字重玩按钮）
showMoves: false（隐藏步数计数器，界面更简洁）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作40次上限触发CTA，无匹配乘客自动提示`,

  // ── 挪车变体：全部逃脱 ──
  'parking-all-escape': `帮我生成一个全部逃脱试玩
玩法类型：全部逃脱
主题：停车场大疏散
变体类型：all_escape（全部逃脱）——拖拽车辆腾出通道，所有车辆驶出多出口即胜利
视觉风格：卡通俯视
配色方案：深夜停车场底色 #1e1e2e + 多色车辆+ 四向出口箭头#44ff44
背景色：#1e1e2e
核心元素：10辆不同颜色汽车——红/蓝/绿/黄/白/黑/紫/橙/粉/青各1辆，不同尺寸方向，四面各有一个出口
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，为每辆车规划驶出最近出口的路径，所有车辆驶出任意出口即胜利，多出口策略选择
方向箭头：车辆上显示方向箭头标记，四面出口均有绿色箭头指引
胜利条件：所有车辆驶出（任意出口）
失败条件：步数用完或时间耗尽
难度等级：高难度，车辆数量多且需要全局规划
关卡设计：3关递进——第1关6x6 6辆车2出口、第2关6x6 8辆车3出口、第3关7x7 10辆车4出口
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口数量、出口位置、步数限制、时间限制
试玩时长：45秒
总时长限制：45秒（跨关分摊）
CTA按钮文案：挑战更多关卡
CTA触发时机：全部车辆驶出或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场2秒后显示手指拖拽+出口方向提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手指
粒子特效：每辆车驶出彩色尾迹粒子、全部清空巨型烟花、步数不足红色警告
动画效果：车辆拖拽丝滑惯性滑动、驶出飞出动画、全部清空全屏庆祝
飞出动画：每辆车驶出后飞出动画
Combo连击：连续驶出触发combo加分提示
音效配置：车辆移动咔嗒音效 + 驶出欢呼声 + 全部清空庆祝音效 + 紧张背景音乐
阻挡音效：车辆无法移动时低沉嘟嘟声
UI风格：俯视停车场地图，顶部已驶出计数+步数+计时器，四面出口箭头标记
镜头效果：最后几辆驶出时镜头拉远全景，全部清空时慢动作庆祝
开始界面：副标题显示「全员疏散」
过关弹窗：过关后弹窗显示疏散效率评分+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作60次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：紧急救援 ──
  'parking-target-escape': `帮我生成一个紧急救援试玩
玩法类型：紧急救援
主题：急救通道
变体类型：target_escape（紧急救援）——拖拽车辆为救护车清出通道，救护车必须驶出出口
玩法模式：parkingGameType: target_escape
视觉风格：写实俯视
配色方案：急救停车场底色 #2a1010 + 救护车白#ffffff + 红十字#ff3344 + 障碍车蓝#3366ff/绿#33cc66/黄#ffcc00/黑#333333 + 警示橙#ff6600 + 出口绿箭头#44ff44
背景色：#2a1010
核心元素：1辆救护车（白色带红十字，横向2格）+ 8辆障碍车
车辆尺寸：救护车占2格，普通车占2格
核心机制：拖动车辆沿朝向移动，为救护车清出通道驶出出口，障碍车挡路，紧迫感强
方向箭头：车辆上显示方向箭头标记
胜利条件：救护车到达出口
失败条件：时间耗尽或救护车被堵住
难度等级：高难度，障碍车堵住通道增加时间压力
关卡设计：3关递进——第1关6x6 6辆车教学、第2关6x6 8辆车、第3关6x6 10辆车复杂布局
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、步数限制、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：紧急下载
CTA触发时机：救护车驶出或超时失败时弹出
CTA位置：底部常驻
CTA倒计时：25秒
引导手势：进场1秒后显示手指拖拽救护车方向提示，持续2秒后消失
空闲提示：第二关起无操作2秒后自动显示引导手指
粒子特效：救护车驶出绿色烟花+光柱、通关庆祝粒子
动画效果：车辆拖拽惯性滑动、救护车驶出飞出动画
飞出动画：救护车驶出后飞出动画
Combo连击：连续有效移动触发combo加分提示
音效配置：车辆移动引擎声 + 救护车警笛音效 + 驶出欢呼音效 + 紧急背景音乐
阻挡音效：车辆无法移动时低沉嘟嘟声
UI风格：急救停车场俯视地图，顶部步数+计时器，底部出口箭头标记
镜头效果：救护车驶出时镜头拉近+慢动作
开始界面：副标题显示「紧急救援」
过关弹窗：过关后弹窗显示救援用时+庆祝特效
胜利界面：全部通关后显示胜利标题+救援统计+再次试玩按钮
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作50次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：颜色停车位匹配 ──
  'parking-color-match': `帮我生成一个停车位匹配试玩
玩法类型：停车位匹配
主题：彩色停车场
变体类型：parking_match（停车位匹配）——拖拽车辆停入对应颜色车位，全部匹配即胜利，无出口
视觉风格：卡通俯视
配色方案：彩色停车场底色 #2a2a3e + 红/蓝/绿/黄/紫/橙车辆和对应颜色车位标线 + 白色车道线#ffffff
背景色：#2a2a3e
核心元素：6辆不同颜色汽车 + 6个对应颜色停车位（分布在网格各处），白色车道标线
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，将每辆车停入对应颜色的车位，全部匹配即胜利，无需出口
方向箭头：车辆上显示方向箭头标记可移动方向
胜利条件：所有车辆停入对应颜色车位
失败条件：步数用完或时间耗尽
难度等级：中等，车辆和车位数量递增
关卡设计：3关递进——第1关5x5 4辆车4车位教学、第2关6x6 6辆车6车位、第3关6x6 8辆车8车位
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、车位数、颜色种类、步数限制、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：完美停车
CTA触发时机：全部匹配或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场2秒后显示手指拖拽同色车辆提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动高亮可匹配的车辆
粒子特效：车辆匹配成功金色星光粒子、全部匹配彩色烟花、错误位置红色叉号
动画效果：车辆拖拽惯性滑动、匹配成功脉冲发光+对勾标记、全部匹配全屏庆祝
飞出动画：无（无出口飞出）
音效配置：车辆移动咔嗒音效 + 匹配成功叮咚音效 + 全部匹配庆祝音效 + 轻快背景音乐
阻挡音效：车辆无法移动时低沉嘟嘟声
UI风格：彩色停车场俯视地图，顶部已匹配数+步数+计时器，底部车位颜色指示器
镜头效果：全部匹配时镜头拉远全景+彩色光效
开始界面：副标题显示「完美停车」
过关弹窗：过关后弹窗显示匹配效率评分+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: text（文字重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作40次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：颜色排序 ──
  'parking-color-sort': `帮我生成一个颜色排序试玩
玩法类型：颜色排序
主题：车辆分拣场
变体类型：color_sort（颜色排序）——拖拽车辆使同色车辆相邻聚集，全部排好即胜利，无出口
视觉风格：卡通俯视
配色方案：分拣场底色 #2a2a3e + 红/蓝/绿/黄/紫/橙各色车辆 + 白色分拣区标线#ffffff
背景色：#2a2a3e
核心元素：12辆汽车（红/蓝/绿/黄各3辆），分拣区网格，颜色分区标线
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，将同色车辆移动到相邻位置聚集，每种颜色全部聚集即该色完成，全部颜色排好即胜利
方向箭头：车辆上显示方向箭头标记可移动方向
胜利条件：所有颜色同色聚集
失败条件：步数用完或时间耗尽
难度等级：中等，颜色种类和车辆数量递增
关卡设计：3关递进——第1关5x5 3色×2辆=6车教学、第2关6x6 4色×3辆=12车、第3关6x6 5色×3辆=15车
关卡数量：3关
逐关元数据：每关可独立配置网格大小、颜色种类、每色车辆数、步数限制、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：排序大师
CTA触发时机：全部排好或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场2秒后显示手指拖拽同色车辆聚集提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动高亮需移动的同色车辆
粒子特效：同色聚集绿色连接线粒子、全部排好彩色烟花、错误位置红色提示
动画效果：车辆拖拽惯性滑动、同色聚集时绿色脉冲连接线、全部排好全屏庆祝
飞出动画：无（无出口飞出）
音效配置：车辆移动咔嗒音效 + 同色聚集叮咚音效 + 全部排好庆祝音效 + 分拣场背景音乐
阻挡音效：车辆无法移动时低沉嘟嘟声
UI风格：分拣场俯视地图，顶部已排序色数+步数+计时器，底部颜色分区指示器
镜头效果：全部排好时镜头拉远全景+彩色光波扩散
开始界面：副标题显示「色彩分拣」
过关弹窗：过关后弹窗显示排序效率评分+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作50次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：连击开走 ──
  'parking-combo': `帮我生成一个连击开走试玩
玩法类型：连击开走
主题：连锁停车场
变体类型：combo（连续Combo）——点击车辆自动驶出，连续驶出获Combo倍率奖励，爽感强
视觉风格：霓虹卡通
配色方案：霓虹底色 #1a0a2e + 彩色车辆 + 连击数字金色#FFD700 + 出口彩虹渐变
背景色：#1a0a2e
核心元素：10辆不同颜色汽车，霓虹出口标志，Combo计数器
车辆尺寸：小车占2格，卡车占3格
核心机制：点击车辆自动沿朝向前进直到碰撞或驶出，连续驶出获Combo倍率奖励，Combo越高分数倍增
方向箭头：车辆上显示方向箭头标记
胜利条件：所有车辆驶出出口
失败条件：时间耗尽或Combo中断超过3秒
难度等级：中等，追求连续驶出的爽感
关卡设计：3关递进——第1关5x5 5辆车教学、第2关6x6 7辆车、第3关6x6 9辆车
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口数量、Combo时间窗口、时间限制
试玩时长：35秒
总时长限制：35秒（跨关分摊）
CTA按钮文案：连击挑战
CTA触发时机：全部驶出或时间耗尽时弹出
结算策略提案：过关自动进入下一关不弹窗，游戏进行到中期弹出一次CTA下载横幅，最后一关过关弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
CTA位置：底部常驻
CTA倒计时：25秒
引导手势：进场1秒后显示手指点击车辆提示，持续2秒后消失
空闲提示：Combo中断2秒后自动高亮可点击车辆
粒子特效：车辆驶出彩色尾迹粒子、Combo数字飞字粒子、高Combo全屏彩色爆发
动画效果：车辆点击弹性缩放、自动行驶平滑滑动、驶出飞出动画、Combo数字弹跳放大
飞出动画：车辆驶出后飞出动画
Combo连击：连续驶出Combo倍率递增（1x→1.5x→2x→2.5x→3x），Combo中断3秒重置
音效配置：点击咔嗒音效 + 驶出欢呼音效 + Combo升级叮咚音效 + 霓虹电子背景音乐
阻挡音效：车辆碰撞时金属撞击声
UI风格：霓虹停车场地图，顶部Combo计数器+分数+计时器，底部出口彩虹箭头
镜头效果：高Combo时镜头微震+彩色滤镜，全部驶出时慢动作
开始界面：副标题显示「连击开走」
过关弹窗：过关后弹窗显示最高Combo+总分数+庆祝特效
胜利界面：全部通关后显示胜利标题+Combo排行+再次试玩按钮
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作30次上限触发CTA，死局自动检测与重排`,

  // ── 挪车变体：双出口策略 ──
  'parking-dual-exit': `帮我生成一个双出口策略试玩
玩法类型：双出口策略
主题：智慧停车场
变体类型：dual_exit（双出口策略）——两个出口，玩家决定每辆车走哪个出口，全部驶出即胜利
视觉风格：写实俯视
配色方案：智慧停车场底色 #2a2a3e + 多色车辆 + 左出口蓝色箭头#3388ff + 右出口绿色箭头#44cc66
背景色：#2a2a3e
核心元素：10辆不同颜色汽车，左右两个出口，策略路径规划
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，两个出口分别在左侧和右侧，玩家需决定每辆车走哪个出口更高效，全部驶出即胜利
方向箭头：车辆上显示方向箭头标记，两个出口分别有蓝/绿箭头指引
胜利条件：所有车辆驶出（任意出口）
失败条件：步数用完或时间耗尽
难度等级：高难度，需要全局策略规划两个出口的车辆分配
关卡设计：3关递进——第1关6x6 6辆车左右出口教学、第2关6x6 8辆车+对向车辆、第3关6x6 10辆车+多尺寸车辆
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制
试玩时长：45秒
总时长限制：45秒（跨关分摊）
CTA按钮文案：策略挑战
CTA触发时机：全部驶出或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场2秒后显示手指拖拽+双出口方向提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手指
粒子特效：左出口驶出蓝色尾迹粒子、右出口驶出绿色尾迹粒子、全部清场双色烟花
动画效果：车辆拖拽惯性滑动、驶出飞出动画（按出口方向不同弧度）、全部清场全屏庆祝
飞出动画：车辆驶出后按出口方向飞出动画
Combo连击：连续驶出触发combo加分提示
音效配置：车辆移动咔嗒音效 + 左出口驶出低音欢呼声 + 右出口驶出高音欢呼声 + 策略背景音乐
阻挡音效：车辆无法移动时低沉嘟嘟声
UI风格：智慧停车场俯视地图，顶部已驶出计数+步数+计时器，左右出口蓝/绿箭头标记
镜头效果：最后几辆驶出时镜头拉远全景，全部清场时慢动作双色庆祝
开始界面：副标题显示「双口通行」
过关弹窗：过关后弹窗显示策略评分+双出口效率对比+庆祝特效
胜利界面：全部通关后显示胜利标题+统计信息+再次试玩按钮
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: text（文字重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作60次上限触发CTA，死局自动检测与重置`,

  // ── 挪车变体：三出口疏散 ──
  'parking-triple-exit': `帮我生成一个三出口疏散试玩
玩法类型：三出口疏散
主题：城市停车场大调度
变体类型：triple_exit（三出口疏散）——三个出口分布不同方向，横纵混合车辆从对应出口驶出
玩法模式：parkingGameType: triple_exit
视觉风格：卡通俯视
配色方案：深夜停车场底色 #1e1e2e + 多色车辆 + 三向出口箭头#44ff44
背景色：#1e1e2e
核心元素：8辆不同颜色汽车——横纵混合方向，三个出口分别在左侧、右侧、底部
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，三个出口分别在左侧、右侧和底部，横向车从左右出口驶出，纵向车从底部出口驶出，所有车辆驶出即胜利
方向箭头：车辆上显示方向箭头标记，三个出口均有绿色箭头指引
胜利条件：所有车辆驶出（任意出口）
失败条件：步数用完或时间耗尽
难度等级：高难度，需要全局策略规划三个出口的车辆分配
关卡设计：3关递进——第1关6x6 6辆车3出口教学、第2关6x6 8辆车+对向车辆、第3关6x6 10辆车+多尺寸车辆
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：策略挑戓
CTA触发时机：全部驶出或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指拖拽+三出口方向提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手势
粒子特效：每辆车驶出彩色尾迹粒子、全部清空巨型烟花
动画效果：车辆拖拽惯性滑动、驶出飞出动画、全部清空全屏庆祝
音效配置：车辆移动咔嗒音效 + 驶出欢呼声 + 轻快背景音乐
UI风格：俯视停车场地图，顶部已驶出计数+步数+计时器，三面出口绿色箭头标记
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作60次上限触发CTA，死局自动检测与重置
重要约束：关卡必须包含横向和纵向车辆，且三个出口方向必须匹配车辆方向（左右接受横向车，底部接受纵向车），保证每辆车都能正常驶出`,

  // ── 挪车变体：四出口全向疏散 ──
  'parking-quad-exit': `帮我生成一个四出口全向疏散试玩
玩法类型：四出口全向疏散
主题：停车场大疏散
变体类型：quad_exit（四出口全向）——上下左右四个出口全向疏散，横纵车辆从对应方向驶出
玩法模式：parkingGameType: quad_exit
视觉风格：卡通俯视
配色方案：深夜停车场底色 #1e1e2e + 多色车辆 + 四向出口箭头#44ff44
背景色：#1e1e2e
核心元素：10辆不同颜色汽车——横纵混合方向，四面各有一个出口
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，四个出口分别在左侧、右侧、顶部和底部，横向车从左右出口驶出，纵向车从上下出口驶出，所有车辆驶出即胜利
方向箭头：车辆上显示方向箭头标记，四面出口均有绿色箭头指引
胜利条件：所有车辆驶出（任意出口）
失败条件：步数用完或时间耗尽
难度等级：高难度，需要全局策略规划四个出口的车辆分配
关卡设计：3关递进——第1关6x6 6辆车4出口教学、第2关6x6 8辆车+多尺寸车辆、第3关7x7 10辆车+复杂布局
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制
试玩时长：45秒
总时长限制：45秒（跨关分摊）
CTA按钮文案：全向挑战
CTA触发时机：全部驶出或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指拖拽+四出口方向提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手势
粒子特效：每辆车驶出彩色尾迹粒子、全部清空巨型烟花
动画效果：车辆拖拽惯性滑动、驶出飞出动画、全部清空全屏庆祝
音效配置：车辆移动咔嗒音效 + 驶出欢呼声 + 紧张背景音乐
UI风格：俯视停车场地图，顶部已驶出计数+步数+计时器，四面出口绿色箭头标记
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: none（无车辆动画）
防护机制：操作60次上限触发CTA，死局自动检测与重置
重要约束：关卡必须同时包含横向车(dir:'h')和纵向车(dir:'v')，左右出口接受横向车，上下出口接受纵向车，保证每辆车都能正常驶出`,

  // ── 挪车变体：解救公主 ──
  'parking-rescue-princess': `帮我生成一个解救公主挪车试玩
玩法类型：解救公主挪车
主题：城堡救援
变体类型：rescue_princess（解救公主）——粉色公主车必须驶出，同时所有车辆从多出口逃脱
玩法模式：parkingGameType: rescue_princess
视觉风格：卡通童话
配色方案：城堡底色 #2a1a3e + 骑士车蓝#3388ff + 公主车粉#ff69b4 + 障碍车红#ff4455/绿#44cc66/黄#ffcc00/紫#9966ff + 出口金箭头#ffd700
背景色：#2a1a3e
核心元素：8辆不同颜色汽车 + 1个被困角色（公主/骑士/小动物，显示在棋盘旁等待救援）
角色设定：角色图片显示在棋盘一侧，表情焦急等待救援，所有车辆驶出后角色获救并显示庆祝动画
车辆尺寸：小车占2格，卡车占3格
核心机制：拖动车辆沿朝向移动，为每辆车规划驶出最近出口的路径，四面出口（上下左右）均接受对应方向车辆，所有车辆驶出即救出角色
方向箭头：车辆上显示方向箭头标记，四面出口均有金色箭头指引
胜利条件：所有车辆驶出，角色获救
失败条件：步数用完或时间耗尽
难度等级：中等，车辆数量递增
关卡设计：3关递进——第1关5x5 5辆车2出口教学、第2关6x6 7辆车3出口、第3关6x6 10辆车4出口
关卡数量：3关
逐关元数据：每关可独立配置网格大小、车辆数、出口位置、步数限制、时间限制
试玩时长：40秒
总时长限制：40秒（跨关分摊）
CTA按钮文案：救援出发
CTA触发时机：全部驶出或步数耗尽时弹出
CTA位置：底部常驻
CTA倒计时：30秒
引导手势：进场1.5秒后显示手指拖拽+出口方向提示，持续3秒后消失
空闲提示：第二关起无操作3秒后自动显示引导手势
粒子特效：车辆驶出金色粒子、全部清空救出角色时彩色烟花+爱心粒子
动画效果：车辆拖拽惯性滑动、驶出飞出动画、角色获救跳跃庆祝+全屏爱心
音效配置：车辆移动咔嗒音效 + 驶出欢呼声 + 角色获救欢呼音效 + 童话背景音乐
UI风格：城堡俯视地图，角色等待在棋盘旁，顶部已驶出计数+步数+计时器，四面出口金色箭头标记
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）
防护机制：操作60次上限触发CTA，死局自动检测与重置
重要约束：关卡必须包含横向和纵向车辆，多方向出口必须匹配车辆方向，保证每辆车都能正常驶出；角色图片使用 character 槽位`,

  // ── 挪车变体：跑道半圈 ──
  'parking-lap-exit': `帮我生成一个跑道半圈挪车试玩
玩法类型：跑道半圈挪车
主题：城市停车场
变体类型：lap_exit（跑道半圈）——车辆驶出停车场后沿外圈跑道转半圈
玩法模式：parkingGameType: lap_exit
视觉风格：卡通俯视
配色方案：沥青底色 #2a2a35 + 目标车红#ff4455 + 障碍车蓝#3388ff/绿#44cc66/黄#ffcc00 + 跑道灰#555a66 + 出口金箭头#ffd700
背景色：#2a2a35
核心元素：6-8辆横纵混合车辆 + 外圈环形跑道 + 右侧主出口 + 底部辅出口
核心机制：拖拽车辆沿朝向滑动腾出通道，所有车辆从对应方向出口驶出，驶出后沿外圈跑道转半圈消失
胜利条件：所有车辆驶出并完成跑道动画
失败条件：步数用完或时间耗尽
难度等级：中等
关卡数量：2关
试玩时长：35秒
引导手势：仅显示真实手指拖拽手势（禁止小汽车/装饰图标作为引导）
重要约束：每关必须同时包含横向车(dir:'h')和纵向车(dir:'v')；右侧出口接受横向车，底部出口接受纵向车；关卡须 BFS 可解`,

  // ═══════════════════════════════════════════════════════════
  // 射击
  // ═══════════════════════════════════════════════════════════
  shooting: `帮我生成一个射击试玩
玩法类型：太空射击
主题：银河保卫战
视觉风格：科幻
配色方案：深空底色 #000020 + 战机蓝#4488ff + 敌机红#ff4444 + 弹幕黄#ffdd00 + 道具绿#44ff44
背景色：#000020
核心元素：玩家战机、敌机（小型侦察机/中型战斗机/BOSS战舰）、弹幕子弹、能量道具、护盾道具
核心机制：手指拖动控制战机移动，自动射击子弹，消灭敌机获得分数，躲避弹幕
胜利条件：击败BOSS通关
失败条件：战机被击毁（3条命）
难度等级：敌机弹幕逐渐密集，BOSS战难度递增
关卡设计：3关——第1关小型敌机群+BOSS1、第2关中型敌机+弹幕+BOSS2、第3关密集弹幕+多种敌机+BOSS3
关卡数量：3关
试玩时长：45秒
CTA按钮文案：开始战斗
CTA触发时机：通关3关或战机被击毁时弹出
引导手势：进场1.5秒后显示手指拖拽移动提示
粒子特效：射击弹道发光粒子、敌机爆炸碎片粒子、道具掉落光晕、BOSS爆炸全屏粒子
动画效果：战机移动惯性动画、敌机入场飞行、弹幕发射、爆炸缩放消失、BOSS血条递减
音效配置：射击激光音效 + 敌机爆炸音效 + BOSS出场警报音效 + 道具拾取音效 + 太空激烈背景音乐
UI风格：科幻HUD边框，顶部分数+生命+道具，BOSS血条底部
镜头效果：BOSS战时镜头微拉远，爆炸时屏幕震动，通关时慢动作
开始界面：副标题显示「银河保卫战」，战机旋转展示`,

  'shooter-target': `帮我生成一个目标射击试玩
玩法类型：目标射击
主题：西部快枪手
视觉风格：西部荒野
配色方案：沙漠黄底色 #D2B48C + 靶心红#ff3344 + 准星金#ccaa44 + 火药黑#333333
背景色：#D2B48C
核心元素：随机出现的目标物（通缉犯头像），左轮手枪准星，弹药计数器
核心机制：点击目标射击命中，连续命中combo倍率递增，miss重置combo
胜利条件：45秒内达到目标分数
失败条件：弹药耗尽或miss超限
难度等级：中等，目标速度3-6随机，大小60-90px随机
关卡设计：目标速度递增，特殊金色BOSS目标出现
关卡数量：3波
试玩时长：45秒
CTA按钮文案：下载成为西部传奇
CTA触发时机：通关或失败时弹出
引导手势：进场1.5秒后显示点击射击提示
粒子特效：命中子弹孔+红色飞溅+火药烟雾粒子、combo金色文字、金色BOSS额外粒子
动画效果：目标出现缩放动画、命中弹孔动画、combo文字飞字、准星跟随手指
音效配置：枪声音效 + 命中音效 + combo升级音效 + 空弹音效 + 西部风格背景音乐
UI风格：通缉令边框，顶部分数+时间+弹药+combo，左轮手枪准星
镜头效果：combo 5+时镜头微震，金色BOSS命中时慢动作
开始界面：副标题显示「快枪手对决」`,

  'shooter-duck': `帮我生成一个飞鸭射击试玩
玩法类型：飞鸭射击
主题：户外猎鸭场
视觉风格：卡通田园
配色方案：天空蓝 #87CEEB + 草地绿#6B8E23 + 鸭子黄#FFD700 + 准星红#FF4444
背景色：#87CEEB
核心元素：飞行中的鸭子（不同大小速度）、猎枪准星、弹药计数、得分面板
核心机制：点击飞行中的鸭子命中，鸭子沿随机曲线飞行，连续命中combo加分
胜利条件：45秒内达到目标分数或命中指定数量鸭子
失败条件：弹药耗尽或miss过多
难度等级：鸭子速度递增、体型递减、飞行轨迹更曲折
关卡设计：3关——第1关慢速大鸭、第2关中速混合鸭群、第3关快速小鸭+金色稀有鸭
关卡数量：3关
试玩时长：40秒
CTA按钮文案：下载挑战
CTA触发时机：通关或失败时弹出
引导手势：进场1.5秒后显示点击射击提示
粒子特效：命中羽毛飞溅粒子、combo金色文字、金色稀有鸭光晕
动画效果：鸭子翅膀扇动飞行、命中后翻转坠落、准星跟随手指
音效配置：田园BGM + 枪声音效 + 鸭子叫声 + 命中音效 + combo升级音
UI风格：木质猎枪边框，顶部时间+分数+弹药+combo
镜头效果：命中时镜头微震，稀有鸭出现时高亮闪烁
开始界面：副标题显示「猎鸭季」，鸭子飞过动画`,

  'shooter-bubble': `帮我生成一个泡泡龙射击试玩
玩法类型：泡泡龙射击
主题：海底泡泡世界
视觉风格：梦幻水下卡通
配色方案：深海蓝 #1a237e + 泡泡彩色——红#ff5252/蓝#448aff/绿#69f0ae/黄#ffd740/紫#e040fb + 发射器银#b0bec5
背景色：#0d47a1
核心元素：顶部密集泡泡阵列、底部泡泡发射器、瞄准辅助线、5种颜色泡泡
核心机制：底部发射器瞄准发射彩色泡泡，3+同色泡泡相连即消除，悬挂泡泡掉落 bonus
胜利条件：消除所有泡泡或达到目标分数
失败条件：泡泡堆到底部底线
难度等级：泡泡颜色种类递增、初始阵列更密集
关卡设计：3关——第1关4色简单阵列、第2关5色复杂阵列+铁链泡泡、第3关5色密集+锁定泡泡
关卡数量：3关
试玩时长：50秒
CTA按钮文案：下载消除
CTA触发时机：通关或泡泡触底时弹出
引导手势：进场1.5秒后显示瞄准发射提示
粒子特效：消除泡泡爆裂彩色粒子、悬挂掉落粒子、combo文字
动画效果：泡泡发射飞行轨迹、碰撞弹跳、消除爆裂、悬挂掉落
音效配置：水下BGM + 发射音效 + 消除音效 + 掉落音效 + combo升级音
UI风格：海底珊瑚边框，顶部分数+关卡+剩余泡泡数
镜头效果：消除时镜头微震，大面积消除时全屏闪光
开始界面：副标题显示「泡泡消除」，泡泡上浮动画`,

  // ═══════════════════════════════════════════════════════════
  // 找茬
  // ═══════════════════════════════════════════════════════════
  'spot-diff-classic': `帮我生成一个找茬试玩
玩法类型：找茬
主题：生活场景
视觉风格：写实卡通
配色方案：米色底色 #F5F5DC + 场景自然色
背景色：#F5F5DC
核心元素：两张几乎相同的场景图，5-8处细微差异
核心机制：点击两张图中不同之处，找全所有差异过关
胜利条件：找全所有差异
失败条件：时间耗尽
难度等级：差异隐蔽度递增
关卡设计：3关——第1关5处差异较明显、第2关6处差异中等隐蔽、第3关8处差异高度隐蔽
关卡数量：3关
试玩时长：40秒
CTA按钮文案：开始找茬
CTA触发时机：完成3关或时间耗尽时弹出
结算策略提案：前2关过关自动进入下一关不弹窗，第3关起每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场1.5秒后显示点击提示
粒子特效：找对绿色标记圈粒子、全找齐庆祝彩带
动画效果：找对位置绿色圆圈标记动画、错误点击红色叉号、全找齐图片拼合动画
音效配置：找对叮咚音效 + 找错嘟声音效 + 完成庆祝音效 + 轻松背景音乐
UI风格：双图对比边框，顶部计时器+差异计数器，提示按钮
镜头效果：找对时镜头微拉近确认，全找齐时全屏庆祝
开始界面：副标题显示「火眼金睛」`,

  'spot-diff-search': `帮我生成一个寻物找茬试玩
玩法类型：寻物找茬
主题：杂乱房间寻宝
视觉风格：写实丰富
配色方案：暖色房间底色 #FFF8DC + 物品彩色——书红#cc6644/玩具蓝#4499cc/钥匙金#ddcc44/眼镜棕#8b5e3c/钟表银#cccccc + 找到对勾绿#44cc44 + 清单白#ffffff
背景色：#FFF8DC
核心元素：杂乱房间场景（堆满物品的书桌、书架、玩具箱、沙发），5个目标物品清单（物品图标+名称+勾选框），放大镜提示按钮🔍（每关1次），倒计时器
核心机制：在杂乱场景中点击找到清单中指定的物品，找到后物品高亮并打勾，全部找全过关，提示按钮可短暂高亮显示未找到物品位置（2秒）
胜利条件：找全所有5个目标物品
失败条件：时间耗尽
难度等级：物品隐蔽度递增——第1关物品较大较明显，第3关物品小且与环境融合
关卡设计：3关递进——第1关「儿童房」5个大物品较明显（玩具熊、绘本、奶瓶、积木、拨浪鼓）45秒、第2关「书房」5个中等物品半隐蔽（眼镜、钢笔、书签、茶杯、放大镜）40秒、第3关「杂物间」5个小物品高度隐蔽（钥匙、硬币、邮票、戒指、电池）40秒
关卡数量：3关
试玩时长：45秒
CTA按钮文案：开始寻宝
CTA触发时机：完成3关或时间耗尽时弹出
引导手势：进场2秒后显示手指点击场景中物品提示，3秒后消失
粒子特效：找到物品绿色光圈粒子+对勾浮现、全部找齐庆祝彩色纸屑+星星粒子、使用提示时放大镜金色粒子、时间紧迫红色闪烁粒子
动画效果：物品找到后高亮放大→缩小回正常、清单物品勾选动画+划掉效果、场景缩放拖拽交互、提示放大镜定位动画、时间不足10秒时倒计时红色闪烁
音效配置：找到物品清脆叮咚音效 + 全部找齐庆祝欢呼音效 + 使用提示放大镜音效 + 时间紧迫滴答警告音效 + 寻宝轻松背景音乐
UI风格：寻宝主题木质边框，顶部物品清单（图标+名称+勾选框）+倒计时+提示按钮，场景区域居中可缩放拖拽，放大镜图标
镜头效果：找到物品时镜头拉近物品位置特写，全部找齐时镜头拉远展示全场景+庆祝，使用提示时镜头移动到提示物品位置
开始界面：副标题显示「寻宝达人」，场景预览动画`,

  'spot-diff-animated': `帮我生成一个动态场景找茬试玩
玩法类型：动态场景找茬
主题：繁忙城市街景
视觉风格：生动卡通动画
配色方案：城市暖色底色 #FFF3E0 + 场景自然色+动态元素高亮
背景色：#FFF3E0
核心元素：动态城市场景（行走的行人、行驶的车辆、飘动的旗帜），5-8处差异（位置变化/颜色变化/消失元素）
核心机制：场景中元素会移动变化，需在动态中找不同之处，点击标记差异
胜利条件：找全所有差异
失败条件：时间耗尽
难度等级：动态元素速度递增、差异越来越隐蔽
关卡设计：3关——第1关慢速行人+明显差异、第2关中速车辆+颜色变化、第3关快速多元素+微小位置偏移
关卡数量：3关
试玩时长：45秒
CTA按钮文案：下载挑战
CTA触发时机：完成3关或时间耗尽时弹出
引导手势：进场1.5秒后显示点击场景提示
粒子特效：找对绿色标记粒子、全找齐庆祝彩带
动画效果：场景元素持续动画（行走/行驶/飘动）、找对位置圆圈标记、错误点击红色叉号
音效配置：城市环境BGM + 找对叮咚音效 + 找错嘟声音效 + 完成庆祝音效
UI风格：城市街景边框，顶部计时器+差异计数器
镜头效果：找对时镜头微拉近确认
开始界面：副标题显示「动态之眼」，城市场景预览`,

  'spot-diff-hidden': `帮我生成一个隐藏物品搜索试玩
玩法类型：隐藏物品搜索
主题：魔法森林寻宝
视觉风格：奇幻手绘风格
配色方案：森林绿底色 #2E7D32 + 物品自然色——蘑菇红#E53935/钥匙金#FFD600/水晶蓝#29B6F6/羽毛白#FAFAFA/花朵粉#F48FB1 + 清单白#ffffff
背景色：#1B5E20
核心元素：茂密魔法森林场景（蘑菇/藤蔓/溪流/小动物），5个隐藏物品清单（图标+名称+勾选框），放大镜提示按钮（每关1次）
核心机制：在复杂场景中找到清单上的隐藏物品，物品半隐藏在环境元素中，找到后高亮打勾
胜利条件：找全所有5个隐藏物品
失败条件：时间耗尽
难度等级：物品与场景融合度递增
关卡设计：3关——第1关「蘑菇谷」5个大物品半隐藏45秒、第2关「藤蔓林」5个中等物品深度隐藏40秒、第3关「精灵泉」5个小物品极隐蔽35秒
关卡数量：3关
试玩时长：50秒
CTA按钮文案：下载寻宝
CTA触发时机：完成3关或时间耗尽时弹出
引导手势：进场2秒后显示手指点击物品提示
粒子特效：找到物品金色光圈+对勾浮现、全部找齐彩色纸屑
动画效果：物品找到后高亮放大→缩小、清单勾选划掉动画、场景微动（树叶飘/溪水流）
音效配置：森林BGM + 找到物品清脆音效 + 全部找齐欢呼音效 + 时间紧迫滴答警告
UI风格：魔法森林藤蔓边框，顶部物品清单+倒计时+提示按钮
镜头效果：找到物品时镜头拉近特写
开始界面：副标题显示「森林寻宝」，魔法森林预览`,

  // ═══════════════════════════════════════════════════════════
  // 红包系列
  // ═══════════════════════════════════════════════════════════
  hongbao: `帮我生成一个红包试玩
玩法类型：红包
主题：春节红包
视觉风格：传统中国风
配色方案：喜庆红色底色 #D32F2F + 金色元宝#ffcc00 + 福字金#ffdd44
背景色：#D32F2F
核心元素：红包袋、金币、金元宝、福字装饰、灯笼背景
核心机制：点击红包拆开获得随机金币奖励，收集金币兑换更大红包，连续拆红包combo奖励
胜利条件：累计金币达到目标
失败条件：无
难度等级：轻松休闲
关卡设计：无限模式，5个红包袋排列，每个拆开有随机金币（10-100），目标累积500金币
关卡数量：无限
试玩时长：30秒
CTA按钮文案：立即提现
CTA触发时机：累计金币达到目标或时间耗尽时弹出
引导手势：进场1秒后显示手指点击拆红包提示
粒子特效：拆红包金光粒子爆发、金币飘落飞入、累积达标全屏金色粒子雨、红包袋开启动画
动画效果：红包袋打开动画、金币飞入计数动画、金币累积进度条填充、达标庆祝弹窗
音效配置：拆红包清脆音效 + 金币收集音效 + 达标庆祝音乐 + 春节喜庆背景音乐
UI风格：中国风红色边框，顶部金币累积+进度条，红包袋排列，福字装饰
镜头效果：累计达标时镜头拉近展示金币堆，红包雨时全屏金色
开始界面：副标题显示「开红包得好运」，红包动画预览`,

  'hongbao-rain': `帮我生成一个红包雨试玩
玩法类型：红包雨
主题：新春红包雨
视觉风格：喜庆中国风
配色方案：红色底色 #CC0000 + 金色红包#ff4444 + 灯笼红#ff6644 + 金币金#ffcc00 + 福字金#ffdd44
背景色：#CC0000
核心元素：从屏幕上方飘落的红包（随机大小60-120px），金币收集槽，积分计数器，灯笼装饰，福字背景
核心机制：红包从上方随机位置飘落，手指左右滑动移动底部收集篮接住红包，也可以在屏幕上直接点击红包收集，不同大小红包分数不同（大红包+30分、中红包+20分、小红包+10分），接住越多分数越高
胜利条件：累计积分达到目标分数
失败条件：时间耗尽
难度等级：轻松休闲，红包飘落速度初期3个/秒→后期6个/秒
关卡设计：无限模式，红包飘落速度逐渐加快，每10秒出现一次红包暴雨（密集飘落5秒），特殊金色红包+50分
关卡数量：无限
试玩时长：30秒
CTA按钮文案：立即提现
CTA触发时机：达到目标分数或时间耗尽时弹出全屏CTA
引导手势：进场1秒后显示手指左右滑动接红包提示，2秒后消失
粒子特效：红包从天空飘落金色粒子轨迹、接到红包金色粒子爆发+飞入计数器、红包暴雨时全屏金色粒子、金色特殊红包七彩粒子
动画效果：红包飘落旋转摇摆动画（随机转速）、接到红包收入弹跳动画、收集篮左右移动弹性动画、积分飞字跳动、红包暴雨时屏幕微微震动
音效配置：红包飘落呼呼风声 + 接到红包清脆叮咚音效 + 红包暴雨密集音效 + 达标庆祝锣鼓音乐 + 春节喜庆背景音乐
UI风格：中国风红包雨边框，顶部积分+倒计时，底部收集篮，灯笼装饰，福字背景纹理
镜头效果：红包暴雨时全屏金色飘落，达标时镜头拉近积分展示+全屏庆祝
开始界面：副标题显示「红包雨来啦」，红包飘落预览动画`,

  'hongbao-scratch': `帮我生成一个刮红包试玩
玩法类型：刮红包
主题：新春刮刮乐
视觉风格：喜庆中国风
配色方案：红色底色 #D32F2F + 刮涂层灰#cccccc + 金色奖励#ffcc00 + 银涂层#c0c0c0 + 灯笼装饰红#ff6644
背景色：#D32F2F
核心元素：3张红包刮刮卡（金边设计、福字装饰），每张卡银色涂层覆盖隐藏奖品（特等奖：888金币💰、一等奖：188金币🪙、二等奖：88金币、三等奖：18金币、谢谢参与），刮除进度条
核心机制：手指在刮刮卡上滑动擦除涂层，刮开面积超过50%揭示奖品，刮开越多揭示越快，每张卡刮完后自动揭开剩余涂层展示结果
胜利条件：刮出特等奖或一等奖
失败条件：无
难度等级：轻松休闲
关卡设计：3张卡依次刮开，奖品概率：特等奖8%、一等奖15%、二等奖25%、三等奖30%、谢谢参与22%，每张卡刮开后有1秒揭示停留
关卡数量：3张卡
试玩时长：30秒
CTA按钮文案：刮更多红包
CTA触发时机：刮完3张卡或刮出特等奖时弹出全屏CTA
引导手势：进场1秒后显示手指在涂层上来回滑动提示，2.5秒后消失
粒子特效：刮涂层时银色碎屑粒子飘落、刮出特等奖金色粒子爆发+光柱+鞭炮特效、一等奖金币粒子雨、二三等奖对应粒子、谢谢参与灰色粒子
动画效果：涂层刮除实时跟随手指轨迹、刮开面积达标时剩余涂层自动消散揭示动画、奖品从卡片中央放大弹出展示、卡片切换翻页动画、刮除进度条填充
音效配置：刮涂层沙沙音效 + 刮开揭示清脆叮咚音效 + 特等奖专属鞭炮欢呼音效 + 一等奖金币音效 + 二三等奖提示音 + 春节喜庆背景音乐
UI风格：中国风刮刮卡边框，顶部剩余卡片数+中奖记录，卡片居中展示金边+福字，刮除进度条，刮卡笔光标跟随手指
镜头效果：刮出特等奖时镜头拉近特写+慢动作+全屏金光+鞭炮，刮开面积达标时镜头微拉远展示全卡
开始界面：副标题显示「刮出好运」，刮刮卡缓慢旋转展示动画`,

  'hongbao-slash': `帮我生成一个切红包试玩
玩法类型：切红包
主题：忍者切红包
视觉风格：中国风+忍者
配色方案：红色底色 #D32F2F + 刀光银#cccccc + 红包金#ffcc00 + 忍者黑#222222 + 竹叶绿#44aa44
背景色：#D32F2F
核心元素：从屏幕各处弹出的红包（随机位置和大小60-100px），刀光轨迹特效，连击combo计数器，积分显示
核心机制：手指在屏幕上滑动切开红包，红包从随机位置弹出后停留1-2秒，滑动经过红包即切开，连续切开combo倍率递增（1x→2x→3x），miss不扣分但不计入combo，限时内尽可能多切
胜利条件：累计积分达到目标分数
失败条件：时间耗尽
难度等级：轻松→中等，红包弹出速度从1个/秒→3个/秒，后期同时出现3-4个
关卡设计：无限模式，红包弹出速度逐渐加快，每15秒出现一波红包密集弹出（同时5-6个），特殊金色红包+50分+combo直接升到3x
关卡数量：无限
试玩时长：30秒
CTA按钮文案：立即提现
CTA触发时机：达到目标分数或时间耗尽时弹出全屏CTA
引导手势：进场1秒后显示手指在屏幕上滑动切红包提示，2秒后消失
粒子特效：切开红包金色粒子爆发+碎片飞溅、刀光银色轨迹粒子、连击combo火焰粒子特效、金色特殊红包彩虹粒子
动画效果：红包从边缘弹出弹性动画、切开红包爆裂碎片动画、刀光轨迹跟随手指滑动、combo数字跳跃放大+火焰边框、红包弹出/消失缩放动画
音效配置：切开红包清脆利落音效 + combo升级忍者音效 + 金色特殊红包专属音效 + 时间紧迫警告音效 + 忍者风格背景音乐
UI风格：中国风+忍者边框，顶部积分+combo+倒计时，刀光光标跟随手指，竹叶装饰
镜头效果：combo 3x时镜头微震，金色特殊红包出现时镜头拉近，combo 5x+时慢动作切红包特写
开始界面：副标题显示「切红包赢大奖」，红包弹出+刀光预览动画`,

  'hongbao-tap': `帮我生成一个点红包试玩
玩法类型：点红包
主题：点红包
视觉风格：喜庆中国风
配色方案：红色底色 #D32F2F + 红包金#ffcc00 + 已拆红#ff6644 + 金币金#ffdd44 + 烟花彩色
背景色：#D32F2F
核心元素：9个红包排列在3x3网格中，每个红包可点击拆开获得随机奖励（金币10-88随机），拆过的红包变为已拆状态（半透明），积分计数器，烟花装饰
核心机制：快速点击红包拆开获得随机金币奖励，限时内点击越多总分越高，拆过的红包1.5秒后自动刷新为新红包，连续快速点击combo加速刷新
胜利条件：累计积分达到目标分数
失败条件：时间耗尽
难度等级：轻松休闲，红包刷新频率初期1.5秒→后期0.8秒
关卡设计：无限模式，红包刷新速度逐渐加快，前10秒每次刷新3个红包，10-20秒刷新5个，20秒后刷新7个，偶尔出现金色大红包+50分
关卡数量：无限
试玩时长：30秒
CTA按钮文案：立即提现
CTA触发时机：达到目标分数或时间耗尽时弹出全屏CTA
引导手势：进场1秒后显示手指点击红包提示，2秒后消失
粒子特效：点击红包金色粒子爆发+金币飞入计数器、金色大红包七彩粒子+光柱、连续快速点击火花粒子、达标烟花粒子
动画效果：红包被点开炸裂动画+金币弹出、已拆红包半透明缩小、新红包从网格中弹出弹性动画、积分数字跳动飞字、连续点击combo火焰特效
音效配置：点击红包清脆拆开音效 + 金币飞入叮咚音效 + 金色大红包专属欢呼音效 + 连续快速点击节奏音效 + 春节喜庆背景音乐
UI风格：中国风红包网格边框，顶部积分+倒计时+combo，3x3红包网格，已拆红包半透明状态，烟花装饰
镜头效果：快速连续点击时镜头微震，金色大红包出现时镜头拉近特写，达标时全屏烟花庆祝
开始界面：副标题显示「点点红包好运来」，红包排列预览动画`,

  'hongbao-collect': `帮我生成一个集红包试玩
玩法类型：集红包
主题：集五福
视觉风格：喜庆中国风
配色方案：红色底色 #D32F2F + 爱国福红#ff3344 + 富强福金#ffcc00 + 和谐福绿#44cc44 + 友善福蓝#4488ff + 敬业福紫#9944ff + 收集册棕#8b5e3c
背景色：#D32F2F
核心元素：5种福字红包（爱国福🔴、富强福🟡、和谐福🟢、友善福🔵、敬业福🟣），收集册（5个福字槽位），红包抽取按钮，集齐进度条，灯笼装饰
核心机制：点击抽取按钮随机获得一种福字，福字飞入收集册对应槽位，已获得的福字不会重复获得，集齐5种不同福字兑换大奖，可进行5次抽取
胜利条件：集齐5种福字
失败条件：5次抽取机会用完未集齐
难度等级：轻松休闲，福字概率均匀分布（每种20%）
关卡设计：5次抽取机会，每次抽取有1.5秒福字飞入动画，集齐进度实时显示，已集到的福字槽位点亮，未集到的灰色
关卡数量：5次抽取
试玩时长：30秒
CTA按钮文案：兑换大奖
CTA触发时机：集齐5种福字或抽取次数用完时弹出全屏CTA
引导手势：进场1秒后显示手指点击抽取按钮提示，2秒后消失
粒子特效：抽取时福字从红包中飞出金色粒子、福字飞入收集册轨迹粒子、集齐5种时全屏金色光柱+烟花+鞭炮粒子、每种福字对应颜色粒子（红/金/绿/蓝/紫）
动画效果：红包抽取打开动画→福字飞出旋转→飞入收集册槽位、槽位点亮弹性动画+福字放大、集齐进度条填充、集齐5种时收集册合拢+全屏庆祝动画
音效配置：抽取红包音效 + 福字飞行音效 + 福字入槽叮咚音效 + 集齐5种专属欢呼锣鼓音效 + 春节喜庆背景音乐
UI风格：中国风收集册边框，顶部剩余抽取次数+集齐进度条，5个福字槽位（收集册），中间抽取按钮，灯笼装饰
镜头效果：集齐5种时镜头拉远全屏庆祝+慢动作+金色光晕，每次福字入槽时镜头微拉近槽位
开始界面：副标题显示「集五福赢大奖」，5种福字旋转展示动画`,

  'hongbao-mining': `帮我生成一个挖红包试玩
玩法类型：挖红包
主题：矿洞挖宝
视觉风格：矿洞卡通
配色方案：矿洞暗色 #2d1810 + 红包红#ff3344 + 岩壁棕#8b5e3c + 镐子铁#888888 + 金矿金#ffcc00 + 钻石蓝#4488ff
背景色：#2d1810
核心元素：矿洞岩壁（5x5砖块网格），隐藏红包和矿石在砖块中，镐子工具，体力计数器（10点体力），金币计数器，矿灯装饰
核心机制：点击岩壁砖块挖矿，每点击一次消耗1点体力，随机挖出——红包🧧（+20金币）、金矿💎（+50金币）、钻石💠（+100金币）、石块🪨（+0金币）、空砖块（+0），挖出红包/金矿/钻石获得奖励
胜利条件：累计金币达到目标
失败条件：体力耗尽（10点用完）
难度等级：轻松休闲，红包出现概率40%、金矿15%、钻石5%、石块20%、空砖20%
关卡设计：10点体力，5x5=25个砖块，每块砖挖1次消耗1体力，体力耗尽后显示总金币，随机奖励分布
关卡数量：10次挖掘
试玩时长：30秒
CTA按钮文案：继续挖矿
CTA触发时机：累计金币达标或体力耗尽时弹出全屏CTA
引导手势：进场1秒后显示手指点击砖块挖矿提示，2秒后消失
粒子特效：挖出红包金色粒子爆发、挖出金矿金色光柱+粒子、挖出钻石蓝色星光+彩虹粒子、挖出石块灰色碎片、挖出空砖灰尘粒子、体力耗尽红色警告
动画效果：镐子挥动击打砖块动画、砖块碎裂→奖品弹出动画、体力心形减少动画（红色→灰色）、金币飞入计数器弹跳动画、矿灯忽明忽暗氛围
音效配置：挖矿敲击音效 + 挖出红包欢快音效 + 挖出金矿叮咚音效 + 挖出钻石闪耀音效 + 挖出石块沉闷音效 + 体力耗尽警告音效 + 矿洞环境背景音乐
UI风格：矿洞主题木质边框，顶部体力心形+金币计数器，5x5砖块网格，矿灯装饰，底部镐子图标
镜头效果：挖出钻石时镜头拉近特写+慢动作+蓝色光晕，体力耗尽时镜头红色暗角+微震，挖出金矿时金色光效
开始界面：副标题显示「挖红包得大奖」，矿洞+镐子预览动画`,

  'hongbao-whack': `帮我生成一个打红包试玩
玩法类型：打红包
主题：打地鼠红包
视觉风格：中国风卡通
配色方案：红色底色 #D32F2F + 锤子银#cccccc + 红包金#ffcc00 + 地洞棕#6b4226 + 打击特效黄#ffdd44 + Miss红#ff4444
背景色：#D32F2F
核心元素：9个地洞（3x3排列），红包随机从洞中弹出（弹出高度60-120px），大锤子光标，积分计数器，combo计数器，计时器
核心机制：红包从9个地洞中随机弹出，弹出后停留0.8-1.5秒后缩回，快速点击红包打击获得奖励，连击combo倍率递增，打中金色红包+30分，普通红包+10分，miss不打到其他位置不扣分
胜利条件：累计积分达到目标分数
失败条件：时间耗尽
难度等级：轻松→中等，红包弹出速度从2秒/个→0.8秒/个，后期同时弹出2-3个
关卡设计：无限模式，红包弹出速度逐渐加快，前10秒每次弹出1个、10-20秒弹出2个、20秒后弹出3个，偶尔出现金色红包（同时弹出位置随机）
关卡数量：无限
试玩时长：30秒
CTA按钮文案：领取红包
CTA触发时机：达到目标分数或时间耗尽时弹出全屏CTA
引导手势：进场1秒后显示手指点击红包提示，2秒后消失
粒子特效：打击红包金色粒子爆发+星光、金色红包七彩粒子+光柱、miss红色叉号粒子、连续combo火焰粒子特效、达标烟花粒子
动画效果：红包从地洞中弹出弹性动画（缩放+位移）、打击红包爆裂动画+碎片飞溅、红包缩回动画、大锤子挥动跟随点击、combo数字跳跃放大+火焰、金币飞入计数器
音效配置：打击红包清脆音效 + 金色红包专属欢呼音效 + miss嘟声音效 + combo升级忍者音效 + 时间紧迫滴答音效 + 春节喜庆背景音乐
UI风格：打地鼠主题边框，顶部积分+combo+倒计时，3x3地洞排列，大锤子光标跟随手指，红包地洞装饰
镜头效果：combo 5x+时镜头微震，金色红包出现时镜头拉近特写，combo 10x+时慢动作打击，达标时全屏烟花庆祝
开始界面：副标题显示「打红包赢大奖」，红包从地洞弹出预览动画`,

  'hongbao-zuma': `帮我生成一个红包祖玛试玩
玩法类型：红包祖玛
主题：祖玛红包消除
视觉风格：中国风
配色方案：红色底色 #D32F2F + 祖玛红#ff3344 + 祖玛金#ffcc00 + 祖玛绿#44cc44 + 祖玛蓝#4488ff + 祖玛紫#9944ff + 发射器金#ccaa44
背景色：#D32F2F
核心元素：5种彩色红包球（红🔴、金🟡、绿🟢、蓝🔵、紫🟣）沿螺旋轨道滚动，底部发射器（可旋转瞄准），发射队列显示当前和下一个红包球颜色，消除进度条，分数计数器
核心机制：点击发射器旋转瞄准，再点击发射红包球到轨道上，匹配3个以上同色红包球消除，阻止红包球链到达终点洞口，连消combo额外加分
胜利条件：消除所有红包球
失败条件：红包球链到达终点洞口
难度等级：中等，红包球滚动速度从1.5秒/球→0.8秒/球，轨道长度逐渐缩短
关卡设计：3关递进——第1关3种颜色15个球慢速轨道、第2关4种颜色25个球中速轨道+弯道、第3关5种颜色35个球快速轨道+弯道+交叉
关卡数量：3关
试玩时长：45秒
CTA按钮文案：领取红包
CTA触发时机：通关3关或失败时弹出全屏CTA
引导手势：进场2秒后显示手指点击瞄准+发射提示，3秒后消失
粒子特效：发射红包球飞行轨迹粒子、消除红包球金色粒子爆发+碎片、连消combo火焰粒子、消除完所有球全屏金色粒子+烟花
动画效果：红包球沿轨道滚动旋转动画、发射器旋转瞄准动画、发射红包球飞行弧线动画、消除红包球爆裂缩小组消失、新球加入轨道滚动、轨道上球链碰撞回弹
音效配置：发射红包球飞行音效 + 消除红包球爆炸音效 + 连消升级音效 + 球链到达终点警告音效 + 通关庆祝锣鼓音乐 + 春节喜庆背景音乐
UI风格：祖玛螺旋轨道边框，顶部分数+combo+当前关卡，发射器在底部中央，发射队列显示当前/下一个球，轨道终点洞口标记
镜头效果：连消3次+时镜头微震，球链接近终点时镜头拉近警告+红色闪烁，通关时镜头拉远全屏庆祝
开始界面：副标题显示「红包祖玛大作战」，红包球沿轨道滚动预览动画`,

  // ═══════════════════════════════════════════════════════════
  // 变体快捷指令 prompt（模板体系全量补齐，每个变体显式声明模式字段，
  // 保证需求理解能锁定变体 gameplayType 与身份参数）
  // ═══════════════════════════════════════════════════════════
  'parking-fire-spread': `帮我生成一个火灾救援试玩
玩法类型：火灾救援
主题：烈焰停车场
核心机制：拖拽车辆腾出通道让目标车驶出，火焰扩散蔓延封路，被火点燃的车辆无法移动
核心元素：目标车、火焰蔓延特效、危险警示
核心数值：5x5 棋盘，火焰每 12 秒向相邻格蔓延，限时 80 秒
玩法模式：parkingGameType: fire_spread
胜负条件：目标车驶出通关，火封死通道失败
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: text（文字重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: none（无车辆动画）`,
  'parking-valet': `帮我生成一个代客泊车试玩
玩法类型：代客泊车
主题：豪华酒店停车场
核心机制：顾客开车进场排队等待车位，拖拽车辆停入对应车位完成接待目标
核心元素：顾客车辆、接待队列、停车位
核心数值：6x6 棋盘，5 批顾客，限时 150 秒
玩法模式：parkingGameType: valet
胜负条件：全部顾客停车接待完成通关，超时失败
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）`,
  'parking-traffic-light': `帮我生成一个信号灯通行试玩
玩法类型：信号灯通行
主题：城市路口
核心机制：路口红绿灯交替，红灯闯行累计违章，绿灯快速通过，目标车驶出即胜利
核心元素：红绿灯、目标车、违章计数
核心数值：5x5 棋盘，红灯 8 秒/绿灯 5 秒交替，违章上限 3 次，限时 100 秒
玩法模式：parkingGameType: traffic_light
胜负条件：目标车驶出通关，违章超限失败
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: none（无车辆动画）`,
  'parking-elevator': `帮我生成一个立体车库试玩
玩法类型：立体车库
主题：多层停车塔
核心机制：多层车库通过电梯切换楼层，拖拽跨层腾挪让目标车从底层驶出
核心元素：电梯、楼层指示、多层车库
核心数值：3 层车库每层 5x5，电梯移动耗 1 步，限时 120 秒
玩法模式：parkingGameType: elevator
胜负条件：目标车驶出通关，超时失败
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: text（文字重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）`,
  'parking-meter-fare': `帮我生成一个计价计费试玩
玩法类型：计价计费
主题：收费停车场
核心机制：初始车费余额有限，每次拖拽扣费，尽快移出目标车保住余额
核心元素：计费表、目标车、余额显示
核心数值：5x5 棋盘，初始余额 20 元，每步扣 1 元，限时 120 秒
玩法模式：parkingGameType: meter_fare
胜负条件：目标车驶出且余额不归零通关，余额耗尽失败
UI布局配置：
ctaBtnPos: top（下载按钮在弹窗上方）
resetBtnStyle: hidden（隐藏重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: false（不显示最佳记录）
carAnim: none（无车辆动画）`,
  'parking-taxi-dispatch': `帮我生成一个出租车派单试玩
玩法类型：出租车派单
主题：城市出租车调度中心
核心机制：订单持续派发到不同车位，点击出租车自动驶出完成派单，连单有额外奖励
核心元素：出租车、订单牌、派单队列
核心数值：5x5 棋盘，完成 8 单通关，连单奖励，限时 120 秒
玩法模式：parkingGameType: taxi_dispatch
胜负条件：完成派单数达标通关，超时失败
UI布局配置：
ctaBtnPos: bottom（下载按钮在弹窗下方）
resetBtnStyle: icon（圆形图标重玩按钮）
showMoves: true（显示步数计数器）
showBestRecord: true（显示最佳记录）
carAnim: heartbeat（车辆心跳呼吸动画）`,
  'merge-2048': `帮我生成一个数字2048滑动合成试玩
玩法类型：数字2048滑动合成
主题：数字方块
核心机制：滑动棋盘合并相同数字方块翻倍，合成 2048 通关，无新格可生成失败
核心元素：数字方块（2/4/8/16/32/64/128/256/512/1024/2048）
核心数值：5x5 棋盘，目标方块 2048
玩法模式：mergeMode: slide
胜负条件：合成 2048 通关，棋盘满格失败`,
  'merge-suika': `帮我生成一个西瓜物理投放合成试玩
玩法类型：西瓜物理投放合成
主题：水果乐园
核心机制：顶部投放圆形水果，物理引擎模拟重力碰撞弹跳，相同水果碰撞自动合成升级，合成大西瓜通关
核心元素：11 级圆形水果（樱桃→草莓→葡萄→橘子→柠檬→猕猴桃→番茄→桃子→菠萝→椰子→大西瓜）
核心数值：顶部投放，物理引擎，目标合成大西瓜
玩法模式：mergeMode: suika
胜负条件：合成大西瓜通关，水果溢出屏幕失败`,
  'merge-drop': `帮我生成一个方块下落投放合成试玩
玩法类型：方块下落投放合成
主题：方块坠落
核心机制：顶部投放方块下落堆叠，相同等级方块碰撞自动合并升级，控制堆叠高度不溢出
核心元素：方块等级链（1-11 级）
核心数值：顶部投放，限高 3 格警告，目标合成 11 级方块
玩法模式：mergeMode: drop，dropMergeEnabled: true
胜负条件：合成最高级方块通关，堆叠溢出失败`,
  'merge-chain': `帮我生成一个水果链式合成试玩
玩法类型：水果链式合成
主题：连锁反应
核心机制：拖拽合并相同元素，每次合成触发连锁反应，连续合并 combo 倍率递增最高 3x
核心元素：元素等级链、combo 计数
核心数值：5x5 棋盘，combo 倍率递增，目标 300 分
玩法模式：mergeMode: chain，chainMergeEnabled: true
结算策略提案：游戏全程底部常驻半透明CTA下载条幅，达到目标分数时弹出结算弹窗（CTA下载按钮+再来一次按钮）
胜负条件：达到目标分数通关`,
  'merge-merge3': `帮我生成一个元素三合一合成试玩
玩法类型：元素三合一合成
主题：三倍奇迹
核心机制：拖拽合并相同元素，三个相同元素同时合成触发三合一，获得 1.5x 额外奖励
核心元素：三合一高亮提示、元素等级链
核心数值：5x5 棋盘，三合一奖励 1.5x，目标 300 分
玩法模式：mergeMode: merge3，merge3Enabled: true
胜负条件：达到目标分数通关`,
  'merge-multi-level': `帮我生成一个棋盘多级合成试玩
玩法类型：棋盘多级合成
主题：合成塔
核心机制：每关棋盘扩张、目标升级，合成达成当前关目标后进入下一关，逐关递进
核心元素：关卡切换动画、目标分数条
核心数值：5 关递进，每关目标递增 50%，棋盘逐关扩大
玩法模式：mergeMode: multi_level，multiLevelEnabled: true，multiLevelCount: 5
胜负条件：通关全部 5 关获胜`,
  'merge-army': `帮我生成一个军团拖拽合成试玩
玩法类型：军团拖拽合成
主题：军团对决
核心机制：拖拽合并相同士兵升级，合成高级兵种攻击敌军，击退敌军波次进攻通关
核心元素：士兵等级链、敌军波次、军团血量
核心数值：5x5 棋盘，3 波敌军，目标合成 6 级军团
玩法模式：mergeMode: merge_army
胜负条件：击退全部敌军波次通关`,
  lottery: `帮我生成一个抽奖试玩
玩法类型：幸运大转盘
主题：幸运大转盘
视觉风格：华丽金色
配色方案：深紫底色 #1a0a2e + 金色边框#ffcc00 + 奖品彩色
背景色：#1a0a2e
核心元素：8格大转盘，每格不同奖品（现金红包、优惠券、金币、实物奖品、再来一次、 谢谢参与、钻石、VIP会员），指针，旋转按钮
核心机制：点击旋转转盘按钮，指针随机停在某一格获得对应奖品
胜利条件：抽到大奖
失败条件：无
难度等级：轻松休闲
关卡设计：5次抽奖机会，每次抽奖后指针旋转3-5秒随机停止
关卡数量：5次
试玩时长：30秒
CTA按钮文案：立即下载
CTA触发时机：抽奖次数用完或抽到大奖时弹出
引导手势：进场1秒后显示手指点击抽奖提示
粒子特效：转盘旋转粒子轨迹、指针停止位置金色粒子、中奖金光特效、大奖全屏金色粒子雨
动画效果：转盘旋转减速动画、指针弹跳停止、奖品弹出展示、未中奖安慰动画
音效配置：转盘旋转咔嗒音效 + 指针停止音效 + 中奖欢呼音乐 + 未中奖安慰音效
UI风格：奢华金色转盘边框，顶部剩余次数，奖品展示区，华丽灯光装饰
镜头效果：转盘旋转时镜头微拉远，中大奖时镜头拉近特写+慢动作
开始界面：副标题显示「幸运大转盘」`,

  'lottery-wheel': `帮我生成一个幸运转盘试玩
玩法类型：幸运转盘
主题：嘉年华乐园
视觉风格：华丽复古马戏团
配色方案：深酒红底色 #2d0a1a + 金色边框#ffcc00 + 帐篷黄#ffdd44 + 彩旗彩色
背景色：#2d0a1a
核心元素：6格精美转盘（奖品：现金红包🧧、钻石💎、优惠券🎫、金币🪙、VIP会员👑、再来一次🔄），红色指针，旋转拉杆，彩旗装饰，聚光灯
核心机制：点击旋转拉杆，转盘加速旋转后逐渐减速，指针随机停在某一格获得对应奖品，奖品按概率分布（现金红包10%、钻石15%、优惠券20%、金币25%、VIP会员5%、再来一次25%）
胜利条件：抽到钻石或现金红包大奖
失败条件：无
难度等级：轻松休闲
关卡设计：3次免费旋转机会，每次旋转持续3-5秒随机减速停止，抽到「再来一次」额外+1次
关卡数量：3次（+再来一次额外）
试玩时长：30秒
CTA按钮文案：下载赢更多
CTA触发时机：抽奖次数用完或抽到大奖时弹出全屏CTA
引导手势：进场1秒后显示手指点击旋转拉杆提示，2秒后消失
粒子特效：转盘旋转金色轨迹粒子、指针停止位置金光爆发、钻石奖品彩虹光柱、现金红包金色粒子雨、再来一次蓝色光晕
动画效果：转盘加速旋转→减速→指针弹跳停止的完整物理动画、拉杆下压回弹、奖品弹出展示放大、未中奖时彩旗飘动安慰动画
音效配置：转盘旋转咔嗒咔嗒音效 + 减速时音效节奏放缓 + 指针停止叮咚音效 + 中大奖欢呼号角 + 马戏团背景音乐
UI风格：马戏团帐篷边框，顶部剩余次数+中奖记录，转盘居中展示，彩旗装饰，拉杆在右侧，聚光灯效果
镜头效果：转盘旋转时镜头微拉远，中大奖时镜头拉近特写+慢动作+全屏金光，抽到「再来一次」时镜头微震
开始界面：副标题显示「嘉年华大转盘」，转盘缓慢旋转预览动画`,

  'lottery-scratch': `帮我生成一个刮刮乐试玩
玩法类型：刮刮乐
主题：黄金宝藏
视觉风格：华丽金属质感
配色方案：深蓝绒布底色 #0a1a2a + 刮涂层银色#c0c0c0 + 奖品金色#ffcc00 + 卡片边框金#ccaa44
背景色：#0a1a2a
核心元素：3张精美刮刮卡（金边设计），银色涂层覆盖隐藏奖品（一等奖：钻石💎、二等奖：金币袋🪙、三等奖：优惠券🎫、谢谢参与），刮卡笔/硬币图标
核心机制：手指在刮刮卡上滑动擦除涂层，刮开面积超过60%揭示奖品，每张卡奖品随机，刮完一张自动切换到下一张
胜利条件：刮出一等奖钻石
失败条件：无
难度等级：轻松休闲
关卡设计：3张卡依次刮开，奖品概率：一等奖10%、二等奖20%、三等奖35%、谢谢参与35%，每张卡刮开后有1.5秒揭示动画
关卡数量：3张卡
试玩时长：30秒
CTA按钮文案：下载刮更多
CTA触发时机：刮完3张卡或刮出一等奖时弹出全屏CTA
引导手势：进场1秒后显示手指在涂层上来回滑动提示，2.5秒后消失
粒子特效：刮涂层时银色碎屑粒子飘落、刮出一等奖金色粒子爆发+光柱、二等奖金币粒子雨、三等奖绿色粒子、谢谢参与灰色粒子
动画效果：涂层刮除实时跟随手指轨迹、刮开面积达标时涂层自动消散揭示动画、奖品从卡片中央弹出放大展示、卡片切换翻页动画
音效配置：刮涂层沙沙真实音效 + 刮开揭示叮咚音效 + 一等奖专属欢呼音效 + 二等奖金币音效 + 三等奖提示音 + 宝藏主题背景音乐
UI风格：深蓝绒布展示台边框，顶部剩余卡片数+中奖记录，卡片居中展示金边+阴影，刮卡笔光标跟随手指
镜头效果：刮出一等奖时镜头拉近特写+慢动作+全屏金光，刮开面积达标时镜头微拉远展示全卡
开始界面：副标题显示「刮出你的宝藏」，刮刮卡缓慢旋转展示动画`,

  'lottery-smash': `帮我生成一个砸金蛋试玩
玩法类型：砸金蛋
主题：皇家宝库
视觉风格：华丽宫廷风
配色方案：深红绒布底色 #1a0505 + 金蛋#ffcc00 + 奖品彩色 + 宝石蓝#2244cc + 翡翠绿#22aa44
背景色：#1a0505
核心元素：4个精美金蛋（编号1-4，金箔贴面光泽质感），一把金色小锤，奖品内容（特等奖：皇冠👑、一等奖：钻石💎、二等奖：金币袋🪙、三等奖：宝石💠），红丝绒展示台
核心机制：点击选择金蛋，再点击锤子或直接点击金蛋砸开，随机获得对应奖品，每次砸开有惊喜揭示动画
胜利条件：砸出特等奖皇冠
失败条件：无
难度等级：轻松休闲
关卡设计：4个金蛋对应4种奖品（特等奖1个、一等奖1个、二等奖1个、三等奖1个），玩家可自由选择砸蛋顺序，砸完所有蛋或砸出特等奖结束
关卡数量：4个金蛋
试玩时长：25秒
CTA按钮文案：下载赢皇冠
CTA触发时机：砸出特等奖或砸完所有金蛋时弹出全屏CTA
引导手势：进场1秒后显示手指点击金蛋提示，2秒后消失
粒子特效：砸蛋时金色蛋壳碎片粒子四散飞溅、特等奖彩虹粒子+金色光柱+全屏星星、一等奖钻石闪耀粒子、二等奖金币粒子雨、三等奖宝石蓝光粒子
动画效果：金蛋被砸时裂开→碎裂→奖品跃出弹跳展示、锤子挥动弧线动画、金蛋选中时金色边框高亮脉冲、奖品弹出放大旋转展示
音效配置：砸蛋清脆碎裂音效 + 蛋壳飞溅音效 + 特等奖皇家号角欢呼音效 + 一等奖钻石音效 + 二等奖金币音效 + 三等奖宝石音效 + 宫廷背景音乐
UI风格：红丝绒展示台边框，顶部剩余金蛋数+中奖记录，4个金蛋水平排列，金色小锤在右侧，华丽灯光装饰
镜头效果：砸蛋瞬间镜头拉近特写+慢动作+蛋壳慢镜头飞溅，砸出特等奖时全屏金色光晕+镜头微震，砸完最后一个蛋时镜头拉远展示全部奖品
开始界面：副标题显示「砸开你的幸运」，金蛋微微晃动预览动画`,

  'lottery-draw': `帮我生成一个幸运抽奖试玩
玩法类型：幸运抽奖
主题：星空许愿池
视觉风格：梦幻星空
配色方案：深空蓝紫底色 #0a0a2e + 星光白#ffffff + 许愿金币#ffcc00 + 幸运紫#9944ff + 流星蓝#4488ff
背景色：#0a0a2e
核心元素：精美许愿抽奖箱（星空纹理），5个发光许愿球，奖品池（五星：传说装备⚔️、四星：钻石💎、三星：金币袋🪙、二星：优惠券🎫、一星：道具🎁），流星装饰
核心机制：点击抽取按钮，许愿球从抽奖箱中飞出随机展示结果，奖品按星级概率分布（五星5%、四星10%、三星20%、二星30%、一星35%），每次抽取有光效揭示
胜利条件：抽到五星传说装备
失败条件：无
难度等级：轻松休闲
关卡设计：5次免费抽取机会，每次抽取许愿球从箱中飞出旋转展示，奖品星级越高光效越华丽
关卡数量：5次
试玩时长：30秒
CTA按钮文案：下载继续抽
CTA触发时机：抽取次数用完或抽到五星传说时弹出全屏CTA
引导手势：进场1秒后显示手指点击抽取按钮提示，2秒后消失
粒子特效：抽取时星空粒子漩涡、五星传说彩虹光柱+全屏流星雨、四星钻石闪耀粒子、三星金币粒子雨、二星蓝色光点、一星绿色微光
动画效果：许愿球从抽奖箱中飞出旋转→裂开→奖品跃出展示、抽奖箱盖子开合动画、奖品星级越高旋转圈数越多揭示越慢、流星划过背景
音效配置：抽取时魔法音效 + 许愿球飞行音效 + 五星传说专属史诗号角音效 + 四星钻石清脆音效 + 三星金币音效 + 二星一星提示音 + 星空背景音乐
UI风格：星空许愿池边框，顶部剩余次数+中奖记录，抽奖箱居中展示，抽取按钮在下方，流星划过装饰
镜头效果：抽取时镜头拉近抽奖箱，许愿球飞出时镜头跟随，抽到五星传说时镜头拉近特写+慢动作+全屏彩虹光效，连续抽到高星时镜头微震
开始界面：副标题显示「许愿抽大奖」，许愿球漂浮旋转预览动画`,

  // ═══════════════════════════════════════════════════════════
  // 答题系列
  // ═══════════════════════════════════════════════════════════
  'quiz-image': `帮我生成一个看图猜物试玩
玩法类型：看图猜物
主题：趣味猜图
视觉风格：清新卡通
配色方案：浅蓝底色 #E1F5FE + 选项彩色
背景色：#E1F5FE
核心元素：被遮挡的图片、4个选项按钮、计时器
核心机制：图片逐渐揭开遮挡层，越快猜中得分越高，从4个选项中选出正确答案
胜利条件：正确猜出图片内容
失败条件：答错或时间耗尽
难度等级：遮挡层数和速度递增
关卡设计：5题——图片逐渐更难猜，遮挡层从厚→薄
关卡数量：5题
试玩时长：40秒
CTA按钮文案：开始挑战
CTA触发时机：完成5题或答错时弹出
引导手势：进场1.5秒后显示手指点击选项提示
粒子特效：正确答题绿色闪光粒子、错误答题红色抖动、连续答对combo星星
动画效果：图片遮挡层逐渐揭开动画、选项按钮点击缩放、正确/错误反馈动画、分数飞字
音效配置：答对清脆音效 + 答错提醒音效 + 倒计时紧迫音效 + 趣味背景音乐
UI风格：清新蓝色边框，顶部题目进度+分数+计时器，4个选项圆角按钮，图片展示区
镜头效果：正确时镜头微拉近，连续答对时combo特效
开始界面：副标题显示「看看你认识多少」`,

  'quiz-knowledge': `帮我生成一个知识竞赛试玩
玩法类型：知识竞赛
主题：百科知识挑战
视觉风格：现代科技感
配色方案：深蓝竞技底色 #0a1a2a + 正确答案绿#44cc44 + 错误答案红#ff4444 + 倒计时金#ffcc00 + 问题高亮白#ffffff
背景色：#0a1a2a
核心元素：知识题目文本（涵盖历史、地理、科学、文化、娱乐5大领域），4个选项按钮（A/B/C/D），倒计时环，分数计数器，连击combo显示
核心机制：阅读题目后从4个选项中选择正确答案，每题限时8秒，答对得分+combo，答错或超时扣血，连续答对combo倍率递增（1x→2x→3x）
胜利条件：5题全部答对
失败条件：答错3题生命耗尽或时间耗尽
难度等级：题目难度从简单→困难递增，第1题送分题，第5题高难度
关卡设计：5题依次作答——第1题简单常识、第2题中等地理、第3题中等科学、第4题困难历史、第5题困难文化，每题限时8秒
关卡数量：5题
试玩时长：45秒
CTA按钮文案：挑战更多
CTA触发时机：完成全部5题或生命耗尽时弹出
引导手势：进场1.5秒后显示手指点击选项提示，3秒后消失
粒子特效：答对绿色闪光粒子+对勾浮现、答错红色抖动粒子+叉号、连续答对combo金色飞字+星星粒子、全部答对彩虹庆祝粒子
动画效果：题目从底部滑入动画、选项按钮点击缩放反馈、正确/错误颜色闪烁反馈、倒计时环缩小动画、combo数字弹跳放大、生命心形减血动画
音效配置：答对清脆叮咚音效 + 答错低沉警告音效 + 倒计时紧迫滴答音效 + combo升级欢呼音效 + 竞赛紧张背景音乐
UI风格：竞技主题深蓝边框，顶部题目进度条+分数+combo+生命，4个选项圆角按钮（A/B/C/D），倒计时环在题目右上角
镜头效果：全部答对时镜头拉远全屏庆祝+慢动作，连续combo时镜头微震，答错时屏幕红色闪烁
开始界面：副标题显示「百科知识大挑战」，题目卡片翻转预览动画`,

  'quiz-word': `帮我生成一个文字问答试玩
玩法类型：文字问答
主题：趣味脑筋急转弯
视觉风格：活泼卡通
配色方案：明亮黄底色 #FFF8E1 + 问题卡片白#ffffff + 选项彩色按钮 + 正确绿#44cc44 + 错误红#ff6644
背景色：#FFF8E1
核心元素：趣味文字谜题（脑筋急转弯/谜语/冷笑话问答），4个彩色选项按钮，灯泡提示图标💡，表情反馈动画😊😢
核心机制：阅读趣味文字题目，从4个选项中选出正确答案，每题限时10秒，答对得分+开心表情，答错扣分+遗憾表情，提示按钮每关可用1次
胜利条件：5题全部答对
失败条件：答错3题或时间耗尽
难度等级：轻松→中等，题目从简单谜语→烧脑急转弯递增
关卡设计：5题——第1题简单谜语热身、第2题中等急转弯、第3题文字游戏、第4题冷笑话问答、第5题终极烧脑题，每题限时10秒，提示按钮每关1次
关卡数量：5题
试玩时长：40秒
CTA按钮文案：开始答题
CTA触发时机：完成全部5题或答错3题时弹出
引导手势：进场1.5秒后显示手指点击选项提示，2.5秒后消失
粒子特效：答对绿色星星粒子+笑脸表情弹出、答错红色叉号粒子+哭脸表情、连续答对combo彩色纸屑、全部答对气球🎈飘落庆祝
动画效果：题目卡片翻转入场、选项按钮点击弹性缩放+颜色变化、正确/错误表情弹跳动画、灯泡提示闪烁、combo数字跳跃
音效配置：答对欢快叮咚音效 + 答错幽默嘟声音效 + 倒计时滑稽滴答音效 + 提示灯泡音效 + 轻快趣味背景音乐
UI风格：卡通风格明亮边框，顶部题目进度+分数+combo+提示按钮，4个彩色圆角选项按钮，灯泡提示图标，表情反馈区
镜头效果：全部答对时镜头拉远全屏气球庆祝，连续答对时逗趣表情放大，答错时屏幕微晃
开始界面：副标题显示「开动你的脑筋」，灯泡闪烁动画`,

  'guess-song': `帮我生成一个答题猜歌试玩
玩法类型：答题猜歌（guess-song）
主题：听歌识曲答题赚钱
视觉风格：炫彩霓虹
配色方案：深紫底色 #1a0033 + 霓虹粉#ff4488 + 电光蓝#4488ff + 音符金#ffcc00 + 现金红#ff3355
背景色：#1a0033
核心元素：音乐播放器/唱片界面、音符🎵、播放按钮、现金奖励飘落、彩带特效
核心机制：点击播放听一段歌曲片段（模拟音频波形/唱片旋转动画），从4个歌名选项中选出正确答案，答对即弹现金奖励（答1题3元），累计金额引导下载领取
奖励设定：每答对一题奖励3元，答对2题共6元，结算弹窗展示累计金额
胜利条件：答对全部2题
失败条件：答错3次或时间耗尽
难度等级：中等
关卡设计：固定2题
关卡数量：2题
试玩时长：30秒
CTA按钮文案：立即下载领取
CTA触发时机：答对全部2题或答错3次时弹出结算领奖弹窗
引导手势：进场1.5秒后显示手指点击播放按钮，作答阶段指向选项
粒子特效：答对现金/金币飘落+霓虹音符粒子+彩带庆祝，答错红色波形抖动并揭示正确答案
动画效果：音频波形跳动/唱片旋转动画、选项按钮弹性缩放、正确绿色灯光闪烁、错误红色灯光警告
音效配置：歌曲片段播放真实带人声歌曲（CDN 音频），进入每题自动播放、按 clipDuration 截断听两句 + 答对欢快提示音 + 答错遗憾音效
UI风格：炫彩霓虹边框，顶部红包里程碑进度条（¥3→¥6）+「1道题≈3元」，中央旋转唱片/跳动波形+播放按钮，底部宽条歌名选项按钮
镜头效果：答对时弹红包「恭喜获得红包3元 无需领取已到账户」+现金飘落，答错时红色警报闪烁并揭示正确歌名
开始界面：副标题显示「听歌识曲，答对领现金」

【必须用真实歌曲，禁止用合成旋律】题库写入 meta.quizQuestions，quizMode 设为 "song"，clipDuration 设为 8。默认使用以下挂在 CDN 上的三首真实带人声歌曲（歌名即链接中的汉字），每题包含：
- title：正确歌名，【必须与 clipUrl 是同一首歌】——clipUrl 播放哪首歌，title 就必须写那首歌名（歌名即链接中的汉字）
- clipUrl：真实歌曲音频地址（CDN URL，带人声）
- wrong：3个风格相近的干扰歌名（4选1），【严禁包含 title 本身】，且必须与 title 都不相同
可用歌曲：
  ① 窗边残梦 → https://webstatic.sigmob.cn/playgen/music/窗边残梦.mp3
  ② 小半 → https://webstatic.sigmob.cn/playgen/music/小半.mp3
  ③ 做个神仙 → https://webstatic.sigmob.cn/playgen/music/做个神仙.mp3
示例题目：{ "title": "窗边残梦", "clipUrl": "https://webstatic.sigmob.cn/playgen/music/窗边残梦.mp3", "wrong": ["小半", "做个神仙", "晴天"] }
进入每题自动播放真实歌曲片段，可点唱片重听；不要生成 melody 简谱旋律`,

  'idiom-fill': `帮我生成一个选字填成语试玩
玩法类型：选字填成语（idiom-fill）
主题：成语填空答题赚钱
视觉风格：国风山水水墨书卷
配色方案：浅米书卷底 #efe6d0 + 朱红#c0392b + 墨绿#2d6a4f + 描金#b8860b + 现金红#ff3355
背景色：#efe6d0
核心元素：国风山水背景、木质卷轴题框、缺字田字格空位、汉字字格、顶部红包进度条、金币/彩带特效
核心机制：题框展示缺1个字的四字成语（如「千军__马」），从下方2个候选字中点击正确的字飞入空位补齐成语，答对即弹现金奖励（答1题10元），顶部红包进度条累计点亮引导下载领取
奖励设定：每答对一题奖励10元，答对2题共20元，顶部红包进度条¥10→¥20，结算弹窗展示累计金额
胜利条件：答对全部2题
失败条件：答错3次或时间耗尽
难度等级：简单，常见成语，第1题极常见热身、第2题稍难制造悬念
关卡设计：固定2题——第1题「千军万马」缺「万」、第2题经典四字成语缺1字
关卡数量：2题
试玩时长：30秒
CTA按钮文案：立即下载领取
CTA触发时机：答对全部2题或答错3次时弹出结算领奖弹窗
引导手势：进场1.5秒后显示手指点击正确候选字，作答阶段指向候选字
粒子特效：答对金币/现金飘落+彩带庆祝+书卷发光，答错候选字红色抖动弹回并揭示正确字
动画效果：候选字飞入空位动画、成语整体绿色高亮闪烁、红包节点点亮跳动、错误红色警告
音效配置：选字音效 + 答对欢快提示音 + 答错遗憾音效 + 金币领奖音效 + 国风背景音乐
UI风格：国风描金木框，顶部红包进度条+累计金额，中央木质卷轴题框展示缺字成语，底部候选字按钮
镜头效果：答对时全屏彩带+金币飘落，答错时红色警报闪烁
开始界面：副标题显示「答对成语领现金」`,

  'arrow': `帮我生成一个箭头移动试玩
玩法类型：箭头移动
主题：几何方块
视觉风格：极简扁平
配色方案：["#FF4455","#4488FF","#44CC66","#FFCC00","#9944FF","#FF8844"]
背景色：#222233
核心元素：10个彩色方块——红色2格横向、蓝色3格横向、绿色2格横向、黄色2格纵向、紫色3格横向、橙色2格纵向，每个方块有箭头标记移动方向
核心机制：点击方块向其箭头方向移动，目标是将所有方块移出棋盘右侧出口
胜利条件：所有方块移出棋盘
失败条件：时间耗尽
难度等级：中等，方块数量和箭头方向复杂度递增
关卡设计：3关递进——第1关5x5 7个方块（全右箭头）120秒教学关、第2关6x6 8个方块（右+下箭头混合）150秒中级关、第3关6x6 10个方块（多方向箭头）180秒困难关
关卡数量：3关
试玩时长：30秒
CTA按钮文案：免费下载
CTA触发时机：通关全部关卡或时间耗尽时弹出
结算策略提案：每关过关弹出结算弹窗（CTA下载按钮+下一关按钮），失败弹出结算弹窗（CTA下载按钮+重试按钮），最后一关过关弹窗按钮显示再来一次
引导手势：进场1.5秒后显示手指点击+右箭头提示，2.5秒后消失
粒子特效：方块移动拖尾粒子、方块到达出口绿色烟花、时间不足红色警告闪烁
动画效果：方块点击弹性缩放+移动丝滑滑动150ms、出口箭头呼吸闪烁、过关时所有方块飞出动画、时间不足时方块抖动
音效配置：方块移动咔嗒音效 + 方块到达出口叮咚音效 + 时间不足警告滴答音效 + 过关庆祝音效 + 电子节奏背景音乐
UI风格：极简暗色背景，顶部关卡进度+计时器，底部重置按钮，右侧出口绿色箭头标记
镜头效果：全部方块移出时全屏庆祝+色彩粒子爆发，时间不足时屏幕红色边框闪烁
开始界面：副标题显示「箭指出口」，方块飞入动画`,

  // ═══════════════════════════════════════════════════════════
  // 箭头十一态新变体（arrow-spec.ts ARROW_VARIANTS 对齐：classic-cell/time-blitz/color-match/
  // one-stroke/chain-combo/relay/reverse/domino/fewest-moves/pixel-drain/pvp-lite）
  // ═══════════════════════════════════════════════════════════
  'arrow-classic-cell': `帮我生成一个方块箭头试玩
玩法类型：方块箭头（单格）
主题：几何单格
核心机制：点击带箭头单格方块，方块沿箭头方向滑出棋盘，无 HP 无失败
核心元素：单格箭头方块、棋盘网格
配色方案：["#FF5252","#448AFF","#FFFFFF"]
核心数值：3x3 棋盘，6 个单格方块
玩法模式：arrowVariant: classic-cell
结算策略提案：游戏全程底部常驻半透明CTA下载条幅，胜利时弹出结算弹窗（CTA下载按钮+再来一次按钮）
胜负条件：全部方块滑出棋盘即胜利，无失败条件`,
  'arrow-time-blitz': `帮我生成一个限时清空箭头试玩
玩法类型：限时清空
主题：时间挑战
核心机制：首次点击启动 60s 倒计时，点击箭头方块沿方向滑出，移除 ≥80% 即胜利，无 HP 无惩罚
核心元素：箭头方块、倒计时环
配色方案：["#FF2D55","#00E5FF","#FFE600","#7C4DFF","#00E676","#FF9100","#FF4081","#18FFFF"]
核心数值：8x10 棋盘，16 个箭头，限时 60 秒，通关比例 80%
玩法模式：arrowVariant: time-blitz
结算策略提案：胜利弹出结算弹窗（CTA下载按钮+再来一次按钮），胜利瞬间顶部同步浮出CTA下载横幅停留5秒，失败弹窗（CTA下载按钮+重试按钮）
胜负条件：倒计时内移除 ≥80% 箭头胜利，超时按比例判定失败`,
  'arrow-color-match': `帮我生成一个颜色配对箭头试玩
玩法类型：颜色配对
主题：色彩双星
核心机制：双击同色箭头配对，双箭头沿各自方向同步滑出，轨迹不重叠才可配对；轨迹重叠或点错颜色扣血；可一次性洗牌
核心元素：同色箭头对、选中金环、洗牌按钮
配色方案：["#FF6B6B","#4ECDC4","#45B7D1","#96CEB4","#FFEAA7","#FF69B4","#FFD700","#00E676"]
核心数值：8x10 棋盘，6 对双色箭头，3 点生命，开局至少 2 对可解
玩法模式：arrowVariant: color-match
结算策略提案：游戏进行到中期弹出一次CTA下载横幅，胜利弹出结算弹窗（CTA下载按钮+再来一次按钮），失败弹窗（CTA下载按钮+重试按钮）
胜负条件：全部箭头配对移出胜利，生命扣完失败`,
  'arrow-one-stroke': `帮我生成一个一笔画路径箭头试玩
玩法类型：一笔画路径
主题：路径迷宫
核心机制：按路径顺序（头尾相连）依次点击箭头，路径中断或顺序错误计数，达上限失败；下一支箭头高亮提示
核心元素：高亮下一支、路径顺序提示
配色方案：["#00B8D9","#FFC400","#00A3FF","#FF8B00"]
核心数值：8x10 棋盘，10 支箭头，路径中断上限 3 次
玩法模式：arrowVariant: one-stroke
结算策略提案：胜利弹出结算弹窗（CTA下载按钮+再来一次按钮）且游戏全程底部常驻半透明CTA下载条幅，失败弹窗（CTA下载按钮+重试按钮）
胜负条件：按序移除全部箭头胜利，路径中断达上限失败`,
  'arrow-chain-combo': `帮我生成一个顺序连击箭头试玩
玩法类型：顺序连击
主题：编号关卡
核心机制：箭头带编号，必须从小到大依次点击移除，连击加分，顺序错误扣血
核心元素：编号标记、combo 计数
配色方案：["#FF9FF3","#54A0FF","#5F27CD","#01A3A4","#F368E0","#FF9F43","#00D2D3","#576574"]
核心数值：6x8 棋盘，10 支编号箭头，3 点生命
玩法模式：arrowVariant: chain-combo
胜负条件：全部箭头按序移除胜利，生命扣完失败`,
  'arrow-relay': `帮我生成一个箭头接力试玩
玩法类型：箭头接力
主题：接力传递
核心机制：只有当前激活箭头可点（其余灰显），移除后自动激活最近的下一支
核心元素：激活高亮、灰显箭头
配色方案：["#FF7043","#FFB74D","#66BB6A","#26A69A","#EF5350","#FFCA28"]
核心数值：6x8 棋盘，10 支箭头，3 点生命
玩法模式：arrowVariant: relay
胜负条件：接力移除全部箭头胜利，生命扣完失败`,
  'arrow-reverse': `帮我生成一个反向模式箭头试玩
玩法类型：反向模式
主题：逆向思维
核心机制：箭头指向不变但实际移动方向相反，考验逆向思维，开局提示反向规则
核心元素：反向标记、方向提示
配色方案：["#7C4DFF","#FF4081","#536DFE","#F50057"]
核心数值：6x6 棋盘，6 支反向箭头，3 点生命
玩法模式：arrowVariant: reverse
胜负条件：全部箭头移出胜利，生命扣完失败`,
  'arrow-domino': `帮我生成一个多米诺连锁箭头试玩
玩法类型：多米诺连锁
主题：连锁反应
核心机制：点击链头箭头触发连锁，后方箭头依次自动移除，步数预算内完成
核心元素：连锁动画、链头标记
配色方案：["#E53935","#FDD835","#FB8C00","#FF7043"]
核心数值：6x6 棋盘，9 支箭头，3 条连锁链，步数预算 5 步
玩法模式：arrowVariant: domino
胜负条件：连锁移除全部箭头胜利，超步数失败`,
  'arrow-fewest-moves': `帮我生成一个最少步数箭头试玩
玩法类型：最少步数
主题：步数挑战
核心机制：点击次数计入步数，目标步数内清空全部箭头，可撤销悔步
核心元素：步数 HUD、撤销按钮
配色方案：["#00897B","#43A047","#00ACC1","#7CB342"]
核心数值：6x6 棋盘，10 支箭头，步数预算 13 步
玩法模式：arrowVariant: fewest-moves
胜负条件：预算内清空全部箭头胜利，超步失败`,
  'arrow-pixel-drain': `帮我生成一个像素点消除箭头试玩
玩法类型：像素点消除
主题：像素画布
核心机制：箭头滑出时在路径留下像素轨迹，轨迹覆盖全部高亮目标格即胜利
核心元素：高亮目标格、像素轨迹特效
配色方案：["#651FFF","#00E5FF","#F50057","#FFD600","#00E676"]
核心数值：6x8 棋盘，8 支箭头，3 点生命
玩法模式：arrowVariant: pixel-drain
胜负条件：像素轨迹覆盖全部目标格胜利，生命扣完失败`,
  'arrow-pvp-lite': `帮我生成一个双人对战箭头试玩
玩法类型：双人对战
主题：镜像竞技场
核心机制：屏幕分上下两半镜像棋盘，玩家点击下方棋盘清空自己的箭头，AI 随机点击上半棋盘，先清空自己一侧者胜
核心元素：上下镜像棋盘、AI 对手、胜负横幅
配色方案：["#E53935","#1E88E5","#EF5350","#42A5F5"]
核心数值：6x6 棋盘，每侧 8 支箭头
玩法模式：arrowVariant: pvp-lite
胜负条件：先清空自己一侧箭头即胜，无失败条件`,

  // ═══════════════════════════════════════════════════════════
  // 箭头八态变体（arrowVariant 由配置派生，见 gameplay-config.tool VARIANT_PARAM_DERIVATION）
  // ═══════════════════════════════════════════════════════════
  'arrow-push-chain': `帮我生成一个连推箭头试玩
玩法类型：连推箭头
主题：推箱走廊
核心机制：点击方块沿箭头方向滑动，撞到前方方块时连推一串继续前进，一推到底
核心元素：方块队列、推力传导特效
配色方案：["#546E7A","#90A4AE","#FFB300","#37474F"]
核心数值：5x5 棋盘，单次连推最多 4 块，限时 90 秒
玩法模式：arrowVariant: push-chain
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-teleport': `帮我生成一个传送箭头试玩
玩法类型：传送箭头
主题：时空隧道
核心机制：点击方块沿箭头方向移动，触碰棋盘边缘从对侧传送回来继续移动
核心元素：传送门、空间扭曲特效
配色方案：["#8E24AA","#00BCD4","#D500F9","#00E5FF"]
核心数值：5x5 棋盘，上下/左右边缘互通，限时 90 秒
玩法模式：arrowVariant: teleport
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-ice-slide': `帮我生成一个冰面箭头试玩
玩法类型：冰面箭头
主题：极地冰面
核心机制：点击方块沿箭头方向滑行，冰面不减速直到撞到障碍或边界才停，滑行距离不可控
核心元素：冰面光泽、滑行轨迹特效
配色方案：["#4FC3F7","#E1F5FE","#29B6F6","#B3E5FC","#81D4FA","#0288D1"]
核心数值：5x5 棋盘，单次滑行 1-4 格，限时 90 秒
玩法模式：arrowVariant: ice-slide
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-turn-tile': `帮我生成一个转向箭头试玩
玩法类型：转向箭头
主题：迷宫街区
核心机制：点击转向板改变方向，方块经过转向板时转向沿新方向继续移动
核心元素：转向板、方向指示箭头
配色方案：["#FFC400","#212121","#FF6F00","#FFE082"]
核心数值：5x5 棋盘，转向板 4 个，限时 90 秒
玩法模式：arrowVariant: turn-tile
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-multi-head': `帮我生成一个多头箭头试玩
玩法类型：多头箭头
主题：多向枢纽
核心机制：点击方块后同时向多个箭头方向移动，多路线并进移出棋盘
核心元素：多向箭头标记、分身轨迹特效
配色方案：["#FF3D00","#00C853","#2979FF","#FFD600","#AA00FF","#00B8D4"]
核心数值：5x5 棋盘，方块含 2-3 个箭头方向，限时 90 秒
玩法模式：arrowVariant: multi-head
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-split': `帮我生成一个分裂箭头试玩
玩法类型：分裂箭头
主题：细胞分裂
核心机制：点击方块沿箭头方向移动，撞墙时分裂成两个小方块反向移动
核心元素：分裂特效、小方块分身
配色方案：["#FF9800","#4CAF50","#FFB74D","#81C784"]
核心数值：5x5 棋盘，每个方块最多分裂 2 次，限时 90 秒
玩法模式：arrowVariant: split
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,
  'arrow-queue': `帮我生成一个队列箭头试玩
玩法类型：队列箭头
主题：列车调度
核心机制：方块按队列顺序依次移动，点击激活队首方块前进一格，放行后下一块接上
核心元素：队列序号标记、调度台
配色方案：["#43A047","#FDD835","#66BB6A","#FFF176"]
核心数值：5x5 棋盘，队列 8 个方块，限时 90 秒
玩法模式：arrowVariant: queue
胜负条件：所有方块移出棋盘通关，时间耗尽失败`,

  // ═══════════════════════════════════════════════════════════
  // 塔防
  // ═══════════════════════════════════════════════════════════
  'tower-defense': `帮我生成一个塔防试玩
玩法类型：塔防
主题：中世纪城堡防御
视觉风格：卡通奇幻
配色方案：深紫底色 #4A148C + 塔彩色 + 怪物暗色
背景色：#4A148C
核心元素：5种防御塔——箭塔（快速远程）、法师塔（范围魔法）、炮塔（高伤害范围）、冰塔（减速冰冻）、雷电塔（连锁电击），3种怪物——步兵（快速）、重甲兵（高血量）、飞龙（飞行无视地形）
核心机制：点击建造防御塔拖放至地图建造点，怪物沿路线前进，防御塔自动攻击，消灭怪物获得金币建造更多塔
胜利条件：守住所有怪物波次
失败条件：怪物到达城堡（3条命）
难度等级：怪物波次递增，从简单→困难
关卡设计：3关——第1关5波怪物+2种塔、第2关8波+3种塔+重甲兵、第3关10波+5种塔+飞龙BOSS
关卡数量：3关
试玩时长：50秒
CTA按钮文案：开始布防
CTA触发时机：守住所有波次或城堡被攻破时弹出
引导手势：进场2秒后显示手指点击建造+拖放提示
粒子特效：箭塔射箭轨迹粒子、法师塔魔法光球、炮塔爆炸火焰、冰塔冰冻碎片、雷电塔电光连锁、怪物死亡消散粒子
动画效果：箭飞行轨迹、魔法弹飞行、炮弹抛物线、冰冻减速、雷电连锁跳转、怪物行走、死亡消散
音效配置：战斗音效（射箭/魔法/炮弹/冰冻/雷电）+ 怪物死亡音效 + BOSS出场警报 + 史诗背景音乐
UI风格：中世纪城堡边框，顶部金币+生命+波次，底部塔建造面板，地图路线展示
镜头效果：BOSS出场时镜头拉远，城堡被攻击时红色闪烁，胜利时全屏庆祝
开始界面：副标题显示「保卫城堡」`,

  'tower-defense-maze': `帮我生成一个迷宫路径塔防试玩
玩法类型：迷宫路径塔防
主题：魔法森林迷宫防御
视觉风格：卡通奇幻
配色方案：森林绿底色 #2E7D32 + 路径土黄#8D6E63 + 塔彩色 + 怪物暗色
背景色：#1B5E20
核心元素：迷宫路径、3种防御塔——箭塔（快速）、法师塔（范围）、炮塔（高伤）、3种怪物——步兵/重甲/飞行
核心机制：怪物沿固定迷宫路径行进，在路径两侧建造防御塔拦截消灭
胜利条件：守住所有怪物波次
失败条件：3只怪物到达终点
难度等级：怪物波次递增，路径更长、怪物更多更强
关卡设计：3关——第1关5波简单路径+2种塔、第2关8弯路径+3种塔+重甲兵、第3关10波复杂迷宫+全塔+飞行怪
关卡数量：3关
试玩时长：50秒
CTA按钮文案：下载布防
CTA触发时机：守住所有波次或怪物到达终点时弹出
引导手势：进场2秒后显示点击建造+拖放提示
粒子特效：箭塔射箭粒子、法师塔魔法光球、炮塔爆炸火焰、怪物死亡消散
动画效果：怪物沿路径行走、防御塔攻击动画、弹道飞行、怪物死亡
音效配置：战斗BGM + 建造音效 + 攻击音效 + 怪物死亡音效
UI风格：森林木质边框，顶部金币+生命+波次，底部塔面板
镜头效果：BOSS出场镜头拉远，胜利全屏庆祝
开始界面：副标题显示「迷宫守卫」`,

  'tower-defense-endless': `帮我生成一个无尽波次塔防试玩
玩法类型：无尽波次塔防
主题：星际基地防御
视觉风格：科幻未来
配色方案：深空底色 #1a1a2e + 塔蓝#448aff + 怪物红#ff5252 + 能量金#ffd740 + 护盾青#00e5ff
背景色：#0a0a2e
核心元素：基地核心、5种防御塔——激光塔/导弹塔/电磁塔/冰冻塔/等离子塔、无尽波次怪物
核心机制：建造和升级防御塔抵御无尽波次敌人，每波结束获得金币可升级塔
胜利条件：挑战最高波次数记录
失败条件：基地血量归零
难度等级：每波怪物数量和血量递增，每5波出现精英怪
关卡设计：无尽模式——第1-5波基础怪、第6-10波+精英、第11-15波+BOSS级、之后循环递增
试玩时长：60秒
CTA按钮文案：下载挑战
CTA触发时机：基地被摧毁时弹出
引导手势：进场2秒后显示建造+升级提示
粒子特效：激光束粒子、导弹尾焰、电磁连锁、冰冻碎片、等离子爆炸
动画效果：塔旋转瞄准、弹道追踪、怪物行走、升级闪光
音效配置：科幻战斗BGM + 建造音效 + 攻击音效 + 波次警报 + BOSS出场音
UI风格：科幻全息边框，顶部金币+基地血量+当前波次+最高记录
镜头效果：BOSS波次镜头拉远，基地被攻击红色闪烁
开始界面：副标题显示「无尽防线」，基地展示动画`,

  // ═══════════════════════════════════════════════════════════
  // 休闲杂项
  // ═══════════════════════════════════════════════════════════
  'misc-ball-sort': `帮我生成一个球分类试玩
玩法类型：球分类
主题：彩色球分类
视觉风格：简约清新
配色方案：白色底色 #FFFFFF + 球彩色——红球#ff3344 + 蓝球#3366ff + 绿球#33cc66 + 黄球#ffcc00 + 紫球#9944ff + 玻璃管透明#e0e0e0
背景色：#FFFFFF
核心元素：7个透明玻璃管，5种颜色球（红蓝绿黄紫各4个），2个空管作为辅助管
核心机制：点击选中玻璃管，再点击目标管倒球，只能倒入空管或顶部颜色相同且有多余空间的管，每步只能倒1个球（从管顶取出最上面的球）
胜利条件：每个管中只有一种颜色球（4个同色球在同一个管中）
失败条件：步数用完
难度等级：中等，第1关简单，第3关需要策略规划
关卡设计：3关递进——第1关4种颜色5管15步入门、第2关5种颜色6管20步中等、第3关5种颜色7管+限制25步困难
关卡数量：3关
试玩时长：45秒
CTA按钮文案：解锁更多关卡
CTA触发时机：通关3关或步数耗尽时弹出
引导手势：进场2秒后显示手指点击试管倒球提示，2.5秒后消失
粒子特效：球倒入水花飞溅粒子、完成排序时试管闪光粒子、步数用尽红色警告
动画效果：球从管中弹出倒入目标管弹性动画250ms、球在管中堆叠排列动画、完成排序时管中球闪烁庆祝、选中管高亮边框脉冲
音效配置：球倒入咕噜音效 + 完成排序清脆叮咚音效 + 步数用尽警告音效 + 轻松解谜背景音乐
UI风格：白色简约边框，顶部步数计数+最佳记录，试管水平排列，选中管高亮边框，球颜色清晰可辨
镜头效果：完成排序时镜头拉近试管特写展示，连续正确操作时镜头微震
开始界面：副标题显示「分拣彩色球」，球在管中排列预览动画`,

  'misc-catch': `帮我生成一个接物投掷试玩
玩法类型：接物投掷
主题：金币雨
视觉风格：明亮卡通
配色方案：天空蓝底色 #87CEEB + 云朵白#ffffff + 金币金#ffcc00 + 炸弹红#ff3344 + 钻石蓝#4488ff + 接物篮棕#8b5e3c
背景色：#87CEEB
核心元素：接物篮/角色（底部可左右移动），从上方掉落的物品——金币🪙（+10分）、钻石💎（+50分）、红包🧧（+30分）、炸弹💣（-1命）、石头🪨（-5分），云朵装饰
核心机制：手指左右滑动或点击屏幕左右两侧移动接物篮接住掉落物品，接到金币/钻石/红包加分，接到炸弹扣命，接到石头扣分，物品掉落速度逐渐加快
胜利条件：达到目标分数
失败条件：生命耗尽（3条命）
难度等级：中等，物品掉落速度从3单位/秒→6单位/秒递增，炸弹出现频率从10%→25%
关卡设计：3波递增——第1波15秒慢速少量炸弹、第2波15秒中速+炸弹增多、第3波15秒快速+钻石出现+密集炸弹
关卡数量：3波
试玩时长：45秒
CTA按钮文案：开始挑战
CTA触发时机：达到目标分数或生命耗尽时弹出
引导手势：进场1.5秒后显示手指左右滑动提示，3秒后消失
粒子特效：接到金币金色粒子爆发+飞入、接到钻石蓝色星光粒子、接到红包红色粒子、接到炸弹红色爆炸粒子+屏幕震动、接到石头灰色碎片粒子
动画效果：物品从天空掉落旋转动画、接物篮左右移动弹性动画、接到物品弹跳飞入计数、炸弹爆炸时接物篮抖动、生命心形减少动画
音效配置：接到金币清脆叮咚音效 + 接到钻石闪耀音效 + 接到红包欢快音效 + 接到炸弹爆炸警告音效 + 接到石头沉闷音效 + 轻快天空背景音乐
UI风格：天空主题边框，顶部当前分数+目标分数+生命心形，物品掉落区域，底部接物篮
镜头效果：接到钻石时镜头微拉近特写，炸弹爆炸时屏幕红色闪烁，连续combo接到时镜头微震
开始界面：副标题显示「接金币大作战」，金币飘落动画`,

  'misc-knife': `帮我生成一个飞刀插入试玩
玩法类型：飞刀插入
主题：飞刀靶心
视觉风格：暗黑简约
配色方案：深色背景 #111111 + 靶心红#ff3344 + 刀身银#cccccc + 刀柄棕#8b5e3c + 已插刀光#ffdd44 + 木板棕#6b4226
背景色：#111111
核心元素：旋转圆木靶（直径200px，表面有同心圆环靶心），6把飞刀（银色刀身+棕色刀柄），已插刀数计数器，靶心红点标记
核心机制：点击屏幕发射飞刀插入旋转圆木靶中，飞刀不能碰到任何已插入的刀（刀间距需大于10px），靶子旋转速度渐变加快
胜利条件：6把飞刀全部插入靶中
失败条件：飞刀碰到已有刀（立即失败）
难度等级：中等，靶子旋转速度从60°/秒→120°/秒→180°/秒递增
关卡设计：3关递进——第1关插入4把刀靶子慢速60°/秒、第2关插入6把刀靶子中速120°/秒、第3关插入8把刀靶子快速180°/秒+偶尔反向旋转
关卡数量：3关
试玩时长：30秒
CTA按钮文案：开始挑战
CTA触发时机：通关3关或失败时弹出
引导手势：进场1秒后显示手指点击发射提示，1.5秒后消失
粒子特效：飞刀插入火花四溅粒子、成功插入金色光点、失败红色碎片粒子+屏幕震动、全部插入靶心庆祝粒子
动画效果：飞刀从底部飞行至靶子插入动画200ms、靶子持续旋转动画、刀插入时靶子微震、插入后刀微微晃动稳定、失败时刀碰撞碎裂动画
音效配置：飞刀破空飞行音效 + 插入木靶清脆音效 + 失败金属碰撞音效 + 全部插入成功欢呼音效 + 紧张刺激背景音乐
UI风格：暗黑主题边框，靶子居中展示，顶部剩余刀数+当前关卡，已插刀数显示，靶心红色标记
镜头效果：飞刀插入瞬间镜头微震，全部插入成功时镜头拉近慢动作特写，失败时屏幕红色闪烁
开始界面：副标题显示「飞刀大师」，靶子缓慢旋转预览动画`,

  'misc-decor': `帮我生成一个装饰试玩
玩法类型：房间装饰
主题：温馨家居
视觉风格：温馨治愈卡通
配色方案：暖色奶油底色 #FFF8F0 + 木地板棕#c4a882 + 沙发粉#ffb3ba + 窗帘蓝#bae1ff + 植物绿#baffc9 + 灯金黄#ffffba
背景色：#FFF8F0
核心元素：空房间场景（含地板、墙壁、窗户），8种家具装饰品——沙发🛋️、书架📚、植物🪴、地毯🟫、台灯💡、挂画🖼️、抱枕🧸、窗帘🪟，拖放区域
核心机制：点击底部家具列表选择家具，手指拖放到房间任意位置，可旋转/缩放家具，系统根据摆放位置和搭配自动评分（满分5星）
胜利条件：装饰评分达到4星及以上
失败条件：无
难度等级：轻松休闲
关卡设计：1个房间自由装饰，初始评分0星，每放置1件家具+0.5星基础分，搭配和谐额外+0.3星，最终评分4星以上触发CTA
关卡数量：1个房间
试玩时长：45秒
CTA按钮文案：下载完整版
CTA触发时机：装饰评分达到4星以上或时间耗尽时弹出
引导手势：进场2秒后显示手指拖放家具提示，3秒后消失
粒子特效：放置家具时星星粒子飘出、评分达标金色庆祝粒子+星光、家具拖放轨迹光点、不满星时鼓励爱心粒子
动画效果：家具从列表拖入房间弹性落地动画、家具旋转/缩放交互、评分星星逐个点亮动画、评分达标时房间灯光渐亮、家具轻微呼吸浮动
音效配置：放置家具咔嗒音效 + 评分星星点亮音效 + 达标庆祝欢快音效 + 温馨家居背景音乐
UI风格：温馨家居边框，顶部当前评分星级，底部家具列表（横向滚动），房间区域居中展示，拖放光标图标
镜头效果：评分达标时镜头拉远展示全屋装饰效果，放置家具时镜头微拉近，最后一件家具放置时慢动作
开始界面：副标题显示「装饰你的梦想家」，空房间预览动画`,

  'misc-fishing': `帮我生成一个钓鱼试玩
玩法类型：钓鱼
主题：休闲钓鱼
视觉风格：卡通水彩
配色方案：海洋蓝底色 #2288cc + 天空蓝#87CEEB + 水面蓝#3399dd + 鱼彩色——金鱼#ffcc00/锦鲤#ff6644/鲈鱼#88cc88/小丑鱼#ff8844/鲨鱼灰#888888/水母粉#ff99cc + 浮漂红#ff3344
背景色：#2288cc
核心元素：钓鱼竿（竹制竿身+鱼线+红色浮漂）、6种鱼类——金鱼🐟（+10分/常见）、锦鲤🐠（+30分/稀有）、鲈鱼🐟（+15分/普通）、小丑鱼🤡（+20分/少见）、鲨鱼🦈（+50分/极稀有）、水母🪼（-5分/干扰），鱼篓计数器
核心机制：点击抛竿按钮将鱼线抛入水中，等待浮漂下沉（随机1-4秒），浮漂下沉时点击收竿按钮拉鱼，根据鱼的大小和稀有度得分，水母咬钩扣分
胜利条件：达到目标分数
失败条件：时间耗尽
难度等级：轻松休闲，鱼咬钩等待时间随机，稀有鱼出现概率低
关卡设计：无限模式，45秒内尽可能多钓鱼，鱼群随机出现，前10秒常见鱼多，后期稀有鱼出现概率增加
关卡数量：无限
试玩时长：45秒
CTA按钮文案：开始钓鱼
CTA触发时机：达到目标分数或时间耗尽时弹出
引导手势：进场2秒后显示手指点击抛竿提示，浮漂下沉时显示收竿提示
粒子特效：抛竿水面涟漪粒子、鱼咬钩水花飞溅粒子、钓到鱼水花+鱼跃出水面粒子、鲨鱼金色粒子+水柱、水母紫色粒子
动画效果：鱼线抛投弧线动画、浮漂在水面上下晃动、浮漂下沉→鱼线拉扯动画、鱼挣扎左右摆动→拉出水面跃起、鱼篓计数增加动画
音效配置：抛竿噗通入水音效 + 浮漂晃动水声音效 + 鱼咬钩水花音效 + 收竿拉鱼音效 + 鲨鱼特殊低沉音效 + 水母干扰音效 + 自然湖畔背景音乐（鸟鸣+水声）
UI风格：钓鱼主题木质边框，顶部当前分数+目标分数+计时器+鱼篓计数，鱼竿/水面区域，抛竿/收竿按钮
镜头效果：钓到鲨鱼时镜头拉近慢动作特写+水花四溅，连续钓到鱼时镜头微震，钓到锦鲤时金色光晕
开始界面：副标题显示「休闲钓鱼乐」，鱼竿和鱼篓展示动画`,

  'misc-multi-ending': `帮我生成一个多结局试玩
玩法类型：多结局互动
主题：奇幻冒险
视觉风格：卡通手绘
配色方案：故事场景暖色调底色 #2a1a0a + 文字白#ffffff + 选项按钮金#ccaa44 + 好结局绿#44cc44 + 坏结局红#ff4444 + 中立结局蓝#4488ff
背景色：#2a1a0a
核心元素：冒险主角角色（可自定义姓名），奇幻场景（森林🌲/城堡🏰/洞穴🕳️），3个分支选择节点，每个节点2-3个选项，结局展示画面
核心机制：阅读剧情文本后从选项中选择行动，不同选择导向不同分支路径，最终根据所有选择组合到达不同结局（好结局/中立结局/坏结局），支持倒回重新选择
胜利条件：达到好结局（拯救王国）
失败条件：达到坏结局（王国毁灭）
难度等级：轻松休闲
关卡设计：3个选择节点→第1节点「遇到岔路」选择走森林/城堡/洞穴、第2节点「遇到NPC」选择帮助/无视/欺骗、第3节点「最终抉择」选择战斗/谈判/逃跑，共3×3×3=27种路线组合，3种结局
关卡数量：3个选择节点
试玩时长：45秒
CTA按钮文案：下载完整故事
CTA触发时机：结局展示后弹出
引导手势：进场2秒后显示手指点击选项提示，2.5秒后消失
粒子特效：选择节点时魔法粒子漩涡、好结局金色光芒+彩虹粒子、坏结局暗红色粒子+碎片、中立结局蓝色星光粒子、场景切换传送门粒子
动画效果：剧情文本打字机逐字显示动画、选项按钮悬浮呼吸动画、场景切换淡入淡出+转场、角色表情变化动画、结局画面全景展开
音效配置：剧情文本打字音效 + 选择点击音效 + 好结局欢快胜利号角 + 坏结局低沉悲伤音乐 + 中立结局平静音乐 + 奇幻冒险背景音乐
UI风格：手绘故事书边框，顶部章节进度条，剧情文本区域（打字机效果），2-3个选项按钮，角色头像+名称，场景插图
镜头效果：关键选择时镜头拉近选项按钮，结局揭晓时镜头拉远全景展示+慢动作，好结局时金色光晕，坏结局时红色暗角
开始界面：副标题显示「选择你的冒险」，故事书翻开动画`,

  // ═══════════════════════════════════════════════════════════
  // 抽卡
  // ═══════════════════════════════════════════════════════════
  'gacha-pull': `帮我生成一个抽卡试玩
玩法类型：抽卡
主题：卡牌收集
视觉风格：华丽动漫
配色方案：深色底色 #1a0a2e + 稀有度金色#ffcc00 + 紫色#9944ff + 蓝色#3366ff + 灰色#999999
背景色：#1a0a2e
核心元素：抽卡卡池（SSR/SS/SR/R/N 5个稀有度），卡牌背面，抽卡按钮，收集图鉴
核心机制：点击抽卡按钮，播放抽卡动画，随机获得不同稀有度卡牌，收集图鉴
胜利条件：抽到SSR卡牌
失败条件：无
难度等级：轻松
关卡设计：5次抽卡机会，稀有度概率：SSR 5%/SS 10%/SR 20%/R 30%/N 35%
关卡数量：5次
试玩时长：30秒
CTA按钮文案：下载继续抽卡
CTA触发时机：抽到SSR或次数用完时弹出
引导手势：进场1秒后显示点击抽卡提示
粒子特效：抽卡魔法阵粒子、SSR彩虹光柱、SS金色光效、SR紫色光效、R蓝色光效、N灰色光效
动画效果：抽卡按钮按下、魔法阵旋转展开、卡牌翻面揭示、稀有度光效爆发、卡牌展示悬浮
音效配置：抽卡魔法音效 + 翻牌音效 + SSR专属欢呼音效 + 稀有度分级音效 + 动漫风格背景音乐
UI风格：华丽抽卡界面边框，顶部剩余次数+图鉴收集进度，卡牌展示区，收集图鉴入口
镜头效果：SSR抽到时镜头拉近+慢动作+全屏彩虹光效，连续抽卡时镜头微震
开始界面：副标题显示「抽卡大作战」`,

  // ═══════════════════════════════════════════════════════════
  // 社交
  // ═══════════════════════════════════════════════════════════
  'social-swipe': `帮我生成一个社交滑动试玩
玩法类型：社交滑动
主题：社交匹配
视觉风格：现代简约
配色方案：渐变色底色 + 卡片白色 + 按钮彩色
背景色：#1a1a2e
核心元素：用户卡片（头像、名字、简介），滑动区域，匹配成功动画
核心机制：左滑跳过，右滑喜欢，互相喜欢则匹配成功
胜利条件：匹配成功
失败条件：无
难度等级：轻松
关卡数量：5张卡片
试玩时长：30秒
CTA按钮文案：下载交友
CTA触发时机：匹配成功或卡片用完时弹出
引导手势：进场1秒后显示左右滑动提示
粒子特效：右滑喜欢爱心粒子、匹配成功金色粒子+心形飘落
动画效果：卡片滑动旋转飞出、匹配成功心形放大动画、卡片堆叠切换
音效配置：滑动音效 + 匹配成功欢呼音效 + 轻松社交背景音乐
UI风格：卡片式边框，顶部匹配进度，卡片堆叠展示
镜头效果：匹配成功时全屏爱心特效+慢动作
开始界面：副标题显示「遇见你的缘分」`,

  // ═══════════════════════════════════════════════════════════
  // 展示类
  // ═══════════════════════════════════════════════════════════
  'showcase-slide': `帮我生成一个轮播展示试玩
玩法类型：轮播展示
主题：App产品展示
视觉风格：现代简约科技风
配色方案：深色渐变底色 #0a0a0a→#1a1a2e + 产品卡片白#ffffff + 高亮标签蓝#4488ff + 分类标签灰#666666 + CTA按钮金#ffcc00
背景色：#0a0a0a
核心元素：5张产品展示卡片（每张卡片含产品大图+标题+特性标签+简介文字），底部圆点轮播指示器，顶部产品分类标签栏（全部/热门/新功能/推荐），CTA悬浮按钮
核心机制：左右滑动切换产品卡片，点击底部指示器圆点跳转对应卡片，自动轮播间隔5秒，点击CTA按钮跳转下载
胜利条件：浏览完所有产品卡片
失败条件：无
难度等级：轻松展示
关卡设计：5张卡片线性排列——卡片1产品概览、卡片2核心功能、卡片3特色亮点、卡片4用户评价、卡片5下载引导，自动轮播5秒/张，手动滑动可中断自动轮播
关卡数量：5张卡片
试玩时长：30秒
CTA按钮文案：立即下载
CTA触发时机：浏览完所有5张卡片或滑动3次后弹出全屏CTA
引导手势：进场1秒后显示手指左右滑动提示，2秒后消失
粒子特效：卡片切换时淡入淡出粒子、CTA按钮脉冲光晕粒子、浏览完所有卡片时金色庆祝粒子
动画效果：卡片左右滑动切换弹性动画300ms、指示器圆点缩放+颜色变化、卡片内容（标题/标签）错位渐入、CTA按钮呼吸脉冲动画、卡边阴影深度变化
音效配置：卡片切换轻柔滑动音效 + 指示器点击音效 + CTA按钮脉冲音效 + 轻松科技背景音乐
UI风格：深色展示边框，顶部产品分类标签栏（横向滚动），底部圆点轮播指示器，卡片居中展示大图+文字，悬浮CTA按钮右下角
镜头效果：卡片切换时平滑过渡，浏览到最后一张卡片时镜头微拉远展示全貌，CTA弹出时镜头拉近
开始界面：副标题显示「发现更多精彩」，产品卡片预览动画`,

  'showcase-interactive': `帮我生成一个互动展示试玩
玩法类型：互动展示
主题：探索发现
视觉风格：现代简约科技风
配色方案：深色渐变底色 #0a0a0a→#1a1a2e + 互动热点脉冲蓝#4488ff + 热点已浏览绿#44cc44 + 展开面板白#ffffff + 关闭按钮灰#999999
背景色：#0a0a0a
核心元素：主展示场景画面（含6个互动热点标记），每个互动热点有脉冲动画标记，点击后弹出详情面板（含图文介绍+图片+关闭按钮），热点浏览进度计数器
核心机制：点击画面中脉冲闪烁的互动热点标记，弹出详情面板展示内容，阅读完关闭面板，热点标记变为已浏览状态（绿色），完成所有热点浏览
胜利条件：浏览完所有互动热点
失败条件：无
难度等级：轻松探索
关卡设计：6个互动热点分布在场景中——热点1产品功能、热点2使用场景、热点3技术亮点、热点4用户数据、热点5特色对比、热点6下载引导，热点建议按顺序解锁
关卡数量：6个互动点
试玩时长：35秒
CTA按钮文案：立即下载
CTA触发时机：浏览完所有6个互动热点后弹出全屏CTA
引导手势：进场1.5秒后显示手指点击脉冲热点提示，3秒后消失
粒子特效：互动热点脉冲波纹扩散粒子（蓝色未浏览→绿色已浏览）、点击展开时面板弹出粒子、全部浏览完金色庆祝粒子
动画效果：互动热点脉冲波纹呼吸动画（1.5秒周期）、点击热点标记放大→缩小、详情面板从热点位置弹性展开、关闭面板缩回动画、已浏览热点绿色对勾标记、全部浏览完热点连线动画
音效配置：点击热点清脆音效 + 面板展开弹出音效 + 面板关闭收回音效 + 全部浏览完庆祝音效 + 轻松探索背景音乐
UI风格：深色展示边框，主场景画面居中展示，互动热点标记脉冲动画，详情面板（白色卡片+阴影），顶部浏览进度条+已浏览计数，关闭按钮
镜头效果：点击热点时镜头拉近对应位置，关闭面板时镜头恢复，全部浏览完时镜头拉远全场景展示+慢动作
开始界面：副标题显示「探索更多内容」，场景画面+热点脉冲预览动画`,

  // ═══════════════════════════════════════════════════════════
  // 换装
  // ═══════════════════════════════════════════════════════════
  'dress-up': `帮我生成一个换装试玩
玩法类型：换装
主题：时尚换装
视觉风格：时尚卡通
配色方案：粉色底色 #FFF0F5 + 服装彩色
背景色：#FFF0F5
核心元素：角色模特、服装分类（上衣、下装、鞋子、配饰）、试穿展示区
核心机制：点击选择不同服装搭配到角色身上，搭配完成后评分展示
胜利条件：搭配评分达标
失败条件：无
难度等级：轻松
关卡数量：1个搭配主题
试玩时长：35秒
CTA按钮文案：下载更多服装
CTA触发时机：搭配完成或时间耗尽时弹出
引导手势：进场2秒后显示点击选择服装提示
粒子特效：换装星光粒子、评分星星粒子、完美搭配金色粒子
动画效果：服装切换动画、角色换装展示、评分星星飞入
音效配置：换装音效 + 评分音效 + 时尚背景音乐
UI风格：衣橱主题边框，顶部评分+主题，底部服装分类，中间角色展示
镜头效果：换装时拉近展示，完美搭配时全屏庆祝
开始界面：副标题显示「时尚搭配大师」`,

};

// ─── 预设快捷指令 ─────────────────────────────────────────

export type PresetCategory = '消除' | '跑酷' | '合成' | '卡牌' | '休闲' | '益智' | '答题';

export interface PresetCommand {
  id: string;
  label: string;
  category: PresetCategory;
  icon: string;
  prompt: string;
}

export const PRESET_COMMANDS: PresetCommand[] = [
  {
    id: 'gem-match3',
    label: '宝石交换三消',
    category: '消除',
    icon: '💎',
    prompt: GAMEPLAY_PROMPTS.match3,
  },
  {
    id: 'jelly-glass-match3',
    label: '果冻交换三消',
    category: '消除',
    icon: '🟢',
    prompt: GAMEPLAY_PROMPTS['match3-jelly-glass'],
  },
  {
    id: 'parking-puzzle',
    label: '拖拽挪车',
    category: '益智',
    icon: '🚗',
    prompt: GAMEPLAY_PROMPTS.parking,
  },

  {
    id: 'fruit-merge',
    label: '水果拖拽合成',
    category: '合成',
    icon: '🍉',
    prompt: GAMEPLAY_PROMPTS.merge,
  },
  {
    id: 'jelly-glass-merge',
    label: '果冻拖拽合成',
    category: '合成',
    icon: '🟢',
    prompt: GAMEPLAY_PROMPTS['merge-jelly-glass'],
  },

  {
    id: 'cat-merge-collect',
    label: '猫咪合成',
    category: '合成',
    icon: '🐱',
    prompt: GAMEPLAY_PROMPTS['merge-collect'],
  },

  {
    id: 'mahjong-eliminate',
    label: '点击麻将入槽',
    category: '消除',
    icon: '🀄',
    prompt: GAMEPLAY_PROMPTS['eliminate-mahjong-scatter'],
  },
  {
    id: 'guess-song',
    label: '答题猜歌',
    category: '答题',
    icon: '🎵',
    prompt: GAMEPLAY_PROMPTS['guess-song'],
  },
  {
    id: 'idiom-fill',
    label: '选字填成语',
    category: '答题',
    icon: '📜',
    prompt: GAMEPLAY_PROMPTS['idiom-fill'],
  },
  {
    id: 'forest-runner',
    label: '左右跑酷',
    category: '跑酷',
    icon: '🏃',
    prompt: GAMEPLAY_PROMPTS.runner,
  },
  {
    id: 'farm-idle',
    label: '点击农场放置',
    category: '休闲',
    icon: '💰',
    prompt: GAMEPLAY_PROMPTS.idle,
  },
  {
    id: 'jigsaw-puzzle',
    label: '拼图解谜',
    category: '益智',
    icon: '🧩',
    prompt: GAMEPLAY_PROMPTS.puzzle,
  },
  {
    id: 'poker-card',
    label: '点击扑克对战',
    category: '卡牌',
    icon: '🃏',
    prompt: GAMEPLAY_PROMPTS.card,
  },
  {
    id: 'arrow-game',
    label: '点击多格箭头',
    category: '益智',
    icon: '➡️',
    prompt: GAMEPLAY_PROMPTS['arrow'],
  },
  {
    id: 'link-eliminate',
    label: '滑动连线消除',
    category: '消除',
    icon: '🔗',
    prompt: GAMEPLAY_PROMPTS['eliminate-link'],
  },
  {
    id: 'tap-eliminate',
    label: '点击色块消除',
    category: '消除',
    icon: '👆',
    prompt: GAMEPLAY_PROMPTS['eliminate-tap'],
  },
  {
    id: 'tap-away-parking',
    label: '点击开走',
    category: '益智',
    icon: '🚙',
    prompt: GAMEPLAY_PROMPTS['parking-tap-away'],
  },
  {
    id: 'combo-parking',
    label: '点击连击开走',
    category: '益智',
    icon: '⚡',
    prompt: GAMEPLAY_PROMPTS['parking-combo'],
  },
];

/** 实验蓝图玩法简介后缀：避免误导为已稳定可生成 */
const BP_NOTE = '（实验中，生成结果可能不是该玩法）';

// ─── 视频转试玩预设（保持不变） ───────────────────────────

export interface VideoPresetCommand {
  id: string;
  title: string;
  description: string;
  prompt: string;
}

export const VIDEO_PRESET_COMMANDS: VideoPresetCommand[] = [
  {
    id: 'freeze_standard',
    title: '标准互动定格串联',
    description: '视频按顺序串联，每段末尾互动定格，按下跳下一段',
    prompt: `将上传的视频按顺序串联。每个视频播放到最后一帧自动暂停（互动定格），在画面正中放置一个 300×300 的橘黄色半透明手势区域，默认手势为按下，触发后跳转到下一个视频。最后一个视频的互动元件默认事件为跳转应用商店。全程静音原声，手势触发时播放点击音效。不加结束页。`,
  },
  {
    id: 'loop_forward',
    title: '互动循环（单向）',
    description: '视频循环播放直至用户交互',
    prompt: `将视频按顺序串联。每个视频设为互动循环（单向循环），视频从头到尾循环播放直至用户交互。手势区域居中 300×300，手势为上滑，触发后跳转下一个视频。最后一个视频循环等待用户上滑后跳转应用商店。`,
  },
  {
    id: 'loop_pingpong',
    title: '互动循环（双向）',
    description: '视频来回播放直至用户交互',
    prompt: `每个视频设为互动循环（双向循环），视频来回播放（正向播完反向播），循环播放直至用户交互。手势区域居中 300×300，手势为任意滑动，触发后跳转下一个视频。最后一个视频互动循环等待用户操作后跳转应用商店。`,
  },
  {
    id: 'auto_jump',
    title: '自动跳转连续展示',
    description: '视频自动连续播放，无需用户操作',
    prompt: `视频按顺序串联，每个视频播完后等待 2 秒自动跳转下一个视频（自动跳转），无需用户操作。最后一个视频播完延迟 2 秒跳转应用商店。适合纯展示类试玩。全程静音原声。`,
  },
  {
    id: 'with_endcard',
    title: '带结束页下载',
    description: '视频串联+结束页下载按钮',
    prompt: `视频串联播放，每视频末尾互动定格，按下跳下一视频。最后添加结束页：中部默认下载 icon + 标题「立即下载」+ 下载按钮，按钮上等大手势区域按下跳转应用商店。结束页背景不透明度 100%，进入动画 fadeIn。`,
  },
  {
    id: 'multi_branch',
    title: '多分支选择',
    description: '引导后分支选择，不同区域不同路径',
    prompt: `第1个视频为引导视频，播完后出现两个手势区域：画面左半部按下走分支A（依次播放 A1、A2），画面右半部按下走分支B（依次播放 B1、B2）。各分支末尾互动定格跳转应用商店。`,
  },
  {
    id: 'text_pulse',
    title: '文字引导+脉冲动画',
    description: '每视频显示引导文字+脉冲动画',
    prompt: `每视频中央显示引导文字（如「按下继续」），红色 24 号字，脉冲动画 100→120 周期 1 秒。视频末尾互动定格，按下跳下一视频。最后一个视频按下跳应用商店。`,
  },
  {
    id: 'sfx_enhanced',
    title: '音效增强',
    description: '全程背景音乐+多种互动音效',
    prompt: `全程背景音乐音量 50%。手势触发时播放点击音效，滑动时播放滑动音效，跳转场景时播放跳转音效，互动成功播放成功音效，视频末尾倒计时 3 秒播放倒计时音效。每视频末尾互动定格，按下跳下一视频。`,
  },
  {
    id: 'landscape_auto',
    title: '横屏自动展示',
    description: '横屏模式自动跳转+结束页',
    prompt: `横屏 667×375。视频串联，自动跳转延迟 1.5 秒。最后添加结束页标题「下载体验」，下载按钮按下跳应用商店。全程静音原声。`,
  },
  {
    id: 'full_featured',
    title: '全功能综合',
    description: '互动循环+文字动画+音效+分支+结束页',
    prompt: `视频串联，每视频互动循环单向，上滑跳下一视频。每视频中央引导文字脉冲动画 100→120 周期 1 秒。背景音乐音量 50% + 点击/滑动/成功/跳转音效。第1个视频设为分支引导，按下走主线。最后结束页下载按钮跳应用商店，进入动画 zoomIn。竖屏 375×667。`,
  },
];

/** 根据玩法 key 获取 prompt，未匹配到则返回 null */
export function getGameplayPrompt(key: string): string | null {
  return GAMEPLAY_PROMPTS[key] ?? null;
}

// ─── 玩法分类与变体快捷指令（玩法清单弹窗数据源） ────────────────

export interface GameplayVariant {
  id: string;
  label: string;
  icon: string;
  promptKey: string;
  description: string;
  supportLevel: 'full' | 'blueprint';
  /** 引擎角标：标注该变体走 Cocos 生成模式（与模板/蓝图入口互相独立） */
  engineBadge?: 'cocos';
}

export interface GameplayCategoryTab {
  id: string;
  label: string;
  icon: string;
  /** 分类说明（用于区分分类定位，如红包本体 vs 网赚红包特效叠加） */
  description?: string;
  variants: GameplayVariant[];
}

export const GAMEPLAY_CATEGORIES: GameplayCategoryTab[] = [
  { id: 'parking', label: '挪车', icon: '🚗', variants: [
    { id: 'parking-classic', label: '拖拽挪车', icon: '🚗', promptKey: 'parking', description: '拖拽车辆沿朝向滑动，为红色目标车腾出通道并驶出出口', supportLevel: 'full' },
    { id: 'parking-obstacle', label: '拖拽挪车障碍', icon: '🏗️', promptKey: 'puzzle-parking', description: '拖拽挪车+立柱/墙壁障碍，需绕行腾出通道让目标车驶出', supportLevel: 'full' },
    { id: 'parking-tap-away', label: '点击开走', icon: '👆', promptKey: 'parking-tap-away', description: '点击车辆沿朝向前进直到碰撞或驶出，全部清空即胜利', supportLevel: 'full' },
    { id: 'parking-bus-out', label: '点击同色上车', icon: '🚌', promptKey: 'parking-bus-out', description: '点击与公交同色的乘客上车，满员后公交自动驶出', supportLevel: 'full' },
    { id: 'parking-all-escape', label: '拖拽全车逃脱', icon: '🚨', promptKey: 'parking-all-escape', description: '四周多出口数量不固定，拖拽使全部车辆驶出，需全局路线规划', supportLevel: 'full' },
    { id: 'parking-target-escape', label: '拖拽救护车救援', icon: '🚑', promptKey: 'parking-target-escape', description: '拖拽清出通道，白色救护车必须驶出出口（无火势机制）', supportLevel: 'full' },
    { id: 'parking-color-match', label: '拖拽配对停车', icon: '🅿️', promptKey: 'parking-color-match', description: '拖拽车辆停入对应颜色车位，全部匹配即胜利，无出口', supportLevel: 'full' },
    { id: 'parking-color-sort', label: '拖拽颜色排序', icon: '🌈', promptKey: 'parking-color-sort', description: '拖拽使同色车辆相邻聚集，全部排好即胜利', supportLevel: 'full' },
    { id: 'parking-combo', label: '点击连击开走', icon: '⚡', promptKey: 'parking-combo', description: '点击车辆自动驶出，连续成功触发 Combo 倍率奖励', supportLevel: 'full' },
    { id: 'parking-dual-exit', label: '拖拽双出口', icon: '🔀', promptKey: 'parking-dual-exit', description: '固定左右两个出口，拖拽为每辆车选择驶出路线', supportLevel: 'full' },
    { id: 'parking-triple-exit', label: '拖拽三出口', icon: '🔱', promptKey: 'parking-triple-exit', description: '固定三个方向出口，横纵混合车辆需选择最优出口疏散', supportLevel: 'full' },
    { id: 'parking-quad-exit', label: '拖拽四出口', icon: '🧭', promptKey: 'parking-quad-exit', description: '固定上下左右四向出口，极高难度全局规划疏散', supportLevel: 'full' },
    { id: 'parking-rescue-princess', label: '拖拽解救公主', icon: '👸', promptKey: 'parking-rescue-princess', description: '多出口停车场，粉色公主车为指定目标，全车驶出并救出角色', supportLevel: 'full' },
    { id: 'parking-lap-exit', label: '拖拽跑道半圈', icon: '🏁', promptKey: 'parking-lap-exit', description: '拖拽腾出通道，车辆驶出后沿外圈跑道转半圈', supportLevel: 'full' },
    { id: 'parking-fire-spread', label: '拖拽火势封路', icon: '🔥', promptKey: 'parking-fire-spread', description: '火焰每5步向四周扩散封路，拖拽在火场中为目标车开辟通道', supportLevel: 'full' },
    { id: 'parking-valet', label: '拖拽代客泊车', icon: '🎩', promptKey: 'parking-valet', description: '顾客车辆排队入场，拖拽引导每辆车停入指定车位', supportLevel: 'full' },
    { id: 'parking-traffic-light', label: '拖拽信号灯通行', icon: '🚦', promptKey: 'parking-traffic-light', description: '红绿灯交替，拖拽车辆在绿灯时通行，闯红灯累计违章', supportLevel: 'full' },
    { id: 'parking-elevator', label: '拖拽立体车库', icon: '🛗', promptKey: 'parking-elevator', description: '多层车库电梯换层，拖拽跨层腾挪让目标车驶出', supportLevel: 'full' },
    { id: 'parking-meter-fare', label: '拖拽计费挪车', icon: '💲', promptKey: 'parking-meter-fare', description: '每步扣除停车费，余额耗尽失败，需最少步数驶出目标车', supportLevel: 'full' },
    { id: 'parking-taxi-dispatch', label: '点击出租车接单', icon: '🚕', promptKey: 'parking-taxi-dispatch', description: '订单持续派发，点击对应颜色出租车自动驶出接单', supportLevel: 'full' },
  ]},
  { id: 'eliminate', label: '三消', icon: '💎', variants: [
    { id: 'match3', label: '宝石交换三消', icon: '💎', promptKey: 'match3', description: '交换相邻宝石形成 3+ 连线消除', supportLevel: 'full' },
    { id: 'match3-jelly-glass', label: '果冻交换三消', icon: '🫧', promptKey: 'match3-jelly-glass', description: '交换相邻果冻块形成 3+ 消除，机制同宝石三消，仅果冻玻璃视觉', supportLevel: 'full' },
    { id: 'eliminate-match3', label: '糖果交换三消', icon: '🍬', promptKey: 'eliminate-match3', description: '交换相邻糖果形成 3+ 消除，机制同宝石三消，仅糖果主题', supportLevel: 'full' },
    { id: 'eliminate-link', label: '滑动连线消除', icon: '🔗', promptKey: 'eliminate-link', description: '手指滑动连接相邻同色星星形成路径，至少 2 颗即消除', supportLevel: 'full' },
    { id: 'eliminate-tap', label: '点击色块消除', icon: '👆', promptKey: 'eliminate-tap', description: '点击一整片相连同色区域消除（至少 2 格）', supportLevel: 'full' },
    { id: 'eliminate-mahjong', label: '点击麻将配对', icon: '🀄', promptKey: 'eliminate-mahjong', description: '点击两张相同且路径畅通的麻将牌配对消除', supportLevel: 'full' },
    { id: 'eliminate-mahjong-scatter', label: '点击麻将入槽', icon: '🎴', promptKey: 'eliminate-mahjong-scatter', description: '点击可见牌飞入收集槽，三张相同自动消除', supportLevel: 'full' },
    { id: 'eliminate-bubble', label: '瞄准泡泡射击', icon: '🫧', promptKey: 'eliminate-bubble', description: `六角网格瞄准发射泡泡，3+ 同色相连消除。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'eliminate-slider', label: '滑动整行消除', icon: '📋', promptKey: 'eliminate-slider', description: `整行整列滑动形成 3+ 连线消除。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'merge', label: '合成', icon: '🍇', variants: [
    { id: 'merge', label: '水果拖拽合成', icon: '🍉', promptKey: 'merge', description: '4×4 铺满棋盘，拖拽相邻同值水果碰撞合成升级', supportLevel: 'full' },
    { id: 'merge-jelly-glass', label: '果冻拖拽合成', icon: '🟢', promptKey: 'merge-jelly-glass', description: '拖拽相邻同值元素合成，机制同水果拖拽合成，仅果冻玻璃视觉', supportLevel: 'full' },
    { id: 'merge-collect', label: '猫咪拖拽合成', icon: '🐱', promptKey: 'merge-collect', description: '拖拽合成升级猫咪，收集图鉴解锁新物种', supportLevel: 'full' },
    { id: 'eliminate-merge-earn', label: '限时三合一合成', icon: '⛏️', promptKey: 'eliminate-merge-earn', description: '拖拽合成+限时倒计时+链式三合一，矿石主题网赚变体', supportLevel: 'full' },
    { id: 'merge-2048', label: '数字滑动合成', icon: '🔢', promptKey: 'merge-2048', description: '上下左右滑动整行数字方块合并，达成 2048 通关', supportLevel: 'full' },
    { id: 'merge-suika', label: '水果物理投放', icon: '🍉', promptKey: 'merge-suika', description: '顶部投放圆形水果，物理碰撞堆叠合成大西瓜', supportLevel: 'full' },
    { id: 'merge-drop', label: '方块下落投放', icon: '⬇️', promptKey: 'merge-drop', description: '顶部投放方块下落堆叠，同等级自动合并', supportLevel: 'full' },
    { id: 'merge-chain', label: '水果连锁合成', icon: '⛓️', promptKey: 'merge-chain', description: '拖拽合成触发连锁反应，Combo 倍率递增', supportLevel: 'full' },
    { id: 'merge-merge3', label: '元素三合一合成', icon: '3️⃣', promptKey: 'merge-merge3', description: '三个相同元素同时存在时自动三合一合成', supportLevel: 'full' },
    { id: 'merge-multi-level', label: '棋盘多级合成', icon: '🏰', promptKey: 'merge-multi-level', description: '拖拽合成逐关递进，棋盘扩张目标升级', supportLevel: 'full' },
    { id: 'merge-army', label: '军团拖拽合成', icon: '⚔️', promptKey: 'merge-army', description: '拖拽合成升级士兵，击退敌军波次进攻', supportLevel: 'full' },
  ]},
  { id: 'runner', label: '跑酷', icon: '🏃', variants: [
    { id: 'runner', label: '左右跑酷', icon: '🏃', promptKey: 'runner', description: '三车道自动奔跑，左右滑动换道躲避障碍收集金币（支持无尽模式）', supportLevel: 'full' },
    { id: 'runner-dodge', label: '上下躲避跑酷', icon: '🤸', promptKey: 'runner-dodge', description: '单赛道自动前进，上下滑动躲避障碍，碰撞即失败', supportLevel: 'full' },
    { id: 'runner-tunnel', label: '隧道左右跑酷', icon: '🚇', promptKey: 'runner-tunnel', description: `操作同左右跑酷，隧道场景视野受限。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'runner-waterslide', label: '滑梯左右跑酷', icon: '🏄', promptKey: 'runner-waterslide', description: `操作同左右跑酷，水上乐园滑梯场景。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'runner-space', label: '太空左右跑酷', icon: '🚀', promptKey: 'runner-space', description: `操作同左右跑酷，太空零重力场景。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'arrow', label: '箭头', icon: '➡️', variants: [
    { id: 'arrow', label: '点击多格箭头', icon: '➡️', promptKey: 'arrow', description: '点击长条或折线箭头，整段沿指向滑出棋盘', supportLevel: 'full' },
    { id: 'arrow-classic-cell', label: '点击单格箭头', icon: '⬜', promptKey: 'arrow-classic-cell', description: '点击 1×1 方块沿指向滑出，无 HP 无惩罚', supportLevel: 'full' },
    { id: 'arrow-time-blitz', label: '点击限时清箭头', icon: '⏱️', promptKey: 'arrow-time-blitz', description: '首次点击启动倒计时，移除 ≥80% 箭头即胜利', supportLevel: 'full' },
    { id: 'arrow-color-match', label: '双击颜色配对', icon: '🌈', promptKey: 'arrow-color-match', description: '双击同色箭头配对同时移出，轨迹不可重叠', supportLevel: 'full' },
    { id: 'arrow-one-stroke', label: '点击一笔画路径', icon: '✏️', promptKey: 'arrow-one-stroke', description: '必须沿连通路径顺序依次点击箭头，顺序错误计数', supportLevel: 'full' },
    { id: 'arrow-chain-combo', label: '点击顺序连击', icon: '🔢', promptKey: 'arrow-chain-combo', description: '箭头带编号，必须从小到大依次点击，连击加分', supportLevel: 'full' },
    { id: 'arrow-relay', label: '点击箭头接力', icon: '🏃', promptKey: 'arrow-relay', description: '同一时间只有高亮激活的箭头可点击，移除后自动激活下一支', supportLevel: 'full' },
    { id: 'arrow-reverse', label: '点击反向箭头', icon: '🔁', promptKey: 'arrow-reverse', description: '实际移动方向与箭头指向完全相反', supportLevel: 'full' },
    { id: 'arrow-domino', label: '点击多米诺连锁', icon: '🀄', promptKey: 'arrow-domino', description: '点击链头触发连锁，后方箭头依次自动移除', supportLevel: 'full' },
    { id: 'arrow-fewest-moves', label: '点击最少步数', icon: '🎯', promptKey: 'arrow-fewest-moves', description: '目标步数内清空全部箭头，支持撤销悔步', supportLevel: 'full' },
    { id: 'arrow-pixel-drain', label: '点击像素轨迹', icon: '🖥️', promptKey: 'arrow-pixel-drain', description: '滑出留下像素轨迹，覆盖全部目标格即胜', supportLevel: 'full' },
    { id: 'arrow-pvp-lite', label: '点击对战清箭头', icon: '⚔️', promptKey: 'arrow-pvp-lite', description: '上下镜像棋盘对战 AI，先清空自己一侧者胜', supportLevel: 'full' },
    { id: 'arrow-push-chain', label: '点击连推箭头', icon: '🧱', promptKey: 'arrow-push-chain', description: '点击箭头推挤同排箭头，产生一格连锁位移', supportLevel: 'full' },
    { id: 'arrow-teleport', label: '点击传送箭头', icon: '🌀', promptKey: 'arrow-teleport', description: '箭头踩传送门瞬移到配对门后继续移动', supportLevel: 'full' },
    { id: 'arrow-ice-slide', label: '点击冰面箭头', icon: '🧊', promptKey: 'arrow-ice-slide', description: '冰面滑行不可控，撞障碍或其他箭头才停', supportLevel: 'full' },
    { id: 'arrow-turn-tile', label: '点击转向箭头', icon: '🔃', promptKey: 'arrow-turn-tile', description: '转向板改变方块移动方向后滑出', supportLevel: 'full' },
    { id: 'arrow-multi-head', label: '点击多头箭头', icon: '🈯', promptKey: 'arrow-multi-head', description: '双头箭头两方向均畅通时整体消除', supportLevel: 'full' },
    { id: 'arrow-split', label: '点击分裂箭头', icon: '🔀', promptKey: 'arrow-split', description: '点击后分裂为两支，沿垂直双向滑出', supportLevel: 'full' },
    { id: 'arrow-queue', label: '点击队列箭头', icon: '🚦', promptKey: 'arrow-queue', description: '按队列顺序依次放行，每轮只能移动队首', supportLevel: 'full' },
    { id: 'cocos-arrow', label: '点击多格箭头 · Cocos', icon: '➡️', promptKey: 'arrow', description: 'Cocos 引擎生成，点击长条/折线箭头整段滑出棋盘', supportLevel: 'full', engineBadge: 'cocos' },
    { id: 'cocos-arrow-time-blitz', label: '点击限时清箭头 · Cocos', icon: '⏱️', promptKey: 'arrow-time-blitz', description: 'Cocos 引擎生成，首次点击启动倒计时，移除 ≥80% 即胜利', supportLevel: 'full', engineBadge: 'cocos' },
    { id: 'cocos-arrow-color-match', label: '双击颜色配对 · Cocos', icon: '🌈', promptKey: 'arrow-color-match', description: 'Cocos 引擎生成，双击同色箭头配对同时移出', supportLevel: 'full', engineBadge: 'cocos' },
    { id: 'cocos-arrow-one-stroke', label: '点击一笔画路径 · Cocos', icon: '✏️', promptKey: 'arrow-one-stroke', description: 'Cocos 引擎生成，按路径顺序依次点击箭头', supportLevel: 'full', engineBadge: 'cocos' },
  ]},
  { id: 'manage', label: '经营', icon: '🏙️', variants: [
    { id: 'idle', label: '点击农场放置', icon: '💰', promptKey: 'idle', description: '点击赚取金币，购买建筑自动增长，挂机收益', supportLevel: 'full' },
    { id: 'manage-tycoon', label: '点击建造城市', icon: '🏙️', promptKey: 'manage-tycoon', description: '点击建造升级建筑，自动收益扩张商业区', supportLevel: 'full' },
    { id: 'manage-kitchen', label: '点击餐厅上菜', icon: '🍳', promptKey: 'manage-kitchen', description: '点击食材烹饪，及时上菜服务顾客赚取小费', supportLevel: 'full' },
    { id: 'manage-garden', label: '点击种花收获', icon: '🌻', promptKey: 'manage-garden', description: '点击浇水加速生长，采收花朵售卖经营花园', supportLevel: 'full' },
    { id: 'cocos-manage-tycoon', label: '点击建造城市 · Cocos', icon: '🏙️', promptKey: 'manage-tycoon', description: 'Cocos 引擎生成，点击建造升级建筑经营城市', supportLevel: 'full', engineBadge: 'cocos' },
    { id: 'cocos-manage-kitchen', label: '点击餐厅上菜 · Cocos', icon: '🍳', promptKey: 'manage-kitchen', description: 'Cocos 引擎生成，点击烹饪上菜服务顾客', supportLevel: 'full', engineBadge: 'cocos' },
    { id: 'cocos-manage-garden', label: '点击种花收获 · Cocos', icon: '🌻', promptKey: 'manage-garden', description: 'Cocos 引擎生成，点击种植采收经营花园', supportLevel: 'full', engineBadge: 'cocos' },
  ]},
  { id: 'puzzle', label: '解谜', icon: '🧩', variants: [
    { id: 'puzzle', label: '拖拽图案拼图', icon: '🧩', promptKey: 'puzzle', description: '拖拽碎片拼合完整图案', supportLevel: 'full' },
    { id: 'puzzle-draw', label: '画线引导解谜', icon: '✏️', promptKey: 'puzzle-draw', description: '画线引导物体到达目标位置', supportLevel: 'full' },
    { id: 'puzzle-liquid', label: '倾倒液体排序', icon: '🧪', promptKey: 'puzzle-liquid', description: `倾倒试管中液体层，使每管只含一种颜色。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'puzzle-pin', label: '点击拔钉解谜', icon: '📌', promptKey: 'puzzle-pin', description: `按顺序拔出插销解锁机关。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'puzzle-screw', label: '点击拧螺丝', icon: '🔧', promptKey: 'puzzle-screw', description: `按顺序拧开螺丝拆卸板材。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'card', label: '纸牌', icon: '🃏', variants: [
    { id: 'card', label: '点击扑克对战', icon: '🃏', promptKey: 'card', description: '简化扑克对战，与 AI 比牌型出牌', supportLevel: 'full' },
    { id: 'card-poker', label: '点击斗地主', icon: '🎴', promptKey: 'card-poker', description: '三人斗地主经典规则，与 AI 对战', supportLevel: 'full' },
    { id: 'card-solitaire', label: '拖拽纸牌接龙', icon: '🂡', promptKey: 'card-solitaire', description: '拖拽排列纸牌，从 A 到 K 完成四堆接龙', supportLevel: 'full' },
  ]},
  { id: 'quiz', label: '答题', icon: '🎵', variants: [
    { id: 'guess-song', label: '点击听歌猜曲', icon: '🎵', promptKey: 'guess-song', description: '听歌片段从选项中选择正确曲名', supportLevel: 'full' },
    { id: 'idiom-fill', label: '点击选字填成语', icon: '📜', promptKey: 'idiom-fill', description: `从候选字中选字补齐缺字成语。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'quiz-image', label: '点击看图猜物', icon: '🖼️', promptKey: 'quiz-image', description: `根据图片提示从选项中猜答案。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'quiz-knowledge', label: '点击知识竞赛', icon: '📚', promptKey: 'quiz-knowledge', description: `多类别知识选择题竞答。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'quiz-word', label: '点击文字问答', icon: '✍️', promptKey: 'quiz-word', description: `文字/成语相关知识题。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},

  { id: 'lottery', label: '抽奖', icon: '🎰', variants: [
    { id: 'lottery-wheel', label: '点击转盘抽奖', icon: '🎡', promptKey: 'lottery-wheel', description: `点击旋转转盘获得随机奖品。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'lottery-scratch', label: '刮开刮刮乐', icon: '🎫', promptKey: 'lottery-scratch', description: `刮开涂层查看奖品。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'lottery-smash', label: '点击砸金蛋', icon: '🥚', promptKey: 'lottery-smash', description: `点击砸开金蛋获取奖品。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'lottery-draw', label: '点击按钮抽奖', icon: '🎁', promptKey: 'lottery-draw', description: `点击按钮执行标准抽奖流程。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'shooter', label: '射击', icon: '🎯', variants: [
    { id: 'shooter-target', label: '点击靶场射击', icon: '🎖️', promptKey: 'shooter-target', description: `点击瞄准射击移动靶得分。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'shooter-duck', label: '点击飞鸭射击', icon: '🦆', promptKey: 'shooter-duck', description: `点击命中飞过屏幕的目标。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'spot-diff', label: '找茬', icon: '🔍', variants: [
    { id: 'spot-diff-classic', label: '点击双图找茬', icon: '🔍', promptKey: 'spot-diff-classic', description: '对比上下两图点击找出所有差异', supportLevel: 'full' },
    { id: 'spot-diff-search', label: '点击搜索找物', icon: '🔎', promptKey: 'spot-diff-search', description: '在密集场景中点击找出清单上的隐藏物品', supportLevel: 'full' },
    { id: 'spot-diff-hidden', label: '点击清单寻宝', icon: '🔦', promptKey: 'spot-diff-hidden', description: '在复杂场景中按侧边清单找出隐藏物品', supportLevel: 'full' },
    { id: 'spot-diff-animated', label: '点击动态找茬', icon: '🎬', promptKey: 'spot-diff-animated', description: '在动态场景双图中点击找出差异', supportLevel: 'full' },
  ]},
  { id: 'tower-defense', label: '塔防', icon: '🏰', variants: [
    { id: 'tower-defense', label: '点击放置塔防', icon: '🏰', promptKey: 'tower-defense', description: `点击放置防御塔抵御敌人波次进攻。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'misc', label: '休闲', icon: '🎮', variants: [
    { id: 'misc-ball-sort', label: '倾倒球分类', icon: '⚽', promptKey: 'misc-ball-sort', description: `一次倒一颗球，使每支试管只含同色球（非液体层）。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'misc-catch', label: '滑动接物', icon: '🧺', promptKey: 'misc-catch', description: `滑动移动容器接住掉落物品。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'misc-knife', label: '点击飞刀插入', icon: '🔪', promptKey: 'misc-knife', description: `点击将飞刀插入旋转目标，不可碰到已有飞刀。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'misc-decor', label: '拖拽房间装饰', icon: '🛋️', promptKey: 'misc-decor', description: `拖拽家具布置房间完成主题搭配。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'misc-fishing', label: '点击钓鱼收竿', icon: '🎣', promptKey: 'misc-fishing', description: `等待鱼上钩后点击收竿。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'misc-multi-ending', label: '点击多结局叙事', icon: '🎬', promptKey: 'misc-multi-ending', description: `点击不同选项触发不同故事结局。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'gacha-social', label: '抽卡社交', icon: '🎴', variants: [
    { id: 'gacha-pull', label: '点击抽卡', icon: '🃏', promptKey: 'gacha-pull', description: `点击抽卡展示随机角色结果。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'social-swipe', label: '滑动社交浏览', icon: '📱', promptKey: 'social-swipe', description: `左右滑动模拟社交 APP 浏览内容。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
  { id: 'showcase-dressup', label: '展示换装', icon: '👗', variants: [
    { id: 'showcase-slide', label: '滑动看图', icon: '📷', promptKey: 'showcase-slide', description: `左右滑动浏览内容页面。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'showcase-interactive', label: '点击热点详情', icon: '🖱️', promptKey: 'showcase-interactive', description: `点击画面热点展开交互详情。${BP_NOTE}`, supportLevel: 'blueprint' },
    { id: 'dress-up', label: '点击服饰换装', icon: '👗', promptKey: 'dress-up', description: `点击选择服饰搭配完成主题穿搭。${BP_NOTE}`, supportLevel: 'blueprint' },
  ]},
];

/** 网赚红包特效层 prompt 片段（动态拼接到 full 玩法 prompt 末尾，非独立 promptKey） */
export const HONGBAO_EFFECT_PROMPT = `
─────────────────────────────
【网赚红包特效层】（锦上添花，不改变以上玩法本体）：
红包系统：开启网赚红包特效，顶部提现进度条以红包为游标，每次操作飘 +¥ 金币飞入进度条
通关弹出红包领取弹窗（开口红包 + 金额 + "再集 ¥X 可提现"）
达成门槛弹"下载领 ¥30"强 CTA
红包/金币素材独立生成，风格与游戏一致但不侵入游戏主体
redpacketEnabled=true, coinFlyEnabled=true`;